/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  useRef,
  Fragment,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  DataTable,
  Button,
  Dialog as LiftDialog,
  DialogContent as LiftDialogContent,
  DialogHeader as LiftDialogHeader,
  DialogTitle as LiftDialogTitle,
  DialogFooter as LiftDialogFooter,
  DialogDescription as LiftDialogDescription,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Toastify,
  Badge,
  SpinnerLoader,
  Tooltip as LiftTooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Label,
  Input,
  Switch as LiftSwitch,
  PageHeader,
  SingleSelect,
  DatePicker as LiftDatePicker,
  BaseInput,
  Checkbox as LiftCheckbox,
  MultiSelectCheckboxTag,
  Textarea as LiftTextarea,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  Sheet as FiscalSheet,
  SheetContent as FiscalSheetContent,
  SheetHeader as FiscalSheetHeader,
  SheetTitle as FiscalSheetTitle,
  SheetFooter as FiscalSheetFooter,
} from "@/components/ui/sheet";
import {
  RefreshCw,
  Plus,
  Edit,
  Search,
  ImageIcon,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  ChevronDown,
  Download,
  Check,
  RotateCcw,
  CloudUpload,
  X,
} from "lucide-react";
import {
  Divider,
  Paper,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Select,
  Autocomplete,
  FormControl,
  InputLabel,
  Checkbox,
  createTheme,
  ThemeProvider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Switch as MuiSwitch,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import Spinner from "react-spinner-material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddPhotoAlternateOutlined from "@mui/icons-material/AddPhotoAlternateOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";

const CusSwitch = styled(MuiSwitch)(() => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#16306D",
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#16306D",
  },
}));
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import cloneDeep from "lodash/cloneDeep";
import FileSaver from "file-saver";
import "@cyntler/react-doc-viewer/dist/index.css";
import {
  LocalizationProvider,
  MobileDatePicker,
  DesktopDatePicker,
} from "@mui/x-date-pickers";
import DatePickerValue from "../../../../shared/components/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import * as XLSX from "xlsx";
import axios from "axios";
import moment from "moment";
import "./brands.styles.scss";
import { useSelector } from "react-redux";
import TableSkeleton from "../../../../shared/components/skeleton-loader/table";
import MultipleSelectChip from "../../../../shared/components/multiple-input";
import isValidDomain from "is-valid-domain";
import isEmail from "validator/lib/isEmail";
import { useThrottledFn } from "@lift-admin/utils/throttle";
import { actionLogTrack } from "@lift-admin/utils/actionLogTrack";
import {
  buildSummary,
  buildDetails,
  diffFields,
  humanizeValue,
  formatIdWithName,
} from "@lift-admin/utils/logChangeFormatter";
import {
  ACORN_BRAND_FEATURE_LABEL,
  BRAND_CREATE_FIELD_SPECS,
  BRAND_FIELD_SPECS,
  normalizeBrandForLog,
} from "./logFields";
import { debounce } from "@lift-admin/utils/debounce";
import apiClient from "@lift-admin/utils/apiClient";
import { useAuthContext } from "@lift-admin/utils/provider";
import { EXCEL_FILE_BASE64 } from "./fiscal_template";
import {
  appendGrapevineFormData,
  getGrapevineEditPrefill,
  getGrapevineValidationError,
  mergeGrapevinePrefill,
  retryGrapevineLookup,
  shouldShowGrapevineFields,
  type GrapevineEditPrefill,
} from "./grapevineBrandForm";
import {
  GrapevineSlugAvailabilityMessage,
  grapevineSlugSubmitError,
  useGrapevineSlugAvailability,
} from "./useGrapevineSlugAvailability";
import { AddBrandWizard } from "./AddBrandWizard";
import type {
  AddBrandWizardResult,
  WizardProductFeature,
} from "./addBrandWizardUtils";
import { useAppDispatch } from "../../../../shared/redux/hooks";

/** Local-only add-brand wizard mockup. Enable with VITE_USE_ADD_BRAND_WIZARD=true in .env.local */
const USE_ADD_BRAND_WIZARD_MOCK =
  import.meta.env.DEV &&
  import.meta.env.VITE_USE_ADD_BRAND_WIZARD === "true";
import {
  setAddDialogOpen,
  setEditData,
  setEditDialogOpen,
  setPerformanceData,
} from "../../../../shared/redux/action/performance.action";
import AddPerformance, {
  type AddPerformanceFormRef,
} from "../../../performance-accounts/add_accounts";
import { AddPerformanceStepHeader } from "../../../performance-accounts/add_accounts/AddPerformanceStepHeader";
import { RootState } from "../../../../shared/redux/types";
import EditPerformance from "../../../performance-accounts/edit_account";
import PreLoader from "@lift-admin/ui/pre-loader";

import { actionLog } from "@/App";
import { getPayloadDiffMessage } from "../../../drawer-structure/drawer-parts/diff-finder/diff-finder";

const theme = createTheme({
  palette: {
    primary: {
      main: "#16306D",
    },
  },
  typography: {
    fontFamily: '"Figtree", sans-serif',
  },
});

const types: any[] = [
  {
    value: "New",
    label: "New",
  },
  {
    value: "Takeover",
    label: "Takeover",
  },
];

const date_presets_list = [
  {
    label: "Custom",
    data: ["Custom Range", "On Going"],
  },
  {
    label: "To Date",
    data: [
      "Yesterday",
      "Week to Date",
      "Bi-Week to date",
      "Month to date",
      "Quarter to date",
      "Year to date",
    ],
  },
  {
    label: "Previous",
    data: [
      "Previous Week",
      "Previous BI-Week",
      "Previous Month",
      "Previous Month",
      "Previous Quarter",
      "Previous Year",
    ],
  },
  {
    label: "Last",
    data: ["Last 7 Days", "Last 14 Days", "Last 28 Days", "Last 90 Days"],
  },
  {
    label: "This",
    data: [
      "This Week",
      "This BI-Week",
      "This Month",
      "This Quarter",
      "This Year",
    ],
  },
  {
    label: "Custom Date Ranges",
    data: [
      "Day Before Yesterday",
      "Last 180 Days",
      "Last 180 Days Thru Today",
      "Last 2 Days",
      "Last 2 Days Thru Today",
      "Last 28 Days Thru Today",
      "Last week (starts Sunday)",
      "Last 3 Days",
      "Last 3 Days Thru Today",
      "Last 30 Days Thru Today",
      "Last 4 Days",
      "Last 4 Days Thru Today",
      "Last 60 Days Thru Today",
      "Last 90 Days Thru Today",
      "Last week (starts Sunday)",
      "Latest 4 Quarters (including current)",
      "Month to Date Thru Yesterday",
      "Oct 2021 Thru Today",
      "Past 11 Weeks (Sun-Sat)",
      "Past 12 Weeks (Mon-Sun)",
      "Past 12 Weeks (Sun-Sat, including current week)",
      "Past 12 Weeks (Sun-Sat)",
      "Past 12 Weeks (Tues-Mon)",
      "Past 2 Months",
      "Past 2 Months Thru MTD",
      "Past 2 Weeks (Mon-Sun)",
      "Past 3 Months",
      "Past 3 Months Thru MTD",
      "Past 3 Weeks (Mon-Sun)",
      "Past 4 Weeks (Mon-Sun)",
      "Past 4 Weeks (Sun-Sat)",
      "Past 4 Weeks (Tues-Mon)",
      "Past 4 Weeks (Wed-Tues)",
      "Past 53 Weeks (Mon-Sun)",
      "Past 6 Weeks (Mon-Sun)",
      "Past 6 Weeks (Sun-Sat)",
      "Past 6 Weeks (Tues-Mon)",
      "Past 6 Weeks (Wed-Tues)",
      "Past Week (Mon-Sun)",
      "Past Week (Sun-Sat)",
      "Past Week (Tues-Mon)",
      "Previous Week (Mon-Sun)",
      "Today",
      "Year to Date Thru Yesterday",
    ],
  },
];
interface BrandsProps {
  drawerOpen: boolean;
}
const Brands: React.FC<BrandsProps> = ({ drawerOpen }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [platTitle, setPlatTitle] = useState<any>("");
  const [isCorrect, setIsCorrect] = useState<any>(false);
  const [isAddIconClicked, setIsAddIconClicked] = useState<any>(false);
  const [scrapDomain, setScrapDomain] = useState<any>(null);
  const [editScrapItem, setEditScrapItem] = useState<any>(null);
  const [changeScrapItem, setChangeScrapItem] = useState<any>({
    id: null,
    platform: "",
    url: "",
  });
  //ga4 values for add brand - now supports multiple rows
  const [ga4Rows, setGa4Rows] = useState<any>([
    {
      accountId: "",
      accountName: "",
      propertyId: "",
      propertyName: "",
      source: "",
      medValues: [],
      singleMedValue: "",
    },
  ]);
  const [addAffiliateAlias, setAddAffiliateAlias] = useState<any>("");

  // Helper functions for Add GA4 rows management
  const addNewGA4Row = () => {
    setGa4Rows([
      ...ga4Rows,
      {
        accountId: "",
        accountName: "",
        propertyId: "",
        propertyName: "",
        source: "",
        medValues: [],
        singleMedValue: "",
      },
    ]);
  };

  const removeGA4Row = (index: number) => {
    if (ga4Rows.length > 1) {
      const newRows = ga4Rows.filter((_: any, i: number) => i !== index);
      setGa4Rows(newRows);
    }
  };

  const updateGA4Row = (index: number, field: string, value: any) => {
    const newRows = [...ga4Rows];
    newRows[index][field] = value;
    setGa4Rows(newRows);
  };

  const handleAddMultipleMediumForRow = (e: any, rowIndex: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const currentRow = ga4Rows[rowIndex];
      if (currentRow.singleMedValue.trim()) {
        const newRows = [...ga4Rows];
        newRows[rowIndex].medValues = [
          ...currentRow.medValues,
          currentRow.singleMedValue.trim(),
        ];
        newRows[rowIndex].singleMedValue = "";
        setGa4Rows(newRows);
      }
    }
  };

  const handleRemoveMediumForRow = (
    e: any,
    rowIndex: number,
    mediumIndex: number
  ) => {
    e.preventDefault();
    const newRows = [...ga4Rows];
    newRows[rowIndex].medValues = newRows[rowIndex].medValues.filter(
      (_: any, i: number) => i !== mediumIndex
    );
    setGa4Rows(newRows);
  };

  //ga4 values for edit brand - now supports multiple rows
  const [editGa4Rows, setEditGa4Rows] = useState<any>([
    {
      accountId: "",
      accountName: "",
      propertyId: "",
      propertyName: "",
      source: "",
      medValues: [],
      singleMedValue: "",
      isExisting: false, // Track if this row exists in database
      ga4Id: null, // Database ID for existing GA4 rows
    },
  ]);
  const [initialEditGA4RowsCount, setInitialEditGA4RowsCount] =
    useState<number>(0);
  const [editAffiliateAlias, setEditAffiliateAlias] = useState<any>("");
  const [removedGA4Rows, setRemovedGA4Rows] = useState<any[]>([]); // Track removed GA4 rows for deletion

  // Helper functions for Edit GA4 rows management
  const addNewEditGA4Row = () => {
    setEditGa4Rows([
      ...editGa4Rows,
      {
        accountId: "",
        accountName: "",
        propertyId: "",
        propertyName: "",
        source: "",
        medValues: [],
        singleMedValue: "",
        isExisting: false, // New rows are always marked as non-existing
        ga4Id: null, // New rows don't have a database ID yet
      },
    ]);
  };

  const removeEditGA4Row = (index: number) => {
    if (editGa4Rows.length > 1) {
      const rowToRemove = editGa4Rows[index];

      // If this is an existing row with a ga4Id, track it for deletion
      if (rowToRemove.isExisting && rowToRemove.ga4Id) {
        setRemovedGA4Rows((prev) => [...prev, rowToRemove]);
      }

      const newRows = editGa4Rows.filter((_: any, i: number) => i !== index);
      setEditGa4Rows(newRows);
    }
  };

  const updateEditGA4Row = (index: number, field: string, value: any) => {
    const newRows = [...editGa4Rows];
    newRows[index][field] = value;
    setEditGa4Rows(newRows);
  };

  const handleAddMultipleMediumForEditRow = (e: any, rowIndex: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const currentRow = editGa4Rows[rowIndex];
      if (currentRow.singleMedValue.trim()) {
        const newRows = [...editGa4Rows];
        newRows[rowIndex].medValues = [
          ...currentRow.medValues,
          currentRow.singleMedValue.trim(),
        ];
        newRows[rowIndex].singleMedValue = "";
        setEditGa4Rows(newRows);
      }
    }
  };

  const handleRemoveMediumForEditRow = (
    e: any,
    rowIndex: number,
    mediumIndex: number
  ) => {
    e.preventDefault();
    const newRows = [...editGa4Rows];
    newRows[rowIndex].medValues = newRows[rowIndex].medValues.filter(
      (_: any, i: number) => i !== mediumIndex
    );
    setEditGa4Rows(newRows);
  };

  // Legacy edit values for backward compatibility

  //state for editExcelData
  const [isReached, setIsReached] = useState<any>(false);

  const [brands, setBrands] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<any>(false);
  const [isEditLoading, setIsEditLoading] = useState<any>(false);
  const [platName, setPlatName] = useState<any>("");
  const [platSelected, setPlatSelected] = useState<any>([]);
  const [perfData, setPerfData] = useState<any>([]);

  const [changeItem, setChangeItem] = useState<any>({
    id: null,
    name: null,
    account_name: "",
    account_id: "",
    attribution_window: "",
    is_mbm: "No",
  });

  const [editChangeItem, setEditChangeItem] = useState<any>(null);

  const [editChangeItemNew, setEditChangeItemNew] = useState<any>(null);

  const [childItem, setChildItem] = useState<any>({
    id: null,
    name: "",
    account_name: "",
    account_id: "",
    attribution_window: "",
    is_mbm: "No",
  });

  const user: any = JSON.parse(localStorage.getItem("user") || "");
  const user_id = user.id;

  const [isGA4Active, setIsGA4Active] = useState<any>(false);

  // /Update Active Status of Brand
  const [aboutToUpdateActiveBrandShow, setAboutToUpdateActiveBrandShow] =
    useState<any>(false);
  const [
    aboutToUpdateActiveBrandProgress,
    setAboutToUpdateActiveBrandProgress,
  ] = useState<any>(false);
  const [aboutToUpdateActiveBrand, setAboutToUpdateActiveBrand] =
    useState<any>(null);

  const [aboutToUpdateCreativeBrand, setAboutToUpdateCreativeBrand] =
    useState<any>(null);
  const [aboutToUpdateCreativeBrandShow, setAboutToUpdateCreativeBrandShow] =
    useState<any>(false);
  const [
    aboutToUpdateCreativeBrandProgress,
    setAboutToUpdateCreativeBrandProgress,
  ] = useState<any>(false);
  const [aboutToUpdateAcornBrand, setAboutToUpdateAcornBrand] =
    useState<any>(null);
  const [aboutToUpdateAcornBrandShow, setAboutToUpdateAcornBrandShow] =
    useState<any>(false);
  const [aboutToUpdateAcornBrandProgress, setAboutToUpdateAcornBrandProgress] =
    useState<any>(false);
  const [aboutToUpdateProspectBrand, setAboutToUpdateProspectBrand] =
    useState<any>(null);
  const [aboutToUpdateProspectBrandShow, setAboutToUpdateProspectBrandShow] =
    useState<boolean>(false);
  const [aboutToUpdateProspectBrandProgress, setAboutToUpdateProspectBrandProgress] =
    useState<boolean>(false);

  //Update Creative Status of Brand
  const updateCreativeBrandStatus = async () => {
    setAboutToUpdateCreativeBrandProgress(true);
    let formData = {
      is_creative: aboutToUpdateCreativeBrand.is_creative ? "false" : "true",
    };

    try {
      const res = await apiClient.put(
        `/brand/v1/admin/brand/update/${aboutToUpdateCreativeBrand.id}`,
        formData
      );

      const desc = {
        message: `Brand "${aboutToUpdateCreativeBrand.name}" creative status ${aboutToUpdateCreativeBrand.is_creative ? 'disabled' : 'enabled'}`,
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: 'brand',
        brandId: aboutToUpdateCreativeBrand.id,
        brandName: aboutToUpdateCreativeBrand.name,
        changes: [
          {
            label: 'Creative',
            from: humanizeValue(aboutToUpdateCreativeBrand.is_creative, { type: 'boolean' }),
            to: humanizeValue(!aboutToUpdateCreativeBrand.is_creative, { type: 'boolean' }),
          },
        ],
      };
      if (res.data.message) {
        setAboutToUpdateCreativeBrandProgress(false);
        setAboutToUpdateCreativeBrandShow(false);
        setAboutToUpdateCreativeBrand(null);
        getBrands();
        Toastify("success", res.data.message);
        await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      }
    } catch (error: any) {
      setAboutToUpdateCreativeBrandProgress(false);
      setAboutToUpdateCreativeBrandShow(false);
      setAboutToUpdateCreativeBrand(null);
      getBrands();
      Toastify(
        "error",
        error?.response?.data?.message || "Error while updating creative status"
      );
      const errorDesc = {
        message: `Failed to update creative status for brand "${aboutToUpdateCreativeBrand?.name}": ${error?.response?.data?.message || 'Unknown error'}`,
        status: error?.response?.status || 500,
        method: "PUT",
        url: `/brand/v1/admin/brand/update/${aboutToUpdateCreativeBrand.id}`,
        category: 'brand',
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const updateAcornBrandStatus = async () => {
    setAboutToUpdateAcornBrandProgress(true);
    let formData = {
      is_acorn: aboutToUpdateAcornBrand.is_acorn ? "false" : "true",
    };
    try {
      const res = await apiClient.put(
        `/brand/v1/admin/brand/update/${aboutToUpdateAcornBrand.id}`,
        formData
      );
      const desc = {
        message: `Brand "${aboutToUpdateAcornBrand.name}" influencer status ${aboutToUpdateAcornBrand.is_acorn ? 'disabled' : 'enabled'}`,
        category: 'brand',
        brandId: aboutToUpdateAcornBrand.id,
        brandName: aboutToUpdateAcornBrand.name,
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        changes: [
          {
            label: ACORN_BRAND_FEATURE_LABEL,
            from: humanizeValue(aboutToUpdateAcornBrand.is_acorn, { type: 'boolean' }),
            to: humanizeValue(!aboutToUpdateAcornBrand.is_acorn, { type: 'boolean' }),
          },
        ],
      };
      if (res.data.message) {
        setAboutToUpdateAcornBrandProgress(false);
        setAboutToUpdateAcornBrandShow(false);
        setAboutToUpdateAcornBrand(null);
        getBrands();
        Toastify("success", res.data.message);
        await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      }
    } catch (error: any) {
      setAboutToUpdateAcornBrandProgress(false);
      setAboutToUpdateAcornBrandShow(false);
      setAboutToUpdateAcornBrand(null);
      getBrands();
      Toastify(
        "error",
        error?.response?.data?.message ||
          "Error while updating influencer status"
      );
      const errorDesc = {
        message: `Failed to update influencer status for brand "${aboutToUpdateAcornBrand?.name}": ${error?.response?.data?.message || 'Unknown error'}`,
        status: error?.response?.status || 500,
        method: "PUT",
        url: `/brand/v1/admin/brand/update/${aboutToUpdateAcornBrand.id}`,
        category: 'brand',
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  //This brandData includes, soa_link, prospect flag, source of truth etc ( table brand_data )
  const [brandData, setBrandData] = useState<any>([]);
  const [localProspectStatus, setLocalProspectStatus] = useState<
    Record<number, boolean>
  >({});
  const [addRevenueSourceOfTruth, setAddRevenueSourceOfTruth] =
    useState<any>("");
  const [editRevenueSourceOfTruth, setEditRevenueSourceOfTruth] =
    useState<any>("");

  const [addConversionSourceOfTruth, setAddConversionSourceOfTruth] =
    useState<any>("");
  const [editConversionSourceOfTruth, setEditConversionSourceOfTruth] =
    useState<any>("");

  //drop files
  const onDrop = useCallback((acceptedFiles: any) => {
    try {
      setAddFiscalCalendar(null);
      const file = acceptedFiles[0];
      setIsLoading(true);

      if (!file) {
        setIsLoading(false);
        return; // No file selected, do nothing
      }

      // Check for valid Excel file types (.xls and .xlsx)
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!validTypes.includes(file.type)) {
        const timeout = setTimeout(() => {
          setIsLoading(false);

          setAddFiscalCalendar(null);
        }, 1000);
        return;
      }

      setFiscalFile(file);

      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (e: any) => {
        try {
          setExcelData(e.target.result);
        } catch (error) {
          alert("Error reading the file");
        }
      };

      reader.onerror = () => {
        alert("Error reading the file");
      };
    } catch (error: any) {
      Toastify(
        "error",
        error?.response?.data?.message || "Something went wrong!! \n Try Again"
      );
    }
  }, []);

  const onDrop1 = useCallback((acceptedFiles: any) => {
    try {
      setEditFiscalCalendar(null);
      const file = acceptedFiles[0];
      setIsEditLoading(true);

      if (!file) {
        setIsEditLoading(false);
        return; // No file selected, do nothing
      }

      // Check for valid Excel file types (.xls and .xlsx)
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!validTypes.includes(file.type)) {
        const timeout = setTimeout(() => {
          setIsEditLoading(false);
          setEditFiscalCalendar(null);
        }, 1000);
        return;
      }

      setEditFiscalFile(file);

      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (e: any) => {
        try {
          setEditExcelData(e.target.result);
        } catch (error) {
          alert("Error reading the file");
        }
      };

      setIsReached(true);

      reader.onerror = () => {
        alert("Error reading the file");
      };
    } catch (error: any) {
      Toastify(
        "error",
        error?.response?.data?.message || "Something went Wrong!!"
      );
    }
  }, []);
  const {
    getRootProps: getRootPropsForAdd,
    getInputProps: getInputPropsForAdd,
    isDragActive: isDragActiveForAdd,
  } = useDropzone({ onDrop });

  const {
    getRootProps: getRootPropsForEdit,
    getInputProps: getInputPropsForEdit,
    isDragActive: isDragActiveForEdit,
  } = useDropzone({ onDrop: onDrop1 });

  const [singlePlatData, setSinglePlatData] = useState<any>([]);

  //Attribution useStates in performance dialog
  const [values, setValues] = useState([0, 0, 0]);
  const [editValues, setEditValues] = useState<any>(null);
  const [editPerfValues, setEditPerfValues] = useState<any>(null);

  const [editSinglePlatDataAddPerf, setEditSinglePlatDataAddPerf] =
    useState<any>(null);

  const [editSinglePlatData, setEditSinglePlatData] = useState<any>(null);

  const [scrapData, setScrapData] = useState<any>([]);

  const [singleScrapData, setSingleScrapData] = useState<any>({
    platform: "",
    url: "",
  });

  const [excelData, setExcelData] = useState<any>(null);
  const [editExcelData, setEditExcelData] = useState<any>("");

  const [usersSearched, setUsersSearched] = useState<any>([]);
  const [brandsBackup, setBrandsBackup] = useState<any>([]);
  const [page, setPage] = useState<any>(0);
  const [rowsPerPage, setRowsPerPage] = useState<any>(25);
  const inputFile: any = useRef(null);
  const csvInput: any = useRef(null);
  const [showAddBrandDialog, setShowAddBrandDialog] = useState<any>(false);
  const [showAddBrandWizard, setShowAddBrandWizard] = useState(false);
  const [addBrandImageRaw, setAddBrandImageRaw] = useState<any>(null);
  const [addBrandImage, setAddBrandImage] = useState<any>(null);
  const [addBrandName, setAddBrandName] = useState<any>("");
  const [triggerAffluentName, setTriggerAffluentName] = useState<any>(false);
  const [addAlias, setAddAlias] = useState<any>("");
  const [addBrandDate, setAddBrandDate] = useState<any>("");
  const [addBrandVertical, setAddBrandVertical] = useState<any>([]);
  const [verticalsOptions, setVerticalsOptions] = useState<any>([]);
  const [addBrandType, setAddBrandType] = useState<any>("");
  const [addOrg, setAddOrg] = useState<any>("");

  const [addDateJoined, setAddDateJoined] = useState<any>("");

  const [editOrg, setEditOrg] = useState<any>("");
  const [allOrg, setAllOrg] = useState<any>([]);
  const [addBrandDomain, setAddBrandDomain] = useState<any>("");
  const [isScrapEmpty, setIsScrapEmpty] = useState<any>(false);
  const [editFiscalFile, setEditFiscalFile] = useState<any>(null);
  const [fiscalFile, setFiscalFile] = useState<any>(null);
  const [addFiscalCalendar, setAddFiscalCalendar] = useState<any>(null);

  const [editFiscalCalendar, setEditFiscalCalendar] = useState<any>(null);

  const [hideAddBrandProgressDialog, setHideAddBrandProgressDialog] =
    useState<any>(true);
  const editInputFile: any = useRef(null);
  const [editBrandData, setEditBrandData] = useState<any>();
  const [showEditBrandDialog, setShowEditBrandDialog] = useState<any>(false);
  const [editBrandImageRaw, setEditBrandImageRaw] = useState<any>(null);
  const [editBrandImage, setEditBrandImage] = useState<any>(null);
  const [editBrandImageChange, setEditBrandImageChange] = useState<any>(0);
  const [editBrandName, setEditBrandName] = useState<any>();
  const [editBrandDatePreset, setEditBrandDatePreset] = useState<any>();
  const [editAlias, setEditAlias] = useState<any>();
  const [editBrandDate, setEditBrandDate] = useState<any>(new Date());
  const [editBrandVertical, setEditBrandVertical] = useState<any>([]);
  const [editBrandVerticalChange, setEditBrandVerticalChange] = useState<any>();
  const [editBrandType, setEditBrandType] = useState<any>("");
  const [addWeekStartDay, setAddWeekStartDay] = useState<any>("Sunday");
  const [addTimeZone, setAddTimeZone] = useState<any>("Eastern Standard Time");
  const [editWeekStartDay, setEditWeekStartDay] = useState<any>("Sunday");
  const [editTimeZone, setEditTimeZone] = useState<any>(
    "Eastern Standard Time"
  );
  const [editBrandDomain, setEditBrandDomain] = useState<any>();
  const [editBrandDomainChange, setEditBrandDomainChange] = useState<any>();
  const [hideEditBrandProgressDialog, setHideEditBrandProgressDialog] =
    useState<any>(true);
  const [editSOWLink, setEditSOWLink] = useState<any>(null);
  const [editMSALink, setEditMSALink] = useState<any>(null);
  const [editContractTerms, setEditContractTerms] = useState<any>(null);
  const [editDirectorID, setEditDirectorID] = useState<any>(null);
  const [editManagerID, setEditManagerID] = useState<any>(null);
  const [editAssociateID1, setEditAssociateID1] = useState<any>(null);
  const [editAssociateID2, setEditAssociateID2] = useState<any>(null);
  const [editOpsID, setEditOpsID] = useState<any>(null);
  const [editDateJoined, setEditDateJoined] = useState<any>(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState<any>(false);
  const [allUsers, setAllUsers] = useState<any>([]);
  const { admin_auth, superAdmin } = useSelector((state: any) => state.admin);
  const { addDialogOpen, performanceData } = useSelector(
    (state: RootState) => state.performance
  );

  const [addPerfCurrentStep, setAddPerfCurrentStep] = useState(1);
  const [addPerfCompletedSteps, setAddPerfCompletedSteps] = useState<number[]>([]);
  const addPerfFormRef = useRef<AddPerformanceFormRef>(null);
  
  // Permission checks for brand management
  const { canEdit, canManage } = useAuthContext();
  const hasEditAccess = canEdit('brand_mgmt');
  const hasManageAccess = canManage('brand_mgmt');

  //search
  const [searchText, setSearchText] = useState<any>("");
  const [searchTextUser, setSearchTextUser] = useState<any>("");
  const [searchedBrands, setSearchedBrands] = useState<any>([]);
  const [searchQuery, setSearchQuery] = useState<any>("");
  const [deleteBrandDialog, setDeleteBrandDialog] = useState<any>(false);
  const [brandRow, setBrandRow] = useState<any>({});
  const [hideDeleteBrandProgressDialog, setHideDeleteBrandProgressDialog] =
    useState<any>(true);

  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const [selectedEditOptions, setSelectedEditOptions] = useState<any>([]);

  // New fields: is_performance_creator and hubspot_company_id
  const [addIsPerformanceCreator, setAddIsPerformanceCreator] =
    useState<boolean>(false);
  const [editIsPerformanceCreator, setEditIsPerformanceCreator] =
    useState<boolean>(false);

  // Highlight the Brand Features section when validation fails (no feature selected)
  const [highlightBrandFeatures, setHighlightBrandFeatures] =
    useState<boolean>(false);
  const brandFeaturesRef = useRef<HTMLDivElement>(null);
  const [addHubspotCompanyId, setAddHubspotCompanyId] = useState<string>("");
  const [editHubspotCompanyId, setEditHubspotCompanyId] = useState<string>("");

  // Grapevine enrichment fields are enabled by the Performance Creator brand feature.
  const [addGvSlug, setAddGvSlug] = useState<string>("");
  const [editGvSlug, setEditGvSlug] = useState<string>("");
  const { status: addGvSlugStatus, resolvedSlug: addGvResolvedSlug } =
    useGrapevineSlugAvailability(addIsPerformanceCreator ? addGvSlug : "");
  const { status: editGvSlugStatus, resolvedSlug: editGvResolvedSlug } =
    useGrapevineSlugAvailability(
      editIsPerformanceCreator ? editGvSlug : "",
      editBrandData?.id,
    );
  const [addGvVertical, setAddGvVertical] = useState<string>("");
  const [editGvVertical, setEditGvVertical] = useState<string>("");
  const [addGvContactName, setAddGvContactName] = useState<string>("");
  const [editGvContactName, setEditGvContactName] = useState<string>("");
  const [addGvContactEmail, setAddGvContactEmail] = useState<string>("");
  const [editGvContactEmail, setEditGvContactEmail] = useState<string>("");
  const [addGvHomepageUrl, setAddGvHomepageUrl] = useState<string>("");
  const [editGvHomepageUrl, setEditGvHomepageUrl] = useState<string>("");
  const [addGvTier, setAddGvTier] = useState<string>("");
  const [editGvTier, setEditGvTier] = useState<string>("");
  const [editGvLoading, setEditGvLoading] = useState<boolean>(false);
  const [editGvError, setEditGvError] = useState<string>("");
  const editGvRequestRef = useRef(0);
  // Grapevine fields submitted when a brand was created in this session. Grapevine is
  // notified fire-and-forget and the slug/contact fields are not stored in LIFT, so
  // opening Edit right after Add must not depend on Grapevine having the brand yet.
  const recentGrapevineRef = useRef<Map<number, GrapevineEditPrefill>>(
    new Map()
  );
  const [selectedDatePreset, setSelectedDatePreset] = useState<any>(
    "Last week (starts Sunday)"
  );
  const [selectedEditDatePreset, setSelectedEditDatePreset] = useState<any>(
    "Last week (starts Sunday)"
  );

  const [deleteFiscalClicked, setDeleteFiscalClicked] = useState<any>(false);

  const [hasMetaplierAccess, setHasMetaplierAccess] = useState<any>({
    hasAccess: false,
    id: null,
  });

  const handleCloseAlertDialogForMBM = (e: any) => {
    if (e.target.textContent === "Agree") {
      setIsAgreeClickedMBM(true);
    }
    setDialogMBM(false);
  };

  useEffect(() => {
    if (performanceData) {
      setPerfData(performanceData);
    }
  }, [performanceData]);

  const handleClickFiscalDialogOpen = () => {
    setUploadFiscalDialogOpen(true);
  };

  const handleClickFiscalEditDialogOpen = () => {
    setUploadFiscalEditDialogOpen(true);
  };

  const handleClickFiscalDialogClose = () => {
    setUploadFiscalDialogOpen(false);
  };
  const handleClickEditFiscalDialogClose = () => {
    setUploadFiscalEditDialogOpen(false);
  };

  const [scrapPlatforms, setScrapPlatforms] = useState<any>([]);

  const weekDays: any = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // time zone list
  const timezoneList: any = [
    "Eastern Standard Time",
    "Pacific Standard Time",
    "Mountain Standard Time",
    "Central Standard Time",
  ];

  //FISCAL TEMPLATE DOWNLOAD
  const handleDownloadFiscalTemplate = () => {
    const sliceSize = 1024;
    const byteCharacters = atob(EXCEL_FILE_BASE64);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);
    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      let begin = sliceIndex * sliceSize;
      let end = Math.min(begin + sliceSize, bytesLength);
      let bytes = new Array(end - begin);
      for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    FileSaver.saveAs(
      new Blob(byteArrays, { type: "application/vnd.ms-excel" }),
      "fiscal_template.xlsx"
    );
  };

  //Attribution Window
  const isEditable = (index: any, itemId: any, itemAttrWindow: any) => {
    return (
      itemId === index + 1 + perfData?.length &&
      itemAttrWindow !== "" &&
      !(editSinglePlatDataAddPerf?.id === itemId)
    );
  };

  const renderInput = (
    index: any,
    item: any,
    value: any,
    id: any,
    handleKey: any,
    handleChange: any
  ) => (
    <React.Fragment key={id}>
      <input
        disabled={isEditable(index, item.id, item.attribution_window)}
        type="text"
        value={value}
        onKeyDown={(e) => handleKey(id, e)}
        onChange={(e) => handleChange(id, e)}
        style={inputStyle}
      />
      {id < values.length - 1 && <span style={spanStyle}>/</span>}
    </React.Fragment>
  );

  const inputStyle: any = {
    width: "2.7em",
    textAlign: "center",
    border: "none",
    outline: "none",
  };

  const spanStyle = { margin: "0 5px" };

  //SCRAP
  const handleAddScrapItem = () => {
    if (
      (changeScrapItem.id === null && !editScrapItem) ||
      scrapData.length === 0
    ) {
      const new_obj = { ...singleScrapData };
      let new_arr = [...scrapData];
      setChangeScrapItem({ ...changeScrapItem, id: scrapData.length + 1 });
      new_arr.push({ ...new_obj, id: new_arr.length + 1 });
      setScrapData(new_arr);
      setIsAddIconClicked(false);
    } else {
      Toastify("error", "You should complete previous url to create new!");
    }
  };

  const handleSetAdditionalOptns = (event: any) => {
    const { name, checked } = event.target;

    setSelectedOptions((prevSelectedOptions: any) => {
      if (checked) {
        return [...prevSelectedOptions, name];
      } else {
        return prevSelectedOptions.filter((option: any) => option !== name);
      }
    });
  };

  const handleSetEditAdditionalOptns = (event: any) => {
    const { name, checked } = event.target;

    setSelectedEditOptions((prevSelectedOptions: any) => {
      if (checked) {
        return [...prevSelectedOptions, name];
      } else {
        return prevSelectedOptions.filter((option: any) => option !== name);
      }
    });
  };

  const handleEditScrapItem = (data: any) => {
    setEditScrapItem(data);

    handleValidationScrapUrl();
  };

  const getScrapPlatforms = async () => {
    try {
      const res = await apiClient.get(`/file/v1/scraping-platforms/getAll`);

      const options = res.data.response.map((item: any) => ({
        name: item.platform,
        domain: item.domain,
      }));
      setScrapPlatforms(options);

      const desc = {
        message: "Fetched scraping platforms successfully.",
        status: res.status,
        method: "GET",
        url: `/file/v1/scraping-platforms/getAll`,
      };
    } catch (error: any) {
      const errorDesc = {
        message: "Error while getting Scraping Platforms!!",
        status: error.response ? error.response.status : 500,
        method: "GET",
        url: `/file/v1/scraping-platforms/getAll`,
      };
      Toastify(
        "error",
        error?.response?.data?.message ||
          "Error while getting Scraping Platforms!!"
      );
    }
  };
  const [dataSources, setDataSources] = useState([]);
  // List of Data Sources
  const handleGetDataSources = async () => {
    try {
      const response = await axios.get(
        "https://script.googleusercontent.com/a/macros/newengen.com/echo?user_content_key=mRWlWTCv6ZMdbTbGRJqBSGDYI_CjdpIGXouDh_3wqhQ0GB4Fu_JFRZ_LnbM8v-RXezxmUxdmJj_UIl5KYj32Vbr4K0DQ3RJ7OJmA1Yb3SEsKFZqtv3DaNYcMrmhZHmUMi80zadyHLKBw98rs-s2Hwv7hp7eZt5FU68n4RAo2RJz2eHM1tPVCsa3Zh-JLoTbPVypxk8kIK7_Tt9HYH5pdz0KEumnpWIuFDX48PxEKDtV9abvbb2kQrot_ELmeDD2YOdwyHrBXXtzc_ScPTHfLrg&lib=M9tSS4uqL-njC3k6PSa5Z6PTYFf-Ccci9"
      );

      setDataSources(response.data.data);
    } catch (error: any) {}
  };

  useEffect(() => {
    handleGetDataSources();
  }, []);

  // Fetch verticals options
  useEffect(() => {
    const fetchVerticals = async () => {
      try {
        const res = await apiClient.get('/outreach/v1/admin/brand/verticals');
        setVerticalsOptions(res.data.response || []);
      } catch (error: any) {
        console.error('Error fetching verticals:', error);
      }
    };
    fetchVerticals();
  }, []);

  //Brand Goals
  const [brandGoals, setBrandGoals] = useState<any>([
    {
      revenue: 10,
      spend: 10,
      comm_rate: 10,
      conv_rate: 10,
      clicks: 10,
      actions: 10,
      cac: 10,
      new_user: 10,
      new_rev: 10,
      discount: 10,
      aov: 10,
      roas: 10,
      canc: 10,
    },
  ]);

  const docs = [
    {
      uri: "https://www.antennahouse.com/hubfs/xsl-fo-sample/pdf/basic-link-1.pdf",
      fileType: "pdf",
      fileName: "demo.pdf",
    }, // Remote file
    {
      uri: "https://www.antennahouse.com/hubfs/xsl-fo-sample/pdf/basic-link-1.pdf",
    }, // Local File
  ];
  //Brand Weights
  const [brandWeights, setBrandWeights] = useState<any>([
    {
      revenue: 0.22,
      aov: 0.1,
      click: 0.12,
      conv_rate: 0.0,
      new_v_existing: 0.2,
      ecpc: 0.12,
      conversions: 0.0,
      new_user: 0.24,
    },
  ]);

  const handleChangeBrandWeights = (e: any) => {
    const new_data = { ...brandWeights[0] };

    new_data[e.target.id] = e.target.value;
    setBrandWeights([new_data]);
  };
  const handleChangeBrandGoals = (e: any) => {
    const new_data = { ...brandGoals[0] };

    new_data[e.target.id] = e.target.value;
    setBrandGoals([new_data]);
  };

  const [selectedItem, setSelectedItem] = useState<any>(0);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [fileName, setFileName] = useState<string>("");

  const [selectedPerfItem, setSelectedPerfItem] = useState<any>(1);

  const [isOverflowingAdd, setIsOverflowingAdd] = useState<any>(false);

  const scrollHeader: any = useRef<any>(null);

  const [brandGoalDialogOpen, setBrandGoalDialogOpen] = React.useState(false);
  const [uploadFiscalDialogOpen, setUploadFiscalDialogOpen] =
    React.useState(false);
  const [uploadFiscalEditDialogOpen, setUploadFiscalEditDialogOpen] =
    React.useState(false);
  const [dialogMBM, setDialogMBM] = React.useState(false);
  const [isAgreeClickedMBM, setIsAgreeClickedMBM] = React.useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  const [editPendingChange, setEditPendingChange] = useState(null);
  const [pendingChangeEditAdd, setPendingChangeEditAdd] = useState(null);
  const [scrapDialogOpen, setScrapDialogOpen] = React.useState(false);
  const [addPerfDialogOpen, setAddPerfDialogOpen] = React.useState(false);
  const [editPerfDialogOpen, setEditPerfDialogOpen] = React.useState(false);

  const [brandWeightDialogOpen, setBrandWeightDialogOpen] =
    React.useState(false);

  //edit performance dialog
  const handleEditPlatform = (row: any) => {
    dispatch(setEditDialogOpen(true));

    dispatch(setEditData(cloneDeep(row)));

    handleClickEditPerfDialogOpen();

    let attr_values = row.attribution_window.split("/");
    setEditPerfValues(attr_values);
    setEditSinglePlatData(cloneDeep(row));
    setEditChangeItemNew(cloneDeep(row));
  };

  const handleDeletePerfItem = (row: any) => {
    const new_arr: any = [...perfData];

    new_arr.splice(row.id - 1, 1);

    for (let i = row.id - 1; i < performanceData.length - 1; i++) {
      if (new_arr[i]["id"] !== i + 1) {
        new_arr[i]["id"] = i + 1;
      }
    }

    dispatch(setPerformanceData(new_arr));
    setPerfData(new_arr);
    Toastify("success", "Successfully Deleted!");
  };

  // add the scrollabe container ref to the container in add brand dialog
  const scrollableContainerRef: any = useRef(null);
  // add this ref to the element you want it to scroll to
  const elementRef: any = useRef(null);
  useEffect(() => {
    if (selectedOptions.length === 0) {
      setSelectedItem(0);
    }
    // Clear the "brand feature required" error once a feature is selected
    if (selectedOptions.length > 0 || addIsPerformanceCreator) {
      setHighlightBrandFeatures(false);
    }
    // Affiliate/Performance step UI is hidden - no longer auto-switch to step 1 or 2
  }, [selectedOptions, addIsPerformanceCreator]);

  useEffect(() => {
    if (selectedEditOptions.length === 0) {
      setSelectedItem(0);
    }
    // Affiliate/Performance step UI is hidden - no longer auto-switch to step 1 or 2
  }, [selectedEditOptions]);

  const handleClickBrandGoalDialogOpen = () => {
    setBrandGoalDialogOpen(true);
  };

  const handleClickScrapDialogOpen = () => {
    setScrapDialogOpen(true);
  };
  const handleClickAddPerfDialogOpen = () => {
    dispatch(setAddDialogOpen(true));
  };

  const handleClickEditPerfDialogOpen = () => {
    setEditPerfDialogOpen(true);
  };

  const handleCloseBrandGoalDialog = () => {
    setBrandGoalDialogOpen(false);
  };

  const handleClickBrandWeightDialogOpen = () => {
    setBrandWeightDialogOpen(true);
  };

  const handleCloseBrandWeightDialog = () => {
    setBrandWeightDialogOpen(false);
  };

  const handleCloseScrapDialog = () => {
    setScrapDialogOpen(false);
  };

  const handleCloseAddPerfDialogCloseBtn = () => {
    setAddPerfDialogOpen(false);
  };

  //EDIT PERFORMANCE
  const handleEditChildItem = (data: any, e: any) => {
    if (e.target.textContent.toLowerCase() === "edit") {
      setEditChangeItemNew(cloneDeep(data));
    } else {
      if (
        editChangeItemNew.account_id !== "" &&
        editChangeItemNew.account_name !== ""
      ) {
        let new_obj_oo = { ...editChangeItemNew };
        let attr_values = `${editPerfValues[0]}/${editPerfValues[1]}/${editPerfValues[2]}`;

        if (
          editChangeItemNew.name === "Pinterest Ads" &&
          attr_values === "0/0/0"
        ) {
          Toastify("error", "Attribution values cannot be 0/0/0 for Pinterest");
        } else {
          if (editChangeItemNew.name !== "Facebook Ads") {
            new_obj_oo = {
              ...new_obj_oo,
              attribution_window: attr_values,
              is_mbm: "No",
            };
          } else {
            new_obj_oo = { ...new_obj_oo, attribution_window: attr_values };
          }
          setEditSinglePlatData(new_obj_oo);
          setEditChangeItemNew(null);
        }
      } else {
        Toastify("error", "Account Name and Account Id cannot be empty");
      }
    }
  };

  const handleEditChangeItem = (e: any) => {
    const new_obj = { ...editChangeItem };
    new_obj[e.target.id] = e.target.value;
    setEditChangeItem({ ...new_obj });
  };

  useEffect(() => {
    if (isAgreeClickedMBM && editPendingChange) {
      handlePendingChangeEdit(editPendingChange);
    }
  }, [isAgreeClickedMBM]);

  const handleEditChangeItemNew = (e: any) => {
    const new_obj = { ...editChangeItemNew };

    if (e.target.name === "name") {
      new_obj[e.target.name] = e.target.value;
    } else {
      if (e.target.name === "is_mbm") {
        if (e.target.value === "Yes") {
          if (hasMetaplierAccess.hasAccess) {
            setDialogMBM(true);
            setEditPendingChange(e);
          } else {
            setHasMetaplierAccess({ hasAccess: true, id: new_obj.id });
            new_obj[e.target.name] = e.target.value;
          }
        } else {
          if (hasMetaplierAccess.id === new_obj.id) {
            setHasMetaplierAccess({ hasAccess: false, id: null });
          }
          new_obj[e.target.name] = e.target.value;
        }
      }
      new_obj[e.target.id] = e.target.value;
    }
    setEditChangeItemNew(new_obj);
  };

  const handleCloseEditSavePerfDialog = () => {
    let new_obj = { ...editSinglePlatData };
    let attr_values = `${editPerfValues[0]}/${editPerfValues[1]}/${editPerfValues[2]}`;
    new_obj = { ...new_obj, attribution_window: attr_values };
    const new_arr = [...perfData];
    new_arr.splice(new_obj.id - 1, 1, new_obj);
    handleCloseEditPerfDialog();
    Toastify("success", `${editSinglePlatData?.name} Successfully edited`);
    setEditSinglePlatData(null);
    setEditPerfValues(null);
  };

  //ADD PERFORMANCE
  const handleCloseAddPerfDialog = () => {
    if (changeItem.name !== null) {
      if (changeItem.account_id !== "" && changeItem.account_name !== "") {
        Toastify("error", "You need to add your subplatform first");
      } else {
        Toastify("error", "Account Id and Account Name are required");
      }
    } else {
      if (singlePlatData.length > 0) {
        const arr = [...perfData];
        setPlatSelected([...platSelected, singlePlatData.name]);
        Toastify("success", `Performance is added successfully`);
        setSinglePlatData([]);
        setAddPerfDialogOpen(false);
      } else {
        Toastify("error", "You need to add atleast one data source");
      }
    }
  };

  const handleCloseEditPerfDialog = () => {
    setEditPerfDialogOpen(false);
  };

  const handleAddNewPlatform = () => {
    if (platTitle !== "") {
      setSinglePlatData({ ...singlePlatData });
    } else {
      Toastify("error", "PlatForm name is required");
    }
  };

  const handleAddChildItemToPlat = (item: any, index: any, e: any) => {
    if (e.target.textContent.toLowerCase() === "edit") {
      if (!changeItem.id) {
        setEditSinglePlatDataAddPerf(item);
        let attribution_window = item.attribution_window.split("/");
        setEditValues(attribution_window);
      } else {
        Toastify("error", "First Complete adding new platform");
      }
    } else if (e.target.textContent.toLowerCase() === "add") {
      if (
        changeItem.account_name !== "" &&
        changeItem.account_id !== "" &&
        changeItem.name !== ""
      ) {
        let attr_window = `${values[0]}/${values[1]}/${values[2]}`;
        if (changeItem.name === "Pinterest Ads" && attr_window === "0/0/0") {
          Toastify("error", "Attribution window cannot be 0/0/0 in Pinterest");
        } else {
          const new_obj = [...singlePlatData];
          const desired_obj = new_obj[index];
          desired_obj["name"] = changeItem.name;
          desired_obj["account_name"] = changeItem.account_name;
          desired_obj["account_id"] = changeItem.account_id;
          desired_obj["attribution_window"] = attr_window;
          setValues([0, 0, 0]);

          if (changeItem.name !== "Facebook Ads") {
            desired_obj["is_mbm"] = "No";
          } else {
            desired_obj["is_mbm"] = changeItem.is_mbm;
          }

          new_obj.splice(index, 1, desired_obj);

          setSinglePlatData(new_obj);
          setChangeItem({
            name: null,
            account_name: "",
            account_id: "",
            id: null,
            is_mbm: "No",
            attribution_window: "",
          });
        }
      } else {
        Toastify("error", "Account Id, Account Name are required");
      }
    } else {
      let new_obj = { ...editSinglePlatDataAddPerf };
      let attr_window = `${editValues[0]}/${editValues[1]}/${editValues[2]}`;
      new_obj = { ...new_obj, attribution_window: attr_window };
      const singlePlatObj = [...singlePlatData];
      singlePlatObj.splice(new_obj.id - 1 - perfData?.length, 1, new_obj);

      setSinglePlatData(singlePlatObj);

      setEditSinglePlatDataAddPerf(null);
      setEditValues(null);
    }
  };

  const handleRemoveChildItemFromPlat = (index: any) => {
    const new_obj = [...singlePlatData];
    const desired_arr = new_obj;
    if (hasMetaplierAccess.id === index + 1) {
      setHasMetaplierAccess({ hasAccess: false, id: null });
    }
    desired_arr.splice(index, 1);
    for (let i = index; i < desired_arr.length; i++) {
      if (hasMetaplierAccess.id === desired_arr[i]["id"]) {
        setHasMetaplierAccess({ ...hasMetaplierAccess, id: i + 1 });
      }
      if (desired_arr[i]["id"] !== i + 1) {
        desired_arr[i]["id"] = i + 1;
      } else {
        continue;
      }
    }
    setSinglePlatData(desired_arr);
    setChangeItem({
      name: null,
      account_name: "",
      account_id: "",
      id: null,
      is_mbm: "No",
      attribution_window: "",
    });
  };

  const handleAddPlatName = (e: any) => {
    if (changeItem.name) {
      Toastify("error", "Add below one first then you can add new one");
    } else {
      if (platName !== "") {
        const new_obj = { ...childItem };
        new_obj["name"] = platName;
        new_obj["id"] =
          singlePlatData.length > 0
            ? perfData?.length + 1 + singlePlatData.length
            : perfData?.length + 1;

        setChangeItem({ ...changeItem, name: platName, id: new_obj.id });
        const new_obj_plat = [...singlePlatData];

        new_obj_plat.push(new_obj);

        setSinglePlatData(new_obj_plat);
        setChildItem({
          id: null,
          name: "",
          account_name: "",
          account_id: "",
          attribution_window: "",
          is_mbm: "No",
        });
        setPlatName("");
      } else {
        Toastify("error", "Data Source Name is required");
      }
    }
  };

  const handlePendingChange = (e: any) => {
    let new_item: any = "";

    new_item = { ...changeItem };

    let req_obj: any = "";

    if (perfData?.length > 0) {
      req_obj = perfData?.filter((item: any) => {
        return item.id === hasMetaplierAccess?.id;
      })[0];

      if (req_obj) {
        req_obj.is_mbm = "No";

        const new_arr = [...perfData];
        new_arr.splice(req_obj.id - 1, 1, req_obj);
        new_item[e.target.name] = e.target.value;

        setChangeItem(new_item);
      }
    }

    if (singlePlatData.length > 0) {
      req_obj = singlePlatData.filter((item: any) => {
        return item.id === hasMetaplierAccess?.id;
      })[0];
      if (req_obj) {
        req_obj.is_mbm = "No";
        const new_arr = [...singlePlatData];
        new_arr.splice(req_obj.id - 1 - perfData?.length, 1, req_obj);
        setSinglePlatData(new_arr);
        new_item[e.target.name] = e.target.value;

        setChangeItem(new_item);
      }
    }
    setHasMetaplierAccess({ hasAccess: true, id: new_item.id });
    setIsAgreeClickedMBM(false);
    return;
  };

  const handlePendingChangeEdit = (e: any) => {
    let new_item: any = "";

    new_item = { ...editChangeItemNew };

    let req_obj: any = "";

    if (perfData?.length > 0) {
      req_obj = perfData?.filter((item: any) => {
        return item.id === hasMetaplierAccess?.id;
      })[0];

      if (req_obj) {
        req_obj.is_mbm = "No";

        const new_arr = [...perfData];
        new_arr.splice(req_obj.id - 1, 1, req_obj);
        new_item[e.target.name] = e.target.value;

        setEditChangeItemNew(new_item);
      }
    }

    setHasMetaplierAccess({ hasAccess: true, id: new_item.id });
    setIsAgreeClickedMBM(false);
    return;
  };

  const handlePendingChangeEditAdd = (e: any) => {
    let new_item: any = "";

    new_item = { ...editSinglePlatDataAddPerf };

    let req_obj: any = "";

    if (perfData?.length > 0) {
      req_obj = perfData?.filter((item: any) => {
        return item.id === hasMetaplierAccess?.id;
      })[0];

      if (req_obj) {
        req_obj.is_mbm = "No";

        const new_arr = [...perfData];
        new_arr.splice(req_obj.id - 1, 1, req_obj);
        new_item[e.target.name] = e.target.value;

        setEditSinglePlatDataAddPerf(new_item);
      }
    }

    if (singlePlatData.length > 0) {
      req_obj = singlePlatData.filter((item: any) => {
        return item.id === hasMetaplierAccess?.id;
      })[0];
      if (req_obj) {
        req_obj.is_mbm = "No";
        const new_arr = [...singlePlatData];
        new_arr.splice(req_obj.id - 1 - perfData?.length, 1, req_obj);
        setSinglePlatData(new_arr);
        new_item[e.target.name] = e.target.value;

        setEditSinglePlatDataAddPerf(new_item);
      }
    }
    setHasMetaplierAccess({ hasAccess: true, id: new_item.id });
    setIsAgreeClickedMBM(false);
    return;
  };

  useEffect(() => {
    if (isAgreeClickedMBM && pendingChange) {
      handlePendingChange(pendingChange);
    }
  }, [isAgreeClickedMBM]);

  useEffect(() => {
    if (isAgreeClickedMBM && pendingChangeEditAdd) {
      handlePendingChangeEditAdd(pendingChangeEditAdd);
    }
  }, [isAgreeClickedMBM]);

  const handleChangeChildItem = (e: any) => {
    const new_obj = { ...changeItem };

    if (e.target.name === "name") {
      new_obj[e.target.name] = e.target.value;
    } else {
      if (e.target.name === "is_mbm") {
        if (e.target.value === "Yes") {
          if (hasMetaplierAccess.hasAccess) {
            setDialogMBM(true);
            setPendingChange(e);
          } else {
            new_obj[e.target.name] = e.target.value;
            setHasMetaplierAccess({ hasAccess: true, id: new_obj.id });
          }
        } else {
          if (hasMetaplierAccess?.id === new_obj.id) {
            setHasMetaplierAccess({ hasAccess: false, id: null });
          }
          new_obj[e.target.name] = e.target.value;
        }
      } else {
        new_obj[e.target.id] = e.target.value;
      }
    }
    setChangeItem(new_obj);
  };

  const handleChangeEditSinglePlatDataAddPerf = (e: any) => {
    const new_obj = { ...editSinglePlatDataAddPerf };
    if (e.target.name === "name") {
      new_obj[e.target.name] = e.target.value;
    } else {
      if (e.target.name === "is_mbm") {
        if (e.target.value === "Yes") {
          if (hasMetaplierAccess.hasAccess) {
            setDialogMBM(true);
            setPendingChangeEditAdd(e);
          } else {
            setHasMetaplierAccess({ hasAccess: true, id: new_obj.id });
            new_obj[e.target.name] = e.target.value;
          }
        } else {
          if (hasMetaplierAccess.id === new_obj.id) {
            setHasMetaplierAccess({ hasAccess: false, id: null });
          }
          new_obj[e.target.name] = e.target.value;
        }
      } else {
        new_obj[e.target.id] = e.target.value;
      }
    }
    setEditSinglePlatDataAddPerf(new_obj);
  };

  const platformNames = [
    "Google",
    "Meta",
    "Microsoft",
    "Social Network",
    "Cloud Platform",
    "Gaming Engine",
    "Search Service",
    "AI Labs",
    "Big Data Analytics",
    "Machine Learning Framework",
  ];
  useEffect(() => {
    const headContainer = scrollHeader.current;
    if (headContainer) {
      setIsOverflowingAdd(
        headContainer.scrollWidth > headContainer.clientWidth
      );
    }
  }, [setIsOverflowingAdd]);

  const date = new Date(0);
  useEffect(() => {
    setEditDateJoined(date.toISOString());
  }, []);

  // Format Date
  const formatDate = (timeStamp: any) => {
    let date = new Date(timeStamp);
    return (
      date.getMonth() + 1 + "/" + date.getDate() + "/" + date.getFullYear()
    );
  };

  // Update data on page change
  const handleChangePage = (_: any, newPage: any) => {
    setPage(newPage);
  };

  // Update data when rows per page count is changed
  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const get_ref = useRef(true);

  // Total items from server for pagination display
  const [totalBrandsCount, setTotalBrandsCount] = useState(0);
  const searchQueryRef = useRef("");

  useEffect(() => {
    if (get_ref.current) {
      get_ref.current = false;
      getBrandsPage(0, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //super token

  const [loadList, setLoadList] = useState(true);

  const getBrandsPage = async (pageIndex: number, pageSize: number, search?: string) => {
    setLoadList(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const params: any = { page: pageIndex + 1, limit: pageSize };
      if (search && search.trim()) {
        params.search = search.trim();
      }
      const brandResponse = await apiClient.get(
        `/brand/v1/admin/brands/allFields/${user.id}`,
        { params }
      );
      if (!brandResponse?.data) {
        Toastify("error", "Error while fetching Brands");
        return;
      }
      const items = brandResponse.data.response ?? [];
      const pagination = brandResponse.data.pagination;

      setBrands(items);
      if (pagination) {
        setTotalBrandsCount(pagination.total ?? items.length);
      } else {
        setTotalBrandsCount(items.length);
      }
    } catch (err: any) {
      Toastify(
        "error",
        err?.response?.data?.message || "Error while getting brands!!"
      );
      console.error(err);
    } finally {
      setLoadList(false);
    }
  };

  const getBrands = () => {
    getBrandsPage(page, rowsPerPage, searchQueryRef.current);
  };

  useEffect(() => {
    getBrandsPage(page, rowsPerPage, searchQueryRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const throttleGetBrands = useThrottledFn(getBrands, 1000);

  const customSortBasedOnDate = (a: any, b: any) => {
    if (superAdmin) {
      return new Date(a.id).getTime() - new Date(b.id).getTime();
    } else {
      return new Date(a.id).getTime() - new Date(b.id).getTime();
    }
  };

  const customSortBasedOnActive = (a: any, b: any) => {
    if (superAdmin) {
      return new Date(b.is_active).getTime() - new Date(a.is_active).getTime();
    } else {
      return new Date(a.id).getTime() - new Date(b.id).getTime();
    }
  };

  const refreshOrganizations = useCallback((): Promise<
    { value: number; label: string; is_active: boolean }[]
  > => {
    return apiClient
      .get(`/brand/v1/organizations/all`)
      .then((res) => {
        const orgs = res.data.response
          .map((organization: any) => ({
            value: organization.id,
            label: organization.name,
            is_active: organization.is_active,
          }))
          .sort(customSortBasedOnActive);
        setAllOrg(orgs);
        return orgs;
      })
      .catch(async (error) => {
        const desc = {
          message:
            error?.response?.data?.message || "Error fetching organizations",
          status: error?.response?.status,
          method: "GET",
          url: `/brand/v1/organizations/all`,
        };
        return [];
      });
  }, [superAdmin]);

  const resetAddBrand = () => {
    //reset necessary brand fields
    setAddBrandImageRaw(null);
    setAddBrandImage(null);
    setAddBrandName("");
    setAddOrg("");
    setAddBrandVertical([]);
    setAddBrandDomain("");
    setAddAffiliateAlias("");
    setAddAlias("");
    setAddBrandType("");
    setAddBrandDate(new Date());
    setAddTimeZone("Eastern Standard Time");
    setAddWeekStartDay("Sunday");
    setSelectedDatePreset("Last week (starts Sunday)");
    setAddDateJoined(new Date());
    setHideAddBrandProgressDialog(true);
    setShowAddBrandDialog(false);
    setSelectedOptions([]);
    setIsGA4Active(false);

    // Reset GA4 rows
    setGa4Rows([
      {
        accountId: "",
        accountName: "",
        propertyId: "",
        propertyName: "",
        source: "",
        medValues: [],
        singleMedValue: "",
      },
    ]);

    //reset scraping data
    setScrapData([]);

    //reset fiscal data
    setAddFiscalCalendar(null);
    setFiscalFile("");

    //reset checkbox selection
    setSelectedOptions([]);

    //reset revenue source of truth

    setAddRevenueSourceOfTruth("");
    setAddConversionSourceOfTruth("");

    // Reset Grapevine fields.
    setAddGvSlug("");
    setAddGvVertical("");
    setAddGvContactName("");
    setAddGvContactEmail("");
    setAddGvHomepageUrl("");
    setAddGvTier("");

    //reset-performance-data
    dispatch(setPerformanceData([]));
    setPerfData([]);
  };

  const addBrand = async () => {
    try {
      // Validate input fields
      if (
        typeof addBrandName === "undefined" ||
        addBrandName === null ||
        addBrandName === ""
      ) {
        Toastify("warning", "Name of brand is required!");
        return;
      } else if (!addAlias || addAlias.trim() === "" || !isEmail(addAlias)) {
        Toastify("warning", "Alias must be an email!!");
        return;
      } else if (
        typeof addBrandVertical === "undefined" ||
        addBrandVertical === null ||
        addBrandVertical.length === 0
      ) {
        Toastify("warning", "Vertical of brand is required!");
        return;
      } else if (
        typeof addBrandType === "undefined" ||
        addBrandType === null ||
        addBrandType === ""
      ) {
        Toastify("warning", "Program type of brand is required!");
        return;
      } else if (
        typeof addBrandDomain === "undefined" ||
        addBrandDomain === null ||
        addBrandDomain === ""
      ) {
        Toastify("warning", "Domain of brand is required!");
        return;
      } else if (
        !isValidDomain(addBrandDomain) ||
        addBrandDomain.split(":")[0] === "http" ||
        addBrandDomain.split(":")[0] === "https"
      ) {
        Toastify(
          "warning",
          "Domain of brand should be of format eg: domain_name.com"
        );
        return;
      } else if (
        typeof addOrg === "undefined" ||
        addOrg === null ||
        addOrg === ""
      ) {
        Toastify("warning", "Organization of Brand is required");
        return;
      } else if (selectedOptions.length === 0 && !addIsPerformanceCreator) {
        setHighlightBrandFeatures(true);
        brandFeaturesRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      } else if (
        selectedOptions.includes("Affiliate") &&
        isGA4Active &&
        ga4Rows.some(
          (row: any) =>
            !row.propertyId || row.propertyId.toString().trim() === ""
        )
      ) {
        Toastify("warning", "GA4 Property ID is required");
        return;
      } else {
        const grapevineError = getGrapevineValidationError(
          addIsPerformanceCreator,
          addHubspotCompanyId,
          {
            slug: addGvSlug,
            vertical: addGvVertical,
            contactName: addGvContactName,
            contactEmail: addGvContactEmail,
            homepageUrl: addGvHomepageUrl,
            tier: addGvTier,
          },
          isEmail
        );
        if (grapevineError) {
          Toastify("warning", grapevineError);
          return;
        }
        const slugError = grapevineSlugSubmitError(
          addIsPerformanceCreator,
          addGvSlug,
          addGvSlugStatus,
        );
        if (slugError) {
          Toastify("warning", slugError);
          return;
        }
      }

      setHideAddBrandProgressDialog(false);

      // Create brand
      const formData = new FormData();
      formData.append("name", addBrandName);
      if (addBrandDate) {
        formData.append("launch", moment(addBrandDate).toISOString());
      }
      formData.append("vertical", addBrandVertical.join(","));
      formData.append("type", addBrandType);
      formData.append("domain", addBrandDomain);
      formData.append("image_url", addBrandImageRaw || "");
      formData.append("paid_alias", addAlias);
      if (addAffiliateAlias && addAffiliateAlias !== "") {
        formData.append("affiliate_alias", addAffiliateAlias);
      }
      formData.append("organization_id", addOrg);
      formData.append("is_active", "true");
      formData.append(
        "is_affiliate",
        selectedOptions.includes("Affiliate") ? "true" : "false"
      );
      formData.append(
        "is_paid",
        selectedOptions.includes("Performance") ? "true" : "false"
      );
      formData.append(
        "is_creative",
        selectedOptions.includes("Creative") ? "true" : "false"
      );
      formData.append(
        "is_acorn",
        selectedOptions.includes("Acorn") ? "true" : "false"
      );
      appendGrapevineFormData(formData, addIsPerformanceCreator, addHubspotCompanyId, {
        slug: addGvSlug,
        vertical: addGvVertical,
        contactName: addGvContactName,
        contactEmail: addGvContactEmail,
        homepageUrl: addGvHomepageUrl,
        tier: addGvTier,
      });

      formData.append("week_start_day", addWeekStartDay);
      formData.append("timezone", addTimeZone);

      if (addDateJoined) {
        formData.append("date_joined", moment(addDateJoined).toISOString());
      }
      formData.append(
        "date_preset",
        selectedDatePreset || "Last week (starts Sunday)"
      );

      const res = await apiClient.post(`/brand/v1/admin/add/brand`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (addIsPerformanceCreator && res.data?.response?.id) {
        recentGrapevineRef.current.set(res.data.response.id, {
          slug: addGvSlug,
          hubspotCompanyId: addHubspotCompanyId,
          vertical: addGvVertical,
          contactName: addGvContactName,
          contactEmail: addGvContactEmail,
          homepageUrl: addGvHomepageUrl,
          tier: addGvTier,
        });
      }

      // Handle brand creation success
      setHideAddBrandProgressDialog(true);
      setShowAddBrandDialog(false);
      setSelectedOptions([]);
      resetForm();

      const createdBrandOrgName = allOrg?.find(
        (o: any) => String(o.value) === String(addOrg)
      )?.label;
      const createdBrandProducts = ["Affiliate", "Performance", "Creative", "Acorn"].filter(
        (p) => selectedOptions.includes(p)
      );
      const desc = {
        message: buildSummary('Brand', addBrandName, 'created'),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: 'brand',
        action: 'create_brand',
        brandName: addBrandName,
        details: buildDetails(
          normalizeBrandForLog({
            name: addBrandName,
            organization: createdBrandOrgName || `ID ${addOrg}`,
            type: addBrandType,
            domain: addBrandDomain,
            vertical: addBrandVertical,
            timezone: addTimeZone,
            week_start_day: addWeekStartDay,
            date_preset: selectedDatePreset || "Last week (starts Sunday)",
            launch: addBrandDate,
            date_joined: addDateJoined,
            paid_alias: addAlias,
            affiliate_alias: addAffiliateAlias,
            hubspot_company_id: addHubspotCompanyId,
            is_performance_creator: addIsPerformanceCreator,
            products: createdBrandProducts,
            is_active: true,
          }),
          BRAND_CREATE_FIELD_SPECS,
          { skipEmpty: true }
        ),
      };

      await actionLog?.actionLogTrack(JSON.stringify(desc), false);

      if (res.data.message.includes("Error")) {
        Toastify("warning", res.data.message);
      } else {
        Toastify("success", res.data.message);
      }

      // Call parallel APIs
      await Promise.all([
        addBrandGoals(res.data.response.id),
        addBrandWeights(res.data.response.id),
        addFiscalCalendarFn(res.data.response.id),
        addScraping(res.data.response.id),
        addGA4Values(res.data.response.id),
        addPaidAccounts(res.data.response.id),
        addBrandData(res.data.response.id),
        // Only call handleBrandProspectToggle if "Prospect" is selected
        ...(selectedOptions.includes("Prospect")
          ? [handleBrandProspectToggle(res.data.response.id)]
          : []),
      ]);

      getBrands();
      fetchBrandData();

      if (selectedOptions.includes("Performance")) {
        sessionStorage.setItem("addPerfForNewBrand", JSON.stringify({
          id: res.data.response.id,
          name: addBrandName,
        }));
        navigate("/performance");
      }
    } catch (error: any) {
      setHideAddBrandProgressDialog(true);
      console.error(error);
      if (error.response) {
        Toastify(
          "error",
          error?.response?.data?.message ||
            "Error while adding brand!! \n Try Again!!"
        );
      } else {
        Toastify("error", error || "Something went wrong !!");
      }
    }
  };

  const addBrandFromWizard = useCallback(
    async (result: AddBrandWizardResult) => {
      const selected = result.productFeatures;
      const launchDate = addBrandDate || new Date();
      const dateJoined = addDateJoined || new Date();
      const datePreset =
        selectedDatePreset || "Last week (starts Sunday)";

      const formData = new FormData();
      formData.append("name", result.name);
      formData.append("launch", moment(launchDate).toISOString());
      formData.append("vertical", result.vertical.join(","));
      formData.append("type", result.type);
      formData.append("domain", result.domain);
      formData.append("image_url", "");
      formData.append("paid_alias", result.alias);
      formData.append("organization_id", result.organizationId);
      formData.append("is_active", "true");
      formData.append(
        "is_affiliate",
        selected.includes("Affiliate") ? "true" : "false",
      );
      formData.append(
        "is_paid",
        selected.includes("Performance") ? "true" : "false",
      );
      formData.append(
        "is_creative",
        selected.includes("Creative") ? "true" : "false",
      );
      formData.append(
        "is_acorn",
        selected.includes("Acorn") ? "true" : "false",
      );
      appendGrapevineFormData(
        formData,
        result.isPerformanceCreator,
        result.hubspotCompanyId,
        {
          slug: result.gvSlug,
          vertical: result.gvVertical,
          contactName: result.gvContactName,
          contactEmail: result.gvContactEmail,
          homepageUrl: result.gvHomepageUrl,
          tier: result.gvTier,
        },
      );
      formData.append("week_start_day", addWeekStartDay);
      formData.append("timezone", addTimeZone);
      formData.append("date_joined", moment(dateJoined).toISOString());
      formData.append("date_preset", datePreset);

      try {
        const res = await apiClient.post(`/brand/v1/admin/add/brand`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (result.isPerformanceCreator && res.data?.response?.id) {
          recentGrapevineRef.current.set(res.data.response.id, {
            slug: result.gvSlug,
            hubspotCompanyId: result.hubspotCompanyId,
            vertical: result.gvVertical,
            contactName: result.gvContactName,
            contactEmail: result.gvContactEmail,
            homepageUrl: result.gvHomepageUrl,
            tier: result.gvTier,
          });
        }

        const createdBrandProducts = [
          "Affiliate",
          "Performance",
          "Creative",
          "Acorn",
        ].filter((p) => selected.includes(p as WizardProductFeature));
        const desc = {
          message: buildSummary("Brand", result.name, "created"),
          status: res.status,
          method: res.config.method,
          url: res.config.url,
          category: "brand",
          action: "create_brand",
          brandName: result.name,
          details: buildDetails(
            normalizeBrandForLog({
              name: result.name,
              organization:
                result.organizationLabel ||
                allOrg?.find(
                  (o: any) => String(o.value) === String(result.organizationId),
                )?.label ||
                `ID ${result.organizationId}`,
              type: result.type,
              domain: result.domain,
              vertical: result.vertical,
              timezone: addTimeZone,
              week_start_day: addWeekStartDay,
              date_preset: datePreset,
              launch: launchDate,
              date_joined: dateJoined,
              paid_alias: result.alias,
              hubspot_company_id: result.hubspotCompanyId,
              is_performance_creator: result.isPerformanceCreator,
              products: createdBrandProducts,
              is_active: true,
            }),
            BRAND_CREATE_FIELD_SPECS,
            { skipEmpty: true },
          ),
        };

        await actionLog?.actionLogTrack(JSON.stringify(desc), false);

        if (res.data.message.includes("Error")) {
          Toastify("warning", res.data.message);
          return;
        }

        Toastify("success", res.data.message);

        const brandId = res.data.response.id;
        await Promise.all([
          addBrandGoals(brandId),
          addBrandWeights(brandId),
          addFiscalCalendarFn(brandId),
          addScraping(brandId),
          addGA4Values(brandId),
          addPaidAccounts(brandId),
          addBrandData(brandId),
          ...(selected.includes("Prospect")
            ? [handleBrandProspectToggle(brandId)]
            : []),
        ]);

        getBrands();
        fetchBrandData();

        if (selected.includes("Performance")) {
          sessionStorage.setItem(
            "addPerfForNewBrand",
            JSON.stringify({ id: brandId, name: result.name }),
          );
          navigate("/performance");
        }
      } catch (error: any) {
        console.error(error);
        Toastify(
          "error",
          error?.response?.data?.message ||
            "Error while adding brand!! \n Try Again!!",
        );
        throw error;
      }
    },
    [
      actionLog,
      addBrandDate,
      addDateJoined,
      addTimeZone,
      addWeekStartDay,
      allOrg,
      selectedDatePreset,
      navigate,
    ],
  );

  const addBrandGoals = async (brandId: number) => {
    try {
      const res = await apiClient.post(
        `/brand/v1/admin/add/brandGoals/${brandId}`,
        brandGoals[0]
      );

      setBrandGoals([
        {
          revenue: 10,
          spend: 10,
          comm_rate: 10,
          conv_rate: 10,
          clicks: 10,
          actions: 10,
          cac: 10,
          new_user: 10,
          new_rev: 10,
          discount: 10,
          aov: 10,
          roas: 10,
          canc: 10,
        },
      ]);

      // Log success
      const successDesc = {
        message: `Added brand goals for brand ID ${brandId}`,
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: 'brand',
        brandId,
      };
      await actionLog?.actionLogTrack(JSON.stringify(successDesc), false);
    } catch (error: any) {
      Toastify("error", error || "Error adding brand goals");
      console.error(error);

      // Log error
      const errorDesc = {
        message: `Error adding brand goals for brand ID ${brandId}: ${error.message}`,
        status: error.response?.status || 500,
        method: "POST",
        url: `/brand/v1/admin/add/brandGoals/${brandId}`,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const addBrandWeights = async (brandId: number) => {
    try {
      const res = await apiClient.post(
        `/brand/v1/admin/add/brandWeights/${brandId}`,
        brandWeights[0]
      );

      setBrandWeights([
        {
          revenue: 0.22,
          aov: 0.1,
          click: 0.12,
          conv_rate: 0.0,
          new_v_existing: 0.2,
          ecpc: 0.12,
          conversions: 0.0,
          new_user: 0.24,
        },
      ]);

      // Log success
      const successDesc = {
        message: `Added brand weights for brand ID ${brandId}`,
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: 'brand',
        brandId,
      };
      await actionLog?.actionLogTrack(JSON.stringify(successDesc), false);
    } catch (error: any) {
      console.error(error);
      Toastify("error", error || "Error adding brand weights");

      // Log error
      const errorDesc = {
        message: `Error adding brand weights for brand ID ${brandId}: ${error.message}`,
        status: error.response?.status || 500,
        method: "POST",
        url: `/brand/v1/admin/add/brandWeights/${brandId}`,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const addFiscalCalendarFn = async (brandId: number) => {
    try {
      if (fiscalFile) {
        const fiscalFormData = new FormData();
        fiscalFormData.append("fiscal_calendar", fiscalFile);
        const res = await apiClient.post(
          `/fiscal/v1/fiscal_calendar/brand/${brandId}`,
          fiscalFormData
        );

        setFiscalFile(null);
        setAddFiscalCalendar(null);

        // Log success
        const successDesc = {
          message: `Fiscal calendar uploaded successfully for brand ID ${brandId}`,
          status: res.status,
          method: res.config.method,
          url: res.config.url,
        };
        await actionLog?.actionLogTrack(JSON.stringify(successDesc), false);
      }
    } catch (error: any) {
      console.error(error);
      Toastify("error", error || "Error uploading fiscal calendar");

      // Log error
      const errorDesc = {
        message: `Error uploading fiscal calendar for brand ID ${brandId}: ${error.message}`,
        status: error.response?.status || 500,
        method: "POST",
        url: `/fiscal/v1/fiscal_calendar/brand/${brandId}`,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), false);
    }
  };

  const addScraping = async (brandId: number) => {
    try {
      const safeScrapData = Array.isArray(scrapData) ? scrapData : [];
      if (safeScrapData.length > 0) {
        const scrapingData = safeScrapData.map(
          ({
            id,
            ...scrapItemWithoutId
          }: {
            id: number;
            [key: string]: any;
          }) => ({
            ...scrapItemWithoutId,
            brand_id: brandId,
            user: user_id,
            competitor: "",
          })
        );
        const res = await apiClient.post(
          "/affcrm/v1/scraping/add",
          { scrapingURLs: scrapingData },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        setScrapData([]);

        // Log success
        const successDesc = {
          message: `Scraping URLs added successfully for brand ID ${brandId}`,
          status: res.status,
          method: res.config.method,
          url: res.config.url,
        };
        await actionLog?.actionLogTrack(JSON.stringify(successDesc), false);
      }
    } catch (error: any) {
      console.error(error);
      Toastify("error", error || "Failed to add scraping URLs");

      // Log error
      const errorDesc = {
        message: `Error adding scraping URLs for brand ID ${brandId}: ${error.message}`,
        status: error.response?.status || 500,
        method: "POST",
        url: "/affcrm/v1/scraping/add",
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const addGA4Values = async (brandId: number) => {
    try {
      if (selectedOptions.includes("Affiliate") && isGA4Active) {
        // Filter valid GA4 configurations (rows with property ID)
        const validGA4Configs = ga4Rows.filter((row: any) => {
          return row.propertyId && row.propertyId.toString().trim() !== "";
        });

        // Add each valid GA4 configuration
        for (const row of validGA4Configs) {
          const cleanRow: any = {
            account_id: Number(row.accountId),
            account_name: row.accountName,
            property_id: Number(row.propertyId),
            property_name: row.propertyName,
            source: row.source,
            medium: row.medValues,
          };

          // Clean up each row
          Object.keys(cleanRow).forEach((key) => {
            if (!cleanRow[key] && cleanRow[key] !== 0) delete cleanRow[key];
          });
          if (cleanRow["medium"]?.length === 0) delete cleanRow["medium"];

          if (Object.keys(cleanRow).length > 0) {
            const res = await apiClient.post(
              `/brand/v1/admin/add/ga4Values/${brandId}`,
              cleanRow,
              {
                headers: { "Content-Type": "application/json" },
              }
            );

            // Log success for each row
            const successDesc = {
              message: `GA4 values added successfully for brand ID ${brandId}`,
              status: res.status,
              method: res.config.method,
              url: res.config.url,
            };
            await actionLog?.actionLogTrack(JSON.stringify(successDesc), false);
          }
        }

        // Reset GA4 form fields
        setGa4Rows([
          {
            accountId: "",
            accountName: "",
            propertyId: "",
            propertyName: "",
            source: "",
            medValues: [],
            singleMedValue: "",
          },
        ]);
      }
    } catch (error: any) {
      console.log(error, "ga4 ga4");
      Toastify(
        "error",
        error?.response?.data?.message || "Error adding GA4 values"
      );

      // Log error
      const errorDesc = {
        message: `Error adding GA4 values for brand ID ${brandId}: ${error.message}`,
        status: error.response?.status || 500,
        method: "POST",
        url: `/brand/v1/admin/add/ga4Values/${brandId}`,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), false);
    }
  };

  const addPaidAccounts = async (brandId: number) => {
    try {
      if (performanceData.length > 0) {
        const updatedPerformanceData = performanceData.map((item: any) => ({
          ...item,
          brand_id: brandId,
          is_mbm: item.is_mbm === "true",
          is_emq: item.is_emq === "true",
          source_of_truth:
            item.source_of_truth === "Yes" || item.source_of_truth === true,
          attribution_window: item.attribution_window.includes("/")
            ? item.attribution_window
            : "Default",
        }));

        const res = await apiClient.post(
          "/brand/v1/paid_ad_accounts/add",
          updatedPerformanceData
        );

        dispatch(setPerformanceData([]));

        // Log success
        const successDesc = {
          message: `Paid accounts added successfully for brand ID ${brandId}`,
          status: res.status,
          method: res.config.method,
          url: res.config.url,
        };
        await actionLog?.actionLogTrack(JSON.stringify(successDesc), false);
      }
    } catch (error: any) {
      dispatch(setPerformanceData([]));

      console.error(error);
      Toastify("error", error || "Error adding paid ad accounts");

      // Log error
      const errorDesc = {
        message: `Error adding paid ad accounts for brand ID ${brandId}: ${error.message}`,
        status: error.response?.status || 500,
        method: "POST",
        url: "/brand/v1/paid_ad_accounts/add",
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), false);
    }
  };

  const addBrandData = async (brandId: number) => {
    try {
      if (addRevenueSourceOfTruth || addConversionSourceOfTruth) {
        const res = await apiClient.post(`/brand/v1/brand-data`, {
          revenue_source_of_truth: addRevenueSourceOfTruth,
          conversion_source_of_truth: addConversionSourceOfTruth,
          brand_id: brandId,
        });

        setAddRevenueSourceOfTruth("");
        setAddConversionSourceOfTruth("");
        // Covered by the main brand create log — no separate brand-data success entry.
      }
    } catch (error: any) {
      console.error(error);
      Toastify("error", error || "Error Adding Brand Data");

      const errorDesc = {
        message: `Failed to add brand data for brand ID ${brandId}: ${error.message}`,
        status: error.response?.status || 500,
        method: "POST",
        url: `/brand/v1/brand-data`,
        category: 'brand',
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const resetForm = () => {
    //reset brand add fields
    setAddBrandName("");
    setAddOrg("");
    setAddBrandVertical([]);
    setAddBrandDomain("");
    setAddAffiliateAlias("");
    setAddAlias("");
    setAddBrandType("");
    setAddBrandDate(new Date());
    setAddTimeZone("Eastern Standard Time");
    setAddWeekStartDay("Sunday");
    setSelectedDatePreset("Last week (starts Sunday)");
    setAddDateJoined(new Date());
    setAddBrandImageRaw(null);
    setAddBrandImage(null);

    // Reset GA4 rows
    setGa4Rows([
      {
        accountId: "",
        accountName: "",
        propertyId: "",
        propertyName: "",
        source: "",
        medValues: [],
        singleMedValue: "",
      },
    ]);

    // Reset is_performance_creator and hubspot_company_id
    setAddIsPerformanceCreator(false);
    setAddHubspotCompanyId("");

    // Reset Grapevine fields.
    setAddGvSlug("");
    setAddGvVertical("");
    setAddGvContactName("");
    setAddGvContactEmail("");
    setAddGvHomepageUrl("");
    setAddGvTier("");
  };

  // delete brand
  const deleteBrand = async (id: any, brandName: any) => {
    try {
      setHideDeleteBrandProgressDialog(false);

      // Delete the brand
      const res = await apiClient.delete(`/brand/v1/admin/brand/${id}`);

      // Delete the scraping data associated with the brand
      const scrapDeleteResponse = await apiClient.delete(
        `/affcrm/v1/scraping/deleteForBrand/${id}`
      );

      // Log the action
      const deletedBrand =
        brands.find((b: any) => String(b.id) === String(id)) || {};
      const deletedBrandOrgName = allOrg?.find(
        (o: any) => String(o.value) === String(deletedBrand.organization_id)
      )?.label;
      const desc = {
        message: buildSummary('Brand', brandName, 'deleted'),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: 'brand',
        action: 'delete_brand',
        brandId: id,
        brandName: brandName,
        details: [
          {
            label: 'Brand ID',
            value: formatIdWithName(id, brandName),
          },
          ...buildDetails(
            normalizeBrandForLog({
              ...deletedBrand,
              organization:
                deletedBrandOrgName ||
                deletedBrand.organization?.name ||
                (deletedBrand.organization_id
                  ? `ID ${deletedBrand.organization_id}`
                  : undefined),
            }),
            [
              { key: 'organization', label: 'Organization' },
              { key: 'domain', label: 'Domain' },
              { key: 'hubspot_company_id', label: 'HubSpot Company ID' },
              {
                key: 'is_active',
                label: 'Status',
                type: 'boolean',
                boolStyle: 'activeInactive',
              },
            ],
            { skipEmpty: true }
          ),
        ],
      };

      // Log success action
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);

      // Notify success
      Toastify("success", "Brand deleted successfully!");
      setHideDeleteBrandProgressDialog(true);
      setDeleteBrandDialog(false);

      // Refresh the brands list
      getBrands();
    } catch (error: any) {
      console.error(error);

      // Handle error
      if (error.response) {
        Toastify(
          "error",
          error?.response?.data?.message || "Error while deleting Brand!!"
        );

        // Log error action
        const errorDesc = {
          message: `Error deleting brand with name ${brandName}: ${error.message}`,
          status: error.response?.status || 500,
          method: "DELETE",
          url: `/brand/v1/admin/brand/${id}`,
        };
        await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
      } else {
        Toastify("error", "Something went wrong!!");

        // Log error action
        const errorDesc = {
          message: `Error deleting brand with name ${brandName}: ${error.message}`,
          status: 500,
          method: "DELETE",
          url: `/brand/v1/admin/brand/${id}`,
        };
        await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
      }
    }
  };

  const throttleDeleteBrand = useThrottledFn(deleteBrand, 1000);

  // edit brand
  const editBrand = async (brand: any) => {
    const grapevineRequestId = ++editGvRequestRef.current;
    console.log(brand, "brand1");
    setEditBrandData(brand);
    setRemovedGA4Rows([]); // Reset removed rows tracking when loading a new brand
    setEditBrandImage(superAdmin ? brand.image_url : brand.image);
    setEditBrandName(superAdmin ? brand.name : brand.name);
    setEditBrandDate(superAdmin ? brand.launch : brand.created_at);

    const item = allOrg.filter(
      (item: any) => item.value === brand.organization_id
    );

    let newOptions = [...selectedEditOptions];
    if (brand.is_affiliate && !selectedEditOptions.includes("Affiliate")) {
      newOptions.push("Affiliate");
    }
    if (brand.is_paid && !selectedEditOptions.includes("Performance")) {
      newOptions.push("Performance");
    }
    const brandIsProspect = localProspectStatus.hasOwnProperty(brand.id)
      ? localProspectStatus[brand.id]
      : brand.brandData?.is_prospect === 1;
    if (brandIsProspect && !selectedEditOptions.includes("Prospect")) {
      newOptions.push("Prospect");
    }
    if (brand.is_creative && !selectedEditOptions.includes("Creative")) {
      newOptions.push("Creative");
    }
    if (brand.is_acorn && !selectedEditOptions.includes("Acorn")) {
      newOptions.push("Acorn");
    }
    if (!brand.is_affiliate) {
      newOptions = newOptions.filter((opt) => opt !== "Affiliate");
    }
    if (!brand.is_paid) {
      newOptions = newOptions.filter((opt) => opt !== "Performance");
    }
    if (!brandIsProspect) {
      newOptions = newOptions.filter((opt) => opt !== "Prospect");
    }
    if (!brand.is_creative) {
      newOptions = newOptions.filter((opt) => opt !== "Creative");
    }
    if (!brand.is_acorn) {
      newOptions = newOptions.filter((opt) => opt !== "Acorn");
    }
    setSelectedEditOptions(newOptions);

    if (item.length > 0) {
      // alert(item[0].label);
      setEditOrg(superAdmin ? item[0] : null);
    } else {
      alert("Organization not found");
      setEditOrg(null);
    }

    // setEditBrandVertical(brand.vertical.split(","));
    setEditBrandVertical(brand.vertical ? brand.vertical.split(",") : ["-"]);
    setEditBrandType(brand.type);
    setEditBrandDomain(brand.domain);
    setShowEditBrandDialog(true);
    // setEditAlias(brand.affiliate_alias);

    //New fields added after first launch
    setEditTimeZone(superAdmin ? brand.timezone : "");
    setEditDateJoined(superAdmin ? brand.date_joined : "");
    setEditWeekStartDay(superAdmin ? brand.week_start_day : "");
    setSelectedEditDatePreset(superAdmin ? brand.date_preset : "");

    // Brands written before the rename only carry the legacy is_influencer flag.
    const isPerformanceCreator = Boolean(
      brand.is_performance_creator ?? brand.is_influencer
    );
    const recentGrapevine = recentGrapevineRef.current.get(brand.id);
    const initialGrapevine = recentGrapevine
      ? mergeGrapevinePrefill(getGrapevineEditPrefill(brand), recentGrapevine)
      : getGrapevineEditPrefill(brand);
    setEditIsPerformanceCreator(isPerformanceCreator);
    setEditHubspotCompanyId(initialGrapevine.hubspotCompanyId);
    setEditGvSlug(initialGrapevine.slug);
    setEditGvVertical(initialGrapevine.vertical);
    setEditGvContactName(initialGrapevine.contactName);
    setEditGvContactEmail(initialGrapevine.contactEmail);
    setEditGvHomepageUrl(initialGrapevine.homepageUrl);
    setEditGvTier(initialGrapevine.tier);
    setEditGvError("");
    setEditGvLoading(false);

    if (isPerformanceCreator || brand.grapevine_brand_id) {
      setEditGvLoading(true);
      const lookupGrapevine = () =>
        apiClient.get(`/brand/v1/admin/brand/${brand.id}/grapevine`);
      // A brand created moments ago may not exist in Grapevine yet; retry instead of failing.
      const grapevineRequest = recentGrapevine
        ? retryGrapevineLookup(
            lookupGrapevine,
            undefined,
            () => editGvRequestRef.current !== grapevineRequestId
          )
        : lookupGrapevine();
      grapevineRequest
        .then((response) => {
          if (editGvRequestRef.current !== grapevineRequestId) return;
          const liveGrapevine =
            response.data?.response ?? response.data?.data ?? response.data;
          const prefill = mergeGrapevinePrefill(
            getGrapevineEditPrefill(brand, liveGrapevine),
            recentGrapevine
          );
          recentGrapevineRef.current.delete(brand.id);
          setEditHubspotCompanyId(prefill.hubspotCompanyId);
          setEditGvSlug(prefill.slug);
          setEditGvVertical(prefill.vertical);
          setEditGvContactName(prefill.contactName);
          setEditGvContactEmail(prefill.contactEmail);
          setEditGvHomepageUrl(prefill.homepageUrl);
          setEditGvTier(prefill.tier);
          setEditGvLoading(false);
        })
        .catch(() => {
          if (editGvRequestRef.current !== grapevineRequestId) return;
          setEditGvLoading(false);
          setEditGvError(
            recentGrapevine
              ? "Grapevine is still syncing this new brand. Showing the values you entered when creating it."
              : "Unable to load live Grapevine details. Current values were preserved."
          );
        });
    }

    // Keep `scrapData` consistently as an array to avoid `.length` crashes
    // when API returns `undefined` for brands without scraping URLs.
    setScrapData(superAdmin ? (Array.isArray(brand.scrapData) ? brand.scrapData : []) : []);

    setAddFiscalCalendar(
      (brand.fiscalData?.response?.length ?? 0) > 0 ? brand.fiscalData?.response : null
    );

    // Populate edit GA4 rows from brand data
    if (superAdmin && brand.ga4Values) {
      // Handle both single GA4 object and array of GA4 objects
      const ga4Data = Array.isArray(brand.ga4Values)
        ? brand.ga4Values
        : [brand.ga4Values];

      const loadedRows = ga4Data.map((ga4: any) => {
        const items = ga4?.medium?.split(",") || [];
        return {
          accountId: ga4?.account_id || "",
          accountName: ga4?.account_name || "",
          propertyId: ga4?.property_id || "",
          propertyName: ga4?.property_name || "",
          source: ga4?.source || "",
          medValues: items.filter((item: any) => item.trim() !== ""),
          singleMedValue: "",
          isExisting: true, // Mark as existing since loaded from database
          ga4Id: ga4?.id || null, // Store the database ID for existing rows
        };
      });

      setEditGa4Rows(loadedRows);
      setInitialEditGA4RowsCount(loadedRows.length);
    } else {
      setEditGa4Rows([
        {
          accountId: "",
          accountName: "",
          propertyId: "",
          propertyName: "",
          source: "",
          medValues: [],
          singleMedValue: "",
          isExisting: false,
          ga4Id: null,
        },
      ]);
      setInitialEditGA4RowsCount(0);
    }

    if (brand.affiliate_alias !== "") {
      setEditAffiliateAlias(superAdmin ? brand.affiliate_alias : "");
      setEditAlias(superAdmin ? brand.paid_alias : "");
    } else {
      setEditAlias(superAdmin ? brand.paid_alias : "");
    }

    // Check if ga4Values is an array and has items, or if it's a single object with data
    const hasGA4Data = Array.isArray(brand.ga4Values)
      ? brand.ga4Values.length > 0
      : brand.ga4Values?.account_id ||
        brand.ga4Values?.account_name ||
        brand.ga4Values?.property_id ||
        brand.ga4Values?.property_name ||
        brand.ga4Values?.source;

    if (hasGA4Data) {
      setIsGA4Active(true);
    } else {
      setIsGA4Active(false);
    }

    // Handle medium values from GA4 data
    let allMediumValues: string[] = [];
    if (Array.isArray(brand.ga4Values)) {
      // Extract medium values from all GA4 entries
      brand.ga4Values.forEach((ga4: any) => {
        if (ga4.medium) {
          const items = ga4.medium.split(",");
          allMediumValues = [...allMediumValues, ...items];
        }
      });
    } else if (brand.ga4Values?.medium) {
      // Handle single GA4 object
      const items = brand.ga4Values.medium.split(",");
      allMediumValues = items;
    }

    if (brand.brandData) {
      setEditRevenueSourceOfTruth(brand.brandData.revenue_source_of_truth);
      setEditConversionSourceOfTruth(
        brand.brandData.conversion_source_of_truth
      );
    }

    setEditSOWLink(brand.sow_link);
    setEditMSALink(brand.msa_link);
    setEditContractTerms(brand.contract_terms);
    setEditDirectorID(brand.director_id);
    setEditManagerID(brand.manager_id);
    setEditAssociateID1(brand.associate_1_id);
    setEditAssociateID2(brand.associate_2_id);
    setEditOpsID(brand.ops_id);
    setEditDateJoined(brand.date_joined);
  };

  console.log(selectedEditOptions, "check oopy");

  const changeDetect = () => {
    const brand = brandsBackup.find((b: any) => b.id == editBrandData.id);
    const changes = [];
    if (editBrandData) {
      if (brand.domain !== editBrandDomain) {
        changes.push(`${brand.domain} => ${editBrandDomain} (domain)`);
      }
      if (brand.affiliate_alias !== editAlias) {
        changes.push(`${brand.affiliate_alias} => ${editAlias} (alias)`);
      }
      if (brand.name !== editBrandName) {
        changes.push(`${brand.name} => ${editBrandName} (brand)`);
      }
      if (brand.launch !== editBrandDate) {
        changes.push(`${brand.launch} => ${editBrandDate} (launch)`);
      }

      if (brand.organization_id !== editOrg) {
        changes.push(`${brand.organization_id} -> ${editOrg}`);
      }

      if (brand.timezone !== editTimeZone) {
        changes.push(`${brand.timezone} -> ${editTimeZone}`);
      }
      if (brand.date_preset !== editBrandDatePreset) {
        changes.push(`${brand.date_preset} -> ${editBrandDatePreset}`);
      }

      if (brand.week_start_day !== editWeekStartDay) {
        changes.push(`${brand.week_start_day} -> ${editWeekStartDay}`);
      }

      if (brand.date_joined !== editDateJoined) {
        changes.push(`${brand.date_joined} -> ${editDateJoined}`);
      }

      if (brand.vertical !== editBrandVertical) {
        changes.push(`${brand.vertical} => ${editBrandVertical} (vertical)`);
      }
      if (editBrandImageRaw !== null) {
        changes.push(`image -> ${editBrandImageRaw}`);
      }
      if (brand.type !== editBrandType) {
        changes.push(
          `${brand.type} => ${editBrandType} ${editBrandType} (type)`
        );
      }

      if (brand.sow_link !== editSOWLink) {
        changes.push(`${brand.sow_link} => ${editSOWLink} (sow)`);
      }
      if (brand.msa_link !== editMSALink) {
        changes.push(`${brand.msa_link} => ${editMSALink} (msa)`);
      }
      if (brand.contract_terms !== editContractTerms) {
        changes.push(
          `${brand.contract_terms} => ${editContractTerms} (contractTerms)`
        );
      }
      if (brand.director_id !== editDirectorID) {
        changes.push(`${brand.director_id} => ${editDirectorID} (directorID)`);
      }
      if (brand.manager_id !== editManagerID) {
        changes.push(`${brand.manager_id} => ${editManagerID} (managerID)`);
      }
      if (brand.associate_1_id !== editAssociateID1) {
        changes.push(
          `${brand.associate_1_id} => ${editAssociateID1} (associateID1)`
        );
      }
      if (brand.associate_2_id !== editAssociateID2) {
        changes.push(
          `${brand.associate_2_id} => ${editAssociateID2} (associateID2)`
        );
      }
      if (brand.ops_id !== editOpsID) {
        changes.push(`${brand.ops_id} => ${editOpsID} (opsId)`);
      }
      if (brand.date_joined !== editDateJoined) {
        changes.push(`${brand.date_joined} => ${editDateJoined} (dateJoined)`);
      }
    }

    return changes;
  };

  const handleChangeEditScrapItem = (e: any) => {
    const new_obj = { ...editScrapItem };
    if (e.target.name == "platform") {
      new_obj["platform"] = e.target.value;
      setEditScrapItem(new_obj);
    } else {
      new_obj[e.target.id] = e.target.value;
      setEditScrapItem(new_obj);
    }
    handleValidationScrapUrl();
  };

  //validation of scrap url based on domain name
  const handleValidationScrapUrl = () => {
    if (changeScrapItem.platform !== "" || editScrapItem?.platform !== "") {
      const arr = scrapPlatforms.filter(
        (item: any) =>
          item.name === (changeScrapItem.platform || editScrapItem?.platform)
      );

      setScrapDomain(arr[0]?.domain);
    } else {
      alert("no data");
    }
  };

  // Function to validate URL based on platform
  function validateUrl(url: any, platform: any) {
    const req_domain = scrapPlatforms.find(
      (item: any) => item.name === platform
    );

    if (!req_domain) {
      return false; // or handle the error as needed
    }

    const regexPattern = `https?:\\/\\/(www\\.)?${req_domain.domain}\\/[^\\/]+`;
    const regex = new RegExp(regexPattern);

    return regex.test(url);
  }

  //handle change Scrap Data
  const handleChangeScrapData = (e: any) => {
    const new_obj = { ...changeScrapItem };
    if (e.target.name == "platform") {
      new_obj["platform"] = e.target.value;
      new_obj["id"] = scrapData.length;
      setChangeScrapItem(new_obj);
    } else {
      new_obj["id"] = scrapData.length;
      new_obj[e.target.id] = e.target.value;
      setChangeScrapItem(new_obj);
    }
    handleValidationScrapUrl();
  };

  const handleAddItemToScrapData = (index: any) => {
    setIsAddIconClicked(true);
    if (changeScrapItem.platform === "" || changeScrapItem.url === "") {
      Toastify("error", "platform and url are required");
    } else {
      if (validateUrl(changeScrapItem.url, changeScrapItem.platform)) {
        const new_arr = [...scrapData];
        const desired_obj = new_arr[index];
        desired_obj["platform"] = changeScrapItem.platform;
        desired_obj["url"] = changeScrapItem.url;

        new_arr.splice(index, 1, desired_obj);
        setScrapData(new_arr);
        setChangeScrapItem({
          id: null,
          platform: "",
          url: "",
        });
      } else {
        setIsCorrect(false);
      }
    }
  };

  const handleDeleteScrapItem = async (item: any, index: any) => {
    const new_arr = [...scrapData];
    new_arr.splice(index, 1);
    for (let i = index; i < new_arr.length; i++) {
      if (new_arr[i]["id"] !== i + 1) {
        new_arr[i]["id"] = i + 1;
      } else {
        continue;
      }
    }

    try {
      const res = await apiClient.delete(`/affcrm/v1/scraping/${item.index}`);
      setScrapData(new_arr);

      // Log success
      const successDesc = {
        message: `Successfully deleted scrap item with index ${item.index}`,
        status: res.status,
        method: res.config.method,
        url: res.config.url,
      };
      await actionLog?.actionLogTrack(JSON.stringify(successDesc), true);
    } catch (error: any) {
      console.error(error);
      Toastify(
        "error",
        error?.response?.data?.message || "Error deleting scrap item"
      );

      // Log error
      const errorDesc = {
        message: `Error deleting scrap item with index ${item.index}: ${error.message}`,
        status: error.response?.status || 500,
        method: "DELETE",
        url: `/affcrm/v1/scraping/${item.index}`,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), false);
    }
  };
  const handleSaveEditScrapItem = (index: any) => {
    if (validateUrl(editScrapItem.url, editScrapItem.platform)) {
      const new_obj = { ...editScrapItem };
      const new_arr = [...scrapData];
      new_arr.splice(index, 1, new_obj);
      setScrapData(new_arr);
      setEditScrapItem(null);
    }
  };

  useEffect(() => {
    handleValidationScrapUrl();

    if (changeScrapItem.id !== null) {
      if (validateUrl(changeScrapItem?.url, changeScrapItem?.platform)) {
        setIsCorrect(true);
      } else {
        setIsCorrect(false);
      }
    }

    if (editScrapItem) {
      if (validateUrl(editScrapItem?.url, editScrapItem?.platform)) {
        setIsCorrect(true);
      } else {
        setIsCorrect(false);
      }
    }
  }, [changeScrapItem, editScrapItem, setChangeScrapItem, setEditScrapItem]);

  //handle AddScrap Data
  const handleAddScrapData = () => {
    if (singleScrapData.platform && singleScrapData.url) {
      const new_arr = [...scrapData, singleScrapData];
      setScrapData(new_arr);
      setIsScrapEmpty(false);
      setSingleScrapData({ platform: "", url: "" });
    } else {
      setIsScrapEmpty(true);
    }
  };

  const resetEditFormFields = () => {
    editGvRequestRef.current += 1;
    //rest brand form fields
    setEditBrandName("");
    setEditOrg("");
    setEditBrandVertical([]);
    setEditBrandDomain("");
    setEditAffiliateAlias("");
    setEditAlias("");
    setEditBrandImageRaw(null);
    setEditBrandType("");
    setEditBrandImage("");
    setEditBrandDate("");
    setEditDateJoined(null);
    setEditBrandDate("");
    setEditTimeZone("Eastern Standard Time");
    setEditWeekStartDay("Sunday");
    setEditBrandDatePreset("Last week (starts Sunday)");
    setSelectedEditDatePreset("Last week (starts Sunday)");

    //reset ga4 Fields
    setEditGa4Rows([
      {
        accountId: "",
        accountName: "",
        propertyId: "",
        propertyName: "",
        source: "",
        medValues: [],
        singleMedValue: "",
        isExisting: false,
        ga4Id: null,
      },
    ]);
    setInitialEditGA4RowsCount(0);
    setRemovedGA4Rows([]); // Clear removed rows tracking

    //reset scraping data
    setScrapData([]);

    //reset Fiscal Data
    setEditFiscalCalendar(null);
    setEditFiscalFile(null);
    setDeleteFiscalClicked(false);

    //reset extra fields
    setEditSOWLink("");
    setEditMSALink("");
    setEditContractTerms("");
    setEditDirectorID("");
    setEditManagerID("");
    setEditAssociateID1("");
    setEditAssociateID2("");
    setEditOpsID("");
    setEditBrandData("");
    setEditIsPerformanceCreator(false);
    setEditHubspotCompanyId("");
    setEditGvSlug("");
    setEditGvVertical("");
    setEditGvContactName("");
    setEditGvContactEmail("");
    setEditGvHomepageUrl("");
    setEditGvTier("");
    setEditGvLoading(false);
    setEditGvError("");
  };

  console.log(editBrandVertical, "editBrandVertical");
  const updateBrand = async () => {
    // Validation checks (keeping existing validation logic)
    if (
      !isValidDomain(editBrandDomain) ||
      editBrandDomain.split(":")[0] === "http" ||
      editBrandDomain.split(":")[0] === "https"
    ) {
      Toastify(
        "warning",
        "Domain of brand should be of format eg: domain_name.com"
      );
    } else if (!editAlias || editAlias.trim() === "" || !isEmail(editAlias)) {
      Toastify("warning", "Alias must be an email!!");
    } else if (
      typeof editBrandName === "undefined" ||
      editBrandName === null ||
      editBrandName === ""
    ) {
      Toastify("warning", "Name of brand is required!");
    } else if (
      typeof editBrandDate === "undefined" ||
      editBrandDate === null ||
      editBrandDate === ""
    ) {
      Toastify("warning", "Date of program launch is required!");
    } else if (
      typeof editBrandVertical === "undefined" ||
      editBrandVertical === null ||
      editBrandVertical.length === 0
    ) {
      Toastify("warning", "Vertical of brand is required!");
    } else if (
      superAdmin &&
      (typeof editBrandType === "undefined" ||
        editBrandType === null ||
        editBrandType === "")
    ) {
      Toastify("warning", "Program type of brand is required!");
    } else if (
      typeof editOrg === "undefined" ||
      editOrg === null ||
      editOrg === ""
    ) {
      Toastify("warning", "Organization of Brand is required");
    } else if (
      selectedEditOptions.includes("Affiliate") &&
      isGA4Active &&
      editGa4Rows.some(
        (row: any) => !row.propertyId || row.propertyId.toString().trim() === ""
      )
    ) {
      Toastify("warning", "GA4 Property ID is required");
    } else {
      const grapevineError = getGrapevineValidationError(
        editIsPerformanceCreator,
        editHubspotCompanyId,
        {
          slug: editGvSlug,
          vertical: editGvVertical,
          contactName: editGvContactName,
          contactEmail: editGvContactEmail,
          homepageUrl: editGvHomepageUrl,
          tier: editGvTier,
        },
        isEmail
      );
      if (grapevineError) {
        Toastify("warning", grapevineError);
        return;
      }
      const slugError = grapevineSlugSubmitError(
        editIsPerformanceCreator,
        editGvSlug,
        editGvSlugStatus,
      );
      if (slugError) {
        Toastify("warning", slugError);
        return;
      }
      setHideEditBrandProgressDialog(false);
      try {
        // Prepare base FormData
        const formData = new FormData();
        // Basic brand details
        formData.append("name", editBrandName);
        formData.append("launch", moment(editBrandDate).toISOString());
        formData.append("vertical", editBrandVertical);
        formData.append("type", editBrandType);
        formData.append("domain", editBrandDomain);
        formData.append("timezone", editTimeZone);
        formData.append("week_start_day", editWeekStartDay);
        formData.append("date_preset", selectedEditDatePreset);
        formData.append("organization_id", editOrg.value);
        formData.append("paid_alias", editAlias);

        // Optional fields
        if (editBrandImageRaw) formData.append("image_url", editBrandImageRaw);
        if (editAffiliateAlias && editAffiliateAlias !== "")
          formData.append("affiliate_alias", editAffiliateAlias);
        if (editSOWLink) formData.append("sowLink", editSOWLink);
        if (editMSALink) formData.append("msaLink", editMSALink);
        if (editContractTerms)
          formData.append("contractTerms", editContractTerms);
        if (editDirectorID) formData.append("directorId", editDirectorID);
        if (editManagerID) formData.append("managerId", editManagerID);
        if (editAssociateID1) formData.append("associateId1", editAssociateID1);
        if (editAssociateID2) formData.append("associateId2", editAssociateID2);
        if (editOpsID) formData.append("opsId", editOpsID);
        if (editDateJoined)
          formData.append("date_joined", moment(editDateJoined).toISOString());

        formData.append(
          "is_affiliate",
          selectedEditOptions.includes("Affiliate") ? "true" : "false"
        );

        formData.append(
          "is_paid",
          selectedEditOptions.includes("Performance") ? "true" : "false"
        );

        formData.append(
          "is_creative",
          selectedEditOptions.includes("Creative") ? "true" : "false"
        );

        formData.append(
          "is_acorn",
          selectedEditOptions.includes("Acorn") ? "true" : "false"
        );
        appendGrapevineFormData(formData, editIsPerformanceCreator, editHubspotCompanyId, {
          slug: editGvSlug,
          vertical: editGvVertical,
          contactName: editGvContactName,
          contactEmail: editGvContactEmail,
          homepageUrl: editGvHomepageUrl,
          tier: editGvTier,
        });

        // Prepare API calls
        const apiCalls = [];

        // 1. Base brand update API
        apiCalls.push(
          apiClient
            .put(`/brand/v1/admin/brand/update/${editBrandData.id}`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
            .then(async (response) => {
              // const message = getPayloadDiffMessage(oldPayload, newPayload);
              // console.log(message,'message')
              const user = JSON.parse(localStorage.getItem("user") || "{}");
              const oldBrand =
                editBrandData ||
                brands.find((b: any) => String(b.id) === String(editBrandData?.id)) ||
                {};
              const oldBrandOrgName =
                allOrg?.find((o: any) => String(o.value) === String(oldBrand.organization_id))?.label ||
                oldBrand.organization_id;
              const newBrandOrgName = editOrg?.label || editOrg?.value;
              const newBrandProducts = ["Affiliate", "Performance", "Creative", "Acorn"].filter(
                (p) => selectedEditOptions.includes(p)
              );
              const brandChanges = diffFields(
                normalizeBrandForLog({
                  ...oldBrand,
                  organization: oldBrandOrgName,
                }),
                normalizeBrandForLog({
                  name: editBrandName,
                  organization: newBrandOrgName,
                  domain: editBrandDomain,
                  type: editBrandType,
                  timezone: editTimeZone,
                  vertical: editBrandVertical,
                  week_start_day: editWeekStartDay,
                  date_preset: selectedEditDatePreset,
                  launch: editBrandDate,
                  date_joined: editDateJoined,
                  paid_alias: editAlias,
                  affiliate_alias: editAffiliateAlias,
                  hubspot_company_id: editHubspotCompanyId,
                  is_performance_creator: editIsPerformanceCreator,
                  sow_link: editSOWLink,
                  msa_link: editMSALink,
                  contract_terms: editContractTerms,
                  director_id: editDirectorID,
                  manager_id: editManagerID,
                  associate_1_id: editAssociateID1,
                  associate_2_id: editAssociateID2,
                  ops_id: editOpsID,
                  products: newBrandProducts,
                  revenue_source_of_truth: editRevenueSourceOfTruth,
                  conversion_source_of_truth: editConversionSourceOfTruth,
                }),
                BRAND_FIELD_SPECS
              );
              if (editBrandImageRaw) {
                brandChanges.push({ label: "Logo", from: "Previous logo", to: "New logo uploaded" });
              }
              const desc = {
                message: buildSummary('Brand', editBrandName, 'updated', brandChanges.length),
                status: response.status,
                method: response.config.method,
                url: response.config.url,
                category: 'brand',
                action: 'update_brand',
                brand_id: editBrandData?.id,
                brand_name: editBrandName,
                user_id: user?.id,
                user_name: user?.name,
                changes: brandChanges,
              };
              await actionLog?.actionLogTrack(JSON.stringify(desc), false);
            })
            .catch(async (error) => {
              const user = JSON.parse(localStorage.getItem("user") || "{}");
              const desc = {
                message: `Failed to update brand (Brand ID: ${editBrandData?.id}, Brand Name: "${editBrandName}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
                status: error.response?.status,
                method: error.config.method,
                url: error.config.url,
                brand_id: editBrandData?.id,
                brand_name: editBrandName,
                user_id: user?.id,
                user_name: user?.name,
              };
              await actionLog?.actionLogTrack(JSON.stringify(desc), true);
            })
        );

        // 2. Fiscal calendar update API
        console.log(editFiscalFile, "editFiscalFile");
        if (editFiscalFile && editFiscalFile !== "") {
          const fiscalFormData = new FormData();
          fiscalFormData.append("fiscal_calendar", editFiscalFile);
          apiCalls.push(
            apiClient
              .put(
                `/fiscal/v1/fiscal_calendar/brand/${editBrandData.id}`,
                fiscalFormData,
                {
                  headers: { "Content-Type": "multipart/form-data" },
                }
              )
              .then(async (response) => {
                const desc = {
                  message: `Updated fiscal calendar for brand (ID: ${editBrandData?.id}, Name: "${editBrandData?.name}")!`,
                  status: response.status,
                  method: response.config.method,
                  url: response.config.url,
                };
                await actionLog?.actionLogTrack(JSON.stringify(desc), false);
              })
              .catch(async (error) => {
                const desc = {
                  message: `Failed to update fiscal calendar for brand (ID: ${editBrandData?.id}, Name: "${editBrandData?.name}")`,
                  status: error.response?.status,
                  method: error.config.method,
                  url: error.config.url,
                };
                await actionLog?.actionLogTrack(JSON.stringify(desc), true);
              })
          );
        }

        // // 3. Scraping URLs update API
        // if (scrapData?.length > 0) {
        //   const scrapingData = scrapData.map((scrapItem: any) => ({
        //     ...scrapItem,
        //     brand_id: editBrandData.id,
        //     user: user_id,
        //     brand: editBrandName,
        //     competitor: "",
        //   }));
        //   apiCalls.push(
        //     apiClient
        //       .put(
        //         `/affcrm/v1/scraping/update`,
        //         { scrapingURLs: scrapingData },
        //         {
        //           headers: { "Content-Type": "application/json" },
        //         }
        //       )
        //       .then(async (response) => {
        //         const desc = {
        //           message: `Updated scraping URLs for brand ${editBrandName}!`,
        //           status: response.status,
        //           method: response.config.method,
        //           url: response.config.url,
        //         };
        //         await actionLog?.actionLogTrack(JSON.stringify(desc), false);
        //       })
        //       .catch(async (error) => {
        //         const desc = {
        //           message: `Failed to update scraping URLs for brand ${editBrandName}`,
        //           status: error.response?.status,
        //           method: error.config.method,
        //           url: error.config.url,
        //         };
        //         await actionLog?.actionLogTrack(JSON.stringify(desc), true);
        //       })
        //   );
        // }

        // 4. GA4 Values update/add API - smart handling of existing vs new rows
        const validEditGA4Configs = editGa4Rows.filter((row: any) => {
          return row.propertyId && row.propertyId.toString().trim() !== "";
        });

        // Separate existing rows from new rows
        const existingRows = validEditGA4Configs.filter(
          (row: any) => row.isExisting
        );
        const newRows = validEditGA4Configs.filter(
          (row: any) => !row.isExisting
        );

        // Update existing GA4 rows (if any exist)
        if (existingRows.length > 0) {
          existingRows.forEach((existingRow: any) => {
            const ga4Values: any = {
              account_id: Number(existingRow.accountId),
              account_name: existingRow.accountName,
              property_id: Number(existingRow.propertyId),
              property_name: existingRow.propertyName,
              source: existingRow.source,
              medium:
                existingRow.medValues.length > 0 ? existingRow.medValues : [],
            };

            // Clean up GA4 values
            Object.keys(ga4Values).forEach((key) => {
              if (!ga4Values[key] && ga4Values[key] !== 0)
                delete ga4Values[key];
            });
            if (ga4Values["medium"]?.length === 0) delete ga4Values["medium"];

            if (Object.keys(ga4Values).length > 0) {
              apiCalls.push(
                apiClient
                  .put(
                    `/brand/v1/admin/update/ga4Values/${editBrandData.id}`,
                    ga4Values,
                    {
                      headers: { "Content-Type": "application/json" },
                    }
                  )
                  .then(async (response) => {
                    const desc = {
                      message: `Updated existing GA4 values for brand ${editBrandName}!`,
                      status: response.status,
                      method: response.config.method,
                      url: response.config.url,
                    };
                    await actionLog?.actionLogTrack(JSON.stringify(desc), false);
                  })
                  .catch(async (error) => {
                    const desc = {
                      message: `Failed to update existing GA4 values for brand ${editBrandName}`,
                      status: error.response?.status,
                      method: error.config.method,
                      url: error.config.url,
                    };
                    await actionLog?.actionLogTrack(JSON.stringify(desc), true);
                  })
              );
            }
          });
        }

        // Delete removed GA4 rows
        if (removedGA4Rows.length > 0) {
          removedGA4Rows.forEach((removedRow: any) => {
            apiCalls.push(
              apiClient
                .delete(`/brand/v1/admin/ga4Values/${removedRow.ga4Id}`)
                .then(async (response) => {
                  const desc = {
                    message: `Deleted GA4 row with ID ${removedRow.ga4Id} for brand ${editBrandName}!`,
                    status: response.status,
                    method: response.config.method,
                    url: response.config.url,
                  };
                  await actionLog?.actionLogTrack(JSON.stringify(desc), false);
                })
                .catch(async (error) => {
                  const desc = {
                    message: `Failed to delete GA4 row with ID ${removedRow.ga4Id} for brand ${editBrandName}`,
                    status: error.response?.status,
                    method: error.config.method,
                    url: error.config.url,
                  };
                  await actionLog?.actionLogTrack(JSON.stringify(desc), true);
                })
            );
          });
        }

        // Add only truly NEW GA4 rows
        if (newRows.length > 0) {
          newRows.forEach((row: any) => {
            const cleanRow: any = {
              account_id: Number(row.accountId),
              account_name: row.accountName,
              property_id: Number(row.propertyId),
              property_name: row.propertyName,
              source: row.source,
              medium: row.medValues,
            };

            // Clean up each new row
            Object.keys(cleanRow).forEach((key) => {
              if (!cleanRow[key] && cleanRow[key] !== 0) delete cleanRow[key];
            });
            if (cleanRow["medium"]?.length === 0) delete cleanRow["medium"];

            if (Object.keys(cleanRow).length > 0) {
              apiCalls.push(
                apiClient
                  .post(
                    `/brand/v1/admin/add/ga4Values/${editBrandData.id}`,
                    cleanRow,
                    {
                      headers: { "Content-Type": "application/json" },
                    }
                  )
                  .then(async (response) => {
                    const desc = {
                      message: `Added new GA4 row for brand ${editBrandName}!`,
                      status: response.status,
                      method: response.config.method,
                      url: response.config.url,
                    };
                    await actionLog?.actionLogTrack(JSON.stringify(desc), false);
                  })
                  .catch(async (error) => {
                    const desc = {
                      message: `Failed to add new GA4 row for brand ${editBrandName}`,
                      status: error.response?.status,
                      method: error.config?.method,
                      url: error.config?.url,
                    };
                    await actionLog?.actionLogTrack(JSON.stringify(desc), true);
                  })
              );
            }
          });
        }

        // 5. Fiscal Deletion API ( On Update Clicked )
        if (deleteFiscalClicked) {
          apiCalls.push(
            apiClient
              .delete(`/fiscal/v1/fiscal_calendar/brand/${editBrandData.id}`)
              .then(async (response) => {
                const desc = {
                  message: `Deleted fiscal calendar for brand ${editBrandName}!`,
                  status: response.status,
                  method: response.config.method,
                  url: response.config.url,
                };
                await actionLog?.actionLogTrack(JSON.stringify(desc), false);
              })
              .catch(async (error) => {
                const desc = {
                  message: `Failed to delete fiscal calendar for brand ${editBrandName}`,
                  status: error.response?.status,
                  method: error.config.method,
                  url: error.config.url,
                };
                await actionLog?.actionLogTrack(JSON.stringify(desc), true);
              })
          );
        }

        //6. Brand Data Update API
        // Source-of-truth fields are included in the main brand update log above —
        // do not emit a second "Updated brand data" activity entry.
        if (editRevenueSourceOfTruth || editConversionSourceOfTruth) {
          apiCalls.push(
            apiClient
              .put(`/brand/v1/brand-data/${editBrandData.id}`, {
                revenue_source_of_truth: editRevenueSourceOfTruth,
                conversion_source_of_truth: editConversionSourceOfTruth,
              })
              .catch(async (error) => {
                const desc = {
                  message: `Failed to update brand data for ${editBrandName}`,
                  status: error.response?.status,
                  method: error.config?.method,
                  url: error.config?.url,
                  category: 'brand',
                  brand_name: editBrandName,
                };
                await actionLog?.actionLogTrack(JSON.stringify(desc), true);
              })
          );
        }

        // Check if the prospect status has changed before calling the toggle function
        const currentBrandData = brandData.find(
          (item: any) => item.brand_id === editBrandData.id
        );
        const isCurrentlyProspect = currentBrandData?.is_prospect === 1;
        const willBeProspect = selectedEditOptions.includes("Prospect");

        // Only call the toggle function if there's a change in prospect status
        if (isCurrentlyProspect !== willBeProspect) {
          await handleBrandProspectToggle(editBrandData.id);
        }

        // Execute all API calls in parallel
        const results = await Promise.allSettled(apiCalls);

        // Check for any failures
        const failures = results.filter(
          (result) => result.status === "rejected"
        );

        if (failures.length > 0) {
          failures.forEach((failure) => {
            if (failure.status === "rejected") {
              const error = failure.reason;
              console.error("API Error:", error);
              Toastify(
                "error",
                error.response?.data?.message || "Error updating brand"
              );
            }
          });
          setHideEditBrandProgressDialog(true);
          return;
        }

        // All calls successful
        getBrands();
        fetchBrandData();
        const timeout = setTimeout(() => {
          setHideEditBrandProgressDialog(true);
          setShowEditBrandDialog(false);
          setShowAdditionalFields(false);
          resetEditFormFields();
          Toastify("success", "Brand updated successfully");
        }, 200);
      } catch (error) {
        console.error("Unexpected error:", error);
        Toastify("error", "An unexpected error occurred");
        setHideEditBrandProgressDialog(true);
      }
    }
  };

  // Search result — server-side
  const searchResults = (query: any) => {
    setPage(0);
    setSearchQuery(query);
    searchQueryRef.current = query;
    getBrandsPage(0, rowsPerPage, query);
  };

  const moveScroll = (direction: any) => {
    if (scrollHeader && scrollHeader.current) {
      if (direction) {
        scrollHeader.current.scrollLeft += 150;
      } else {
        scrollHeader.current.scrollLeft -= 150;
      }
    }
  };

  const searchResultsUsers = (query: any) => {
    let userSearchResult = [];

    for (let i = 0; i < allUsers.length; i++) {
      if (allUsers[i]["name"].toLowerCase().includes(query.toLowerCase())) {
        userSearchResult.push(allUsers[i]);
      }
    }

    setUsersSearched(userSearchResult);
  };

  const searchOptimized: any = useCallback(debounce(searchResults, 600), []);
  const searchUserOptimized: any = useCallback(
    debounce(searchResultsUsers, 600),
    [allUsers]
  );
  const admin_data: any = localStorage.getItem("admin_auth")
    ? JSON.parse(localStorage.getItem("admin_auth") as string)
    : null;
  const additionalFieldsRef: any = useRef(null);
  const dialogContentRef: any = useRef(null);

  const handleSwitchClick = () => {
    setShowAdditionalFields(!showAdditionalFields);
  };

  useEffect(() => {
    if (additionalFieldsRef.current && showAdditionalFields) {
      additionalFieldsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, [showAdditionalFields]);

  const getAllUsers = () => {
    const res = apiClient
      .get(`/user/v1/admin/all`)
      .then(async (response) => {
        setAllUsers(response.data.response);
        const desc = {
          message: "Fetched all users successfully.",
          status: response.status,
          method: response.config.method,
          url: response.config.url,
        };
      })
      .catch(async (err) => {
        const desc = {
          message:
            err?.response?.data?.message || "Error while getting All Users",
          status: err?.response?.status,
          method: err.config.method,
          url: err.config.url,
        };
        Toastify(
          "error",
          err?.response?.data?.message ||
            "Error while getting All Users \n Try Again!"
        );
        console.error(err);
      });
  };
  useEffect(() => {
    getAllUsers();
  }, []);

  //performance table functions
  function createData(
    name: string,
    calories: number,
    fat: number,
    carbs: number,
    protein: number,
    price: number
  ) {
    return {
      name,
      calories,
      fat,
      carbs,
      protein,
      price,
      history: [
        {
          date: "2020-01-05",
          customerId: "11091700",
          amount: 3,
        },
        {
          date: "2020-01-02",
          customerId: "Anonymous",
          amount: 1,
        },
      ],
    };
  }

  //Update Organization Active Status Function
  const updateActiveBrandStatus = async () => {
    setAboutToUpdateActiveBrandProgress(true);
    let formData = {
      //INFO: Sending false and true to backend to maintain the consistency for is_active as formData in other function converts all things to string
      is_active: aboutToUpdateActiveBrand.is_active ? "false" : "true",
    };

    try {
      const res = await apiClient.put(
        `/brand/v1/admin/brand/update/${aboutToUpdateActiveBrand.id}`,
        formData
      );

      const desc = {
        // message: res.data.message || "Updated active status successfully",
        message: buildSummary('Brand', aboutToUpdateActiveBrand.name, aboutToUpdateActiveBrand?.is_active ? 'deactivated' : 'activated'),
        category: 'brand',
        brandId: aboutToUpdateActiveBrand.id,
        brandName: aboutToUpdateActiveBrand.name,
        changes: [
          {
            label: 'Status',
            from: humanizeValue(aboutToUpdateActiveBrand?.is_active, { type: 'boolean', boolStyle: 'activeInactive' }),
            to: humanizeValue(!aboutToUpdateActiveBrand?.is_active, { type: 'boolean', boolStyle: 'activeInactive' }),
          },
        ],
        status: res.status,
        method: "PUT",
        url: `/brand/v1/admin/brand/update/${aboutToUpdateActiveBrand.id}`,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);

      if (res.data.message) {
        setAboutToUpdateActiveBrandProgress(false);
        setAboutToUpdateActiveBrandShow(false);
        setAboutToUpdateActiveBrand(null);
        getBrands();
        Toastify("success", res.data.message);
      }
    } catch (error: any) {
      setAboutToUpdateActiveBrandProgress(false);
      setAboutToUpdateActiveBrandShow(false);
      setAboutToUpdateActiveBrand(null);
      getBrands();

      const desc = {
        message:
          error?.response?.data?.message ||
          "Error while updating active status",
        status: error?.response?.status,
        method: "PUT",
        url: `/brand/v1/admin/brand/update/${aboutToUpdateActiveBrand.id}`,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), true);

      Toastify(
        "error",
        error?.response?.data?.message || "Error while updateActiveBrandStatus"
      );
    }
  };

  function Row(props: { row: ReturnType<typeof createData> }) {
    const { row }: { row: any } = props;

    const [open, setOpen] = React.useState<any>(false);

    return (
      <React.Fragment>
        <MuiTableRow
          sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
          onClick={() => setOpen(!open)}
        >
          <MuiTableCell>{row.id}</MuiTableCell>
          <MuiTableCell component="th" scope="row" align="center">
            {row.display_name}
          </MuiTableCell>
          <MuiTableCell align="center">{row.account_name}</MuiTableCell>
          <MuiTableCell align="center">{row.account_id}</MuiTableCell>
          <MuiTableCell align="center">{row.property_name || "N/A"}</MuiTableCell>
          <MuiTableCell align="center">{row.property_id || "N/A"}</MuiTableCell>

          <MuiTableCell align="center">
            {row.attribution_window || "N/A"}
          </MuiTableCell>
          <MuiTableCell align="center">{row.attribution_basis || "N/A"}</MuiTableCell>
          <MuiTableCell align="center">
            {row.selected_conversion_event || "N/A"}
          </MuiTableCell>
          <MuiTableCell align="center">
            {row.selected_conversion_tag_names || "N/A"}
          </MuiTableCell>
          <MuiTableCell align="center">{row.is_mbm || "N/A"}</MuiTableCell>
          <MuiTableCell align="center">
            <div className="flex items-center justify-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleEditPlatform(row);
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleDeletePerfItem(row);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </MuiTableCell>
        </MuiTableRow>
      </React.Fragment>
    );
  }

  const rows = [createData("Frozen yoghurt", 159, 6.0, 24, 4.0, 3.99)];

  //handle Delete Brand
  const handleDeleteBrandDialog = (row: any) => {
    setDeleteBrandDialog(true);
    setBrandRow(row);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      setAddFiscalCalendar(null);
      const file = event.target.files?.[0];
      setIsLoading(true);

      if (!file) {
        const timeout = setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return; // No file selected, do nothing
      }

      // Check for valid Excel file types (.xls and .xlsx)
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!validTypes.includes(file.type)) {
        // setErrorMessage('Please select a valid Excel file (.xls or .xlsx)');
        return;
      }

      setFiscalFile(file);

      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (e: any) => {
        try {
          setExcelData(e.target.result);
        } catch (error) {
          alert("Error reading the file");
        }
      };

      reader.onerror = () => {
        alert("Error reading the file");
      };
    } catch (error: any) {
      Toastify(
        "error",
        error?.response?.data?.message || "Something went Wrong!! \n Try Again!"
      );
    }
  };

  const handleEditFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      setEditFiscalCalendar(null);
      const file = event.target.files?.[0];
      setIsLoading(true);

      if (!file) {
        const timeout = setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return; // No file selected, do nothing
      }

      // Check for valid Excel file types (.xls and .xlsx)
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!validTypes.includes(file.type)) {
        // setErrorMessage('Please select a valid Excel file (.xls or .xlsx)');
        return;
      }

      setEditFiscalFile(file);

      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (e: any) => {
        try {
          setEditExcelData(e.target.result);
        } catch (error) {
          alert("Error reading the file");
        }
      };

      reader.onerror = () => {
        alert("Error reading the file");
      };
    } catch (error: any) {
      Toastify(
        "error",
        error?.response?.data?.message || "Something went Wrong!! \n Try Again!"
      );
    }
  };

  const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#16306D",
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(MuiTableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  useEffect(() => {
    try {
      if (excelData && !addFiscalCalendar) {
        const timeout = setTimeout(() => {
          const workBook = XLSX.read(excelData, { type: "buffer" });
          const workSheetName = workBook?.SheetNames[0];
          const worksheet = workBook?.Sheets[workSheetName];
          const data = XLSX?.utils?.sheet_to_json(worksheet);

          setAddFiscalCalendar(data.slice(0, 10));
          setIsLoading(false);
        }, 2500);
      } else if (excelData && !addFiscalCalendar) {
        setAddFiscalCalendar(null);
      }
    } catch (error: any) {
      Toastify(
        "error",
        error?.response?.data?.message || "Something went Wrong!!"
      );
    }
  }, [excelData, setExcelData]);

  useEffect(() => {
    try {
      if (editExcelData) {
        const timeout = setTimeout(() => {
          const workBook = XLSX.read(editExcelData, { type: "buffer" });
          const workSheetName = workBook?.SheetNames[0];
          const worksheet = workBook?.Sheets[workSheetName];
          const data = XLSX?.utils?.sheet_to_json(worksheet);
          setEditFiscalCalendar(data.slice(0, 10));
          setIsEditLoading(false);
        }, 2500);
      } else if (!editExcelData && isReached) {
        setEditFiscalCalendar([]);
      }
    } catch (error: any) {
      Toastify(
        "error",
        error?.response?.data?.message || "Something went Wrong!!"
      );
    }
  }, [editExcelData, setEditExcelData]);
  useEffect(() => {
    refreshOrganizations();
  }, [refreshOrganizations]);

  const handleResetFiscalClicked = () => {
    setAddFiscalCalendar(null);
    setFiscalFile(null);
  };

  const handleResetEditFiscalClicked = () => {
    setEditFiscalCalendar(null);
    setEditFiscalFile(null);
    setEditExcelData(null);
    setIsReached(false);
  };

  const handleDeleteFiscalClicked = async () => {
    try {
      setDeleteFiscalClicked(true);
      setAddFiscalCalendar(null);
      Toastify("success", "Deleted Successfully!!!");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Error while deleting Fiscal File";
      Toastify("error", errorMessage);
    }
  };

  const handleChangeOrg = (e: any, selectedOption: any) => {
    setAddOrg(selectedOption ? selectedOption.value : "");
  };

  const handleEditChange = (e: any, selectedOption: any) => {
    setEditOrg(selectedOption);
  };
  const fetchBrandData = async () => {
    try {
      const brandData = await apiClient.get(
        `/brand/v1/brand-data/prospects/all`
      );
      setBrandData(brandData.data.response);
    } catch (error: any) {
      console.error("Error fetching brand data:", error);
    } finally {
      // Log the completion of the fetch attempt
    }
  };
  useEffect(() => {
    fetchBrandData();
  }, []);

  const isBrandProspect = (brandId: number) => {
    if (localProspectStatus.hasOwnProperty(brandId)) {
      return localProspectStatus[brandId];
    }
    const brand = brandData.find(
      (item: any) => item.brand_id === brandId
    );
    return brand?.is_prospect === 1;
  };

  const handleBrandProspectToggle = async (brandId: number): Promise<boolean> => {
    const currentStatus = isBrandProspect(brandId);
    const newStatus = !currentStatus;

    setLocalProspectStatus((prev) => ({ ...prev, [brandId]: newStatus }));

    try {
      const updatedBrandData = await apiClient.put(
        `/brand/v1/brand-data/${brandId}`,
        {
          is_prospect: newStatus,
        }
      );

      const prospectBrand = brands.find((b: any) => String(b.id) === String(brandId));
      const prospectBrandName = prospectBrand?.name || `ID ${brandId}`;
      const desc = {
        message: `Brand "${prospectBrandName}" marked as ${newStatus ? 'prospect' : 'not a prospect'}`,
        status: updatedBrandData.status,
        method: "PUT",
        url: `/brand/v1/brand-data/${brandId}`,
        category: 'brand',
        brandId,
        brandName: prospectBrandName,
        changes: [
          {
            label: 'Prospect',
            from: humanizeValue(currentStatus, { type: 'boolean', boolStyle: 'yesNo' }),
            to: humanizeValue(newStatus, { type: 'boolean', boolStyle: 'yesNo' }),
          },
        ],
      };

      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      return newStatus;
    } catch (error: any) {
      console.error("Failed to update prospect status:", error);
      setLocalProspectStatus((prev) => ({ ...prev, [brandId]: currentStatus }));

      const errorDesc = {
        message: "Failed to update prospect status",
        status: error.response ? error.response.status : 500,
        method: "PUT",
        url: `/brand/v1/brand-data/${brandId}`,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);

      Toastify("error", "Failed to update prospect status");
      return currentStatus;
    }
  };

  const confirmProspectChange = async () => {
    if (!aboutToUpdateProspectBrand) return;
    setAboutToUpdateProspectBrandProgress(true);
    try {
      const brandId = aboutToUpdateProspectBrand.id;
      const newStatus = await handleBrandProspectToggle(brandId);
      const value = newStatus ? 1 : 0;

      // Update brands/brandsBackup so DataTable sees new object references and re-renders
      setBrands((prev: any[]) =>
        prev.map((b: any) =>
          b.id === brandId
            ? { ...b, brandData: { ...(b.brandData || {}), is_prospect: value } }
            : b
        )
      );
      setBrandsBackup((prev: any[]) =>
        prev.map((b: any) =>
          b.id === brandId
            ? { ...b, brandData: { ...(b.brandData || {}), is_prospect: value } }
            : b
        )
      );

      fetchBrandData();
      setAboutToUpdateProspectBrandShow(false);
      setAboutToUpdateProspectBrand(null);
      Toastify("success", "Prospect status updated");
    } finally {
      setAboutToUpdateProspectBrandProgress(false);
    }
  };

  return (
    <>
      <div className="brands bg-white">
          {/* isActive Confirmation Dialog */}
        <LiftDialog open={aboutToUpdateActiveBrandShow} onOpenChange={(open) => !aboutToUpdateActiveBrandProgress && setAboutToUpdateActiveBrandShow(open)}>
          <LiftDialogContent className="sm:max-w-md">
            <LiftDialogHeader>
              <LiftDialogTitle>{aboutToUpdateActiveBrand?.is_active ? "Turn Active off?" : "Turn Active on?"}</LiftDialogTitle>
              <LiftDialogDescription>
                You are about to turn Active {aboutToUpdateActiveBrand?.is_active ? "off" : "on"} for the brand{" "}
                <strong>{aboutToUpdateActiveBrand?.name}</strong>. Do you want to continue?
              </LiftDialogDescription>
            </LiftDialogHeader>
            <LiftDialogFooter className="gap-2">
            <Button
              variant="outline"
                onClick={() => setAboutToUpdateActiveBrandShow(false)}
                disabled={aboutToUpdateActiveBrandProgress}
            >
              Cancel
            </Button>
            <Button
                onClick={updateActiveBrandStatus}
              disabled={aboutToUpdateActiveBrandProgress}
            >
                {aboutToUpdateActiveBrandProgress ? <SpinnerLoader size={16} /> : "Yes"}
            </Button>
            </LiftDialogFooter>
          </LiftDialogContent>
        </LiftDialog>

        <LiftDialog open={aboutToUpdateCreativeBrandShow} onOpenChange={(open) => !aboutToUpdateCreativeBrandProgress && setAboutToUpdateCreativeBrandShow(open)}>
          <LiftDialogContent className="sm:max-w-md">
            <LiftDialogHeader>
              <LiftDialogTitle>{aboutToUpdateCreativeBrand?.is_creative ? "Turn Creative off?" : "Turn Creative on?"}</LiftDialogTitle>
              <LiftDialogDescription>
                You are about to turn Creative {aboutToUpdateCreativeBrand?.is_creative ? "off" : "on"} for the brand{" "}
                <strong>{aboutToUpdateCreativeBrand?.name}</strong>. Do you want to continue?
              </LiftDialogDescription>
            </LiftDialogHeader>
            <LiftDialogFooter className="gap-2">
            <Button
                variant="outline"
                onClick={() => setAboutToUpdateCreativeBrandShow(false)}
                disabled={aboutToUpdateCreativeBrandProgress}
            >
              Cancel
            </Button>
            <Button
                onClick={updateCreativeBrandStatus}
                disabled={aboutToUpdateCreativeBrandProgress}
              >
                {aboutToUpdateCreativeBrandProgress ? <SpinnerLoader size={16} /> : "Yes"}
            </Button>
            </LiftDialogFooter>
          </LiftDialogContent>
        </LiftDialog>

        <LiftDialog open={aboutToUpdateAcornBrandShow} onOpenChange={(open) => !aboutToUpdateAcornBrandProgress && setAboutToUpdateAcornBrandShow(open)}>
          <LiftDialogContent className="sm:max-w-md">
            <LiftDialogHeader>
              <LiftDialogTitle>{aboutToUpdateAcornBrand?.is_acorn ? "Turn Influencer off?" : "Turn Influencer on?"}</LiftDialogTitle>
              <LiftDialogDescription>
                You are about to turn Influencer {aboutToUpdateAcornBrand?.is_acorn ? "off" : "on"} for the brand{" "}
                <strong>{aboutToUpdateAcornBrand?.name}</strong>. Do you want to continue?
              </LiftDialogDescription>
            </LiftDialogHeader>
            <LiftDialogFooter className="gap-2">
            <Button
                variant="outline"
                onClick={() => setAboutToUpdateAcornBrandShow(false)}
                disabled={aboutToUpdateAcornBrandProgress}
            >
              Cancel
            </Button>
            <Button
                onClick={updateAcornBrandStatus}
                disabled={aboutToUpdateAcornBrandProgress}
              >
                {aboutToUpdateAcornBrandProgress ? <SpinnerLoader size={16} /> : "Yes"}
            </Button>
            </LiftDialogFooter>
          </LiftDialogContent>
        </LiftDialog>

        {/* Prospect Confirmation Dialog */}
        <LiftDialog open={aboutToUpdateProspectBrandShow} onOpenChange={(open) => !aboutToUpdateProspectBrandProgress && setAboutToUpdateProspectBrandShow(open)}>
          <LiftDialogContent className="sm:max-w-md">
            <LiftDialogHeader>
              <LiftDialogTitle>
                {aboutToUpdateProspectBrand && isBrandProspect(aboutToUpdateProspectBrand.id) ? "Turn Prospect off?" : "Turn Prospect on?"}
              </LiftDialogTitle>
              <LiftDialogDescription>
                You are about to turn Prospect {aboutToUpdateProspectBrand && isBrandProspect(aboutToUpdateProspectBrand.id) ? "off" : "on"} for the brand{" "}
                <strong>{aboutToUpdateProspectBrand?.name}</strong>. Do you want to continue?
              </LiftDialogDescription>
            </LiftDialogHeader>
            <LiftDialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setAboutToUpdateProspectBrandShow(false)}
                disabled={aboutToUpdateProspectBrandProgress}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmProspectChange}
                disabled={aboutToUpdateProspectBrandProgress}
              >
                {aboutToUpdateProspectBrandProgress ? <SpinnerLoader size={16} /> : "Yes"}
              </Button>
            </LiftDialogFooter>
          </LiftDialogContent>
        </LiftDialog>

        {/* Delete Brand Dialog */}
        <LiftDialog open={deleteBrandDialog} onOpenChange={(open) => hideDeleteBrandProgressDialog && setDeleteBrandDialog(open)}>
          <LiftDialogContent className="sm:max-w-md">
            <LiftDialogHeader>
              <LiftDialogTitle>Delete Brand</LiftDialogTitle>
              <LiftDialogDescription>
                Deleting this Brand will remove all data related to it. Are you sure you want to delete this Brand?
              </LiftDialogDescription>
            </LiftDialogHeader>
            <LiftDialogFooter className="gap-2">
            <Button
                variant="outline"
              onClick={() => setDeleteBrandDialog(false)}
                disabled={!hideDeleteBrandProgressDialog}
            >
              Cancel
            </Button>
            <Button
                variant="destructive"
              onClick={() => {
                if (hideDeleteBrandProgressDialog) {
                  throttleDeleteBrand(
                    brandRow.id,
                    superAdmin ? brandRow.brand : brandRow.name
                  );
                }
              }}
                disabled={!hideDeleteBrandProgressDialog}
              >
                {!hideDeleteBrandProgressDialog ? <SpinnerLoader size={16} /> : "Yes, Delete"}
            </Button>
            </LiftDialogFooter>
          </LiftDialogContent>
        </LiftDialog>

        {USE_ADD_BRAND_WIZARD_MOCK && (
          <AddBrandWizard
            open={showAddBrandWizard}
            onOpenChange={setShowAddBrandWizard}
            organizations={allOrg}
            onOrganizationsRefresh={refreshOrganizations}
            verticalOptions={verticalsOptions}
            typeOptions={types.map((option: any) => ({
              label: option.label,
              value: option.value,
            }))}
            onSave={addBrandFromWizard}
          />
        )}

        {/* Add brand Sheet */}
        <Sheet 
          open={showAddBrandDialog}
          onOpenChange={(open) => {
            if (!open) {
              resetAddBrand();
            }
          }}
        >
          <SheetContent 
            side="right" 
            className="w-[600px] sm:max-w-[600px] flex flex-col h-full min-w-0 max-w-[100vw]"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <SheetHeader>
              <SheetTitle>Add Brand</SheetTitle>
            </SheetHeader>
                <div
              className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 pr-2"
              ref={scrollableContainerRef}
            >
              <div className="space-y-6 p-4 min-w-0">
                {/* Image Upload Section */}
                <div className="flex justify-center">
                    <div
                      className="relative h-[200px] w-full max-w-[400px] rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-gray-400 transition-colors overflow-hidden"
                      onClick={() => inputFile.current?.click()}
                    >
                      <input
                        type="file"
                        ref={inputFile}
                        className="hidden"
                        accept="image/*"
                        onChange={(event) => {
                          if (event.target.files?.[0]) {
                            setAddBrandImageRaw(event.target.files[0]);
                            setAddBrandImage(URL.createObjectURL(event.target.files[0]));
                          }
                          event.target.value = "";
                        }}
                      />
                      {addBrandImage === null ? (
                        <div className="flex flex-col items-center gap-2 text-gray-500">
                          <ImageIcon className="h-10 w-10" />
                          <p className="text-sm">Click to upload brand image</p>
                        </div>
                      ) : (
                        <>
                          <img
                            className="w-full h-full object-contain rounded-lg"
                            src={addBrandImage}
                            alt="Brand"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-lg">
                            <p className="text-sm font-medium text-white">Change image</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                {/* Row 1: Name, Alias, Launch Date */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="brandName">Name *</Label>
                    <Input
                      id="brandName"
                      placeholder="Eg. - LT Partners"
                        value={addBrandName}
                      onChange={(e) => setAddBrandName(e.target.value)}
                      />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="brandAlias">Alias *</Label>
                    <Input
                      id="brandAlias"
                      placeholder="Eg. - lift@newengen.com"
                        value={addAlias}
                      onChange={(e) => setAddAlias(e.target.value)}
                      />
                  </div>
                  <div className="space-y-2">
                    <Label>Launch Date</Label>
                    <LiftDatePicker
                      value={addBrandDate ? dayjs(addBrandDate).toDate() : undefined}
                      onChange={(date) => setAddBrandDate(date ? dayjs(date) : null)}
                      className="w-full"
                    />
                      </div>
                    </div>

                {/* Row 2: Verticals, Type, Domain */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Verticals *</Label>
                    <MultiSelectCheckboxTag
                      placeholder="Select verticals"
                      value={addBrandVertical}
                      onChange={(selectedValues: string[]) => {
                        setAddBrandVertical(selectedValues);
                      }}
                      options={verticalsOptions.map((v: string) => ({
                        label: v,
                        value: v,
                      }))}
                      containerClassName="w-full"
                      showSelectAll
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type *</Label>
                    <SingleSelect
                      options={types.map((option) => ({
                        label: option.label,
                        value: option.value,
                      }))}
                      defaultValue={addBrandType}
                      onChange={(value) => setAddBrandType(value)}
                      placeholder="Select Type"
                      containerClassName="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="brandDomain">Domain *</Label>
                    <Input
                      id="brandDomain"
                      placeholder="Eg. - newengen.com"
                        value={addBrandDomain}
                      onChange={(e) => setAddBrandDomain(e.target.value)}
                      />
                    </div>
                    </div>
                {/* Row 3: Timezone, Date Joined, Week Start Day */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <SingleSelect
                      options={timezoneList.map((tz: any) => ({
                        label: tz,
                        value: tz,
                      }))}
                      defaultValue={addTimeZone}
                      onChange={(value) => setAddTimeZone(value)}
                      placeholder="Select Timezone"
                      containerClassName="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date Joined</Label>
                    <LiftDatePicker
                      value={addDateJoined ? dayjs(addDateJoined).toDate() : undefined}
                      onChange={(date) => setAddDateJoined(date ? dayjs(date) : null)}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Week Start Day</Label>
                    <SingleSelect
                      options={weekDays.map((day: any) => ({
                        label: day,
                        value: day,
                      }))}
                      defaultValue={addWeekStartDay}
                      onChange={(value) => setAddWeekStartDay(value)}
                      placeholder="Select Day"
                      containerClassName="w-full"
                          />
                  </div>
                      </div>

                {/* Row 4: Organization, Date Preset */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Organization *</Label>
                    <SingleSelect
                      options={allOrg.filter((org: any) => org.is_active).map((org: any) => ({
                        label: org.label,
                        value: org.value.toString(),
                      }))}
                      defaultValue={addOrg?.toString()}
                      onChange={(value) => {
                        const selectedOrg = allOrg.find((org: any) => org.value.toString() === value);
                        if (selectedOrg) {
                          handleChangeOrg(null, selectedOrg);
                        }
                      }}
                      placeholder="Select Organization"
                      containerClassName="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date Preset</Label>
                    <SingleSelect
                      options={date_presets_list.flatMap((item: any) =>
                        item.data.map((d: any) => ({
                          label: `${item.label} - ${d}`,
                          value: d,
                        }))
                      )}
                      defaultValue={selectedDatePreset}
                      onChange={(value) => setSelectedDatePreset(value)}
                      placeholder="Select Date Preset"
                      containerClassName="w-full"
                    />
                  </div>
                </div>

                {/* Row 5: Revenue Primary, Conversion Primary */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Revenue Primary</Label>
                    <SingleSelect
                      options={[
                        { label: "Ads Platform", value: "Ads Platform" },
                        { label: "Web Analytics", value: "Web Analytics" },
                        { label: "Mobile Measurement", value: "Mobile Measurement" },
                        { label: "Ad Server", value: "Ad Server" },
                        { label: "Multi-Touch Attribution", value: "Multi-Touch Attribution" },
                        { label: "E-Commerce", value: "E-Commerce" },
                        { label: "Internal Client Source", value: "Internal Client Source" },
                        { label: "Other Platform", value: "Other Platform" },
                      ]}
                      defaultValue={addRevenueSourceOfTruth}
                      onChange={(value) => setAddRevenueSourceOfTruth(value)}
                          disabled={!superAdmin}
                      placeholder="Select Revenue Primary"
                      containerClassName="w-full"
                    />
                    </div>
                  <div className="space-y-2">
                    <Label>Conversion Primary</Label>
                    <SingleSelect
                      options={[
                        { label: "Ads Platform", value: "Ads Platform" },
                        { label: "Web Analytics", value: "Web Analytics" },
                        { label: "Mobile Measurement", value: "Mobile Measurement" },
                        { label: "Ad Server", value: "Ad Server" },
                        { label: "Multi-Touch Attribution", value: "Multi-Touch Attribution" },
                        { label: "E-Commerce", value: "E-Commerce" },
                        { label: "Internal Client Source", value: "Internal Client Source" },
                        { label: "Other Platform", value: "Other Platform" },
                      ]}
                      defaultValue={addConversionSourceOfTruth}
                      onChange={(value) => setAddConversionSourceOfTruth(value)}
                      disabled={!superAdmin}
                      placeholder="Select Conversion Primary"
                      containerClassName="w-full"
                          />
                  </div>
                </div>

                {/* Upload Fiscal Calendar - opens in drawer */}
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={handleClickFiscalDialogOpen}
                    className="w-full"
                  >
                    Upload Fiscal Calendar
                  </Button>
                </div>

                {/* Brand Features Checkboxes */}
                <div ref={brandFeaturesRef} className="space-y-2">
                  <Label>Brand Features</Label>
                  <div className="flex flex-wrap gap-6">
                    <div className="flex items-center space-x-2">
                      <LiftCheckbox
                        id="affiliate-checkbox"
                            checked={selectedOptions.includes("Affiliate")}
                        onCheckedChange={(checked) => {
                          handleSetAdditionalOptns({ target: { name: "Affiliate", checked } } as any);
                        }}
                      />
                      <Label htmlFor="affiliate-checkbox" className="font-normal cursor-pointer">Affiliate</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <LiftCheckbox
                        id="performance-checkbox"
                            checked={selectedOptions.includes("Performance")}
                        onCheckedChange={(checked) => {
                          handleSetAdditionalOptns({ target: { name: "Performance", checked } } as any);
                        }}
                      />
                      <Label htmlFor="performance-checkbox" className="font-normal cursor-pointer">Performance</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <LiftCheckbox
                        id="prospect-checkbox"
                            checked={selectedOptions.includes("Prospect")}
                        onCheckedChange={(checked) => {
                          handleSetAdditionalOptns({ target: { name: "Prospect", checked } } as any);
                        }}
                      />
                      <Label htmlFor="prospect-checkbox" className="font-normal cursor-pointer">Prospect</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <LiftCheckbox
                        id="creative-checkbox"
                            checked={selectedOptions.includes("Creative")}
                        onCheckedChange={(checked) => {
                          handleSetAdditionalOptns({ target: { name: "Creative", checked } } as any);
                        }}
                      />
                      <Label htmlFor="creative-checkbox" className="font-normal cursor-pointer">Creative</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <LiftCheckbox
                        id="acorn-checkbox"
                            checked={selectedOptions.includes("Acorn")}
                        onCheckedChange={(checked) => {
                          handleSetAdditionalOptns({ target: { name: "Acorn", checked } } as any);
                        }}
                      />
                      <Label htmlFor="acorn-checkbox" className="font-normal cursor-pointer">
                        {ACORN_BRAND_FEATURE_LABEL}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <LiftCheckbox
                        id="performance-creator-checkbox"
                        checked={addIsPerformanceCreator}
                        onCheckedChange={(checked) => {
                          setAddIsPerformanceCreator(checked as boolean);
                        }}
                      />
                      <Label htmlFor="performance-creator-checkbox" className="font-normal cursor-pointer">Performance Creator</Label>
                    </div>
                  </div>
                  {highlightBrandFeatures && (
                    <p className="text-sm text-red-500">
                      At least one brand feature must be selected
                    </p>
                  )}
                </div>

                {/* HubSpot Company ID Input */}
                <div className="space-y-2">
                  <Label>
                    HubSpot Company ID{addIsPerformanceCreator ? " *" : ""}
                  </Label>
                  <Input
                    placeholder="Enter HubSpot Company ID"
                    value={addHubspotCompanyId}
                    onChange={(e) => setAddHubspotCompanyId(e.target.value)}
                    maxLength={100}
                    aria-required={addIsPerformanceCreator}
                  />
                  <p
                    className={`text-xs ${
                      addIsPerformanceCreator && !addHubspotCompanyId.trim()
                        ? "text-red-500"
                        : "text-muted-foreground"
                    }`}
                  >
                    {addIsPerformanceCreator
                      ? "Required for Performance Creator brands."
                      : "Optional. Max 100 characters."}
                  </p>
                </div>

                {shouldShowGrapevineFields(addIsPerformanceCreator) && (
                  <div className="space-y-4 rounded-md border p-3">
                    <div className="space-y-2">
                      <Label>Grapevine Slug</Label>
                      <Input
                        placeholder="brand-slug"
                        value={addGvSlug}
                        onChange={(e) => setAddGvSlug(e.target.value)}
                        maxLength={150}
                      />
                      <GrapevineSlugAvailabilityMessage
                        status={addGvSlugStatus}
                        slug={addGvSlug}
                        resolvedSlug={addGvResolvedSlug}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Grapevine Vertical *</Label>
                      <Input
                        placeholder="e.g. Beverages"
                        value={addGvVertical}
                        onChange={(e) => setAddGvVertical(e.target.value)}
                        maxLength={100}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Name</Label>
                      <Input
                        placeholder="Grapevine contact name"
                        value={addGvContactName}
                        onChange={(e) => setAddGvContactName(e.target.value)}
                        maxLength={150}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Email *</Label>
                      <Input
                        placeholder="contact@brand.com"
                        value={addGvContactEmail}
                        onChange={(e) => setAddGvContactEmail(e.target.value)}
                        maxLength={150}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Homepage URL</Label>
                      <Input
                        placeholder="https://brand.com"
                        value={addGvHomepageUrl}
                        onChange={(e) => setAddGvHomepageUrl(e.target.value)}
                        maxLength={255}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Brand Tier</Label>
                      <Input
                        placeholder="e.g. Normal"
                        value={addGvTier}
                        onChange={(e) => setAddGvTier(e.target.value)}
                        maxLength={100}
                      />
                    </div>
                  </div>
                )}

                {/* Info Message - only when Affiliate (Affluent) is checked */}
                {selectedOptions.includes("Affiliate") && (
                  <div className="text-sm text-gray-600 text-center">
                    Make sure the brand name matches with Affluent!
                  </div>
                )}
              </div>
              {/* End of lift-ui form section */}

              {/* Remaining MUI section for tabs - kept as-is due to complexity */}
              <Box sx={{ px: 2, pb: 2 }}>
                <ThemeProvider theme={theme}>
                {false && selectedOptions.length > 0 && (
                  <hr style={{ width: "100%", margin: "17px 0" }} />
                )}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    flexDirection: "column",
                    gap: "30px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {/* NEW CONTENT GOES HERE */}
                    {/* <button > */}
                    {isOverflowingAdd && (
                      <ChevronLeft
                        onClick={() => moveScroll(0)}
                        className="scroll-btn"
                      />
                    )}
                    {/* </button> */}
                    <div
                      ref={scrollHeader}
                      className="add-header"
                      style={{
                        position: "relative",
                        width: "1050px",
                        display: "flex",
                        justifyContent: isOverflowingAdd
                          ? "flex-start"
                          : "center",
                        alignItems: "center",
                        overflow: "auto",
                        gap: "30px",
                        margin: "10px 0 20px 0",
                      }}
                    >
                      {false && selectedOptions.includes("Affiliate") && (
                        <div
                          className={
                            selectedItem == 1
                              ? "add-header-content active"
                              : "add-header-content"
                          }
                          id="1"
                          onClick={(e: any) => setSelectedItem(e.target.id)}
                        >
                          Affiliate
                        </div>
                      )}
                      {false && selectedOptions.includes("Performance") && (
                        <div
                          className={
                            selectedItem == 2
                              ? "add-header-content active"
                              : "add-header-content"
                          }
                          id="2"
                          onClick={(e: any) => setSelectedItem(e.target.id)}
                        >
                          Performance
                        </div>
                      )}
                    </div>
                    {isOverflowingAdd && (
                      <ChevronRight
                        onClick={() => moveScroll(1)}
                        className="scroll-btn"
                      />
                    )}
                  </div>
                </div>
                {false && selectedItem == 1 && selectedOptions.length > 0 && (
                  <>
                    <div
                      className="affiliate-wrapper"
                      style={{
                        display: "flex",
                        marginTop: "20px",
                        flexDirection: "column",
                        gap: "20px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span>Want to link GA4 ? </span>
                        <CusSwitch
                          style={{
                            cursor: "pointer",
                            color: "#16306D",
                          }}
                          onClick={() => setIsGA4Active(!isGA4Active)}
                          checked={isGA4Active}
                        />
                      </div>
                      <div className="flex flex-wrap">
                        <TextField
                          disabled={!superAdmin ? true : false}
                          size="small"
                          label="Affiliate Email Alias"
                          // helperText="Eg. - lift@newengen.com"
                          value={addAffiliateAlias}
                          onChange={(event) =>
                            setAddAffiliateAlias(event.target.value)
                          }
                        />
                      </div>
                      {isGA4Active && (
                        <div
                          style={{
                            marginTop: "20px",
                            width: "100%",
                            maxWidth: "100%",
                          }}
                        >
                          {ga4Rows.map((row: any, rowIndex: number) => (
                            <div
                              key={rowIndex}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                marginBottom: "15px",
                                paddingTop: "15px",
                                paddingBottom: "15px",
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                                backgroundColor: "#fafafa",
                                flexWrap: "nowrap",
                                overflowX: "auto",
                                width: "100%",
                              }}
                            >
                              <TextField
                                disabled={!superAdmin ? true : false}
                                size="small"
                                label="GA4 Account Name"
                                value={row.accountName}
                                onChange={(event) =>
                                  updateGA4Row(
                                    rowIndex,
                                    "accountName",
                                    event.target.value
                                  )
                                }
                                style={{ minWidth: "160px", flexShrink: 0 }}
                              />
                              <TextField
                                type="number"
                                disabled={!superAdmin ? true : false}
                                size="small"
                                label="GA4 Account ID"
                                value={row.accountId}
                                onChange={(event) =>
                                  updateGA4Row(
                                    rowIndex,
                                    "accountId",
                                    event.target.value
                                  )
                                }
                                style={{ minWidth: "160px", flexShrink: 0 }}
                              />
                              <TextField
                                type="number"
                                required
                                disabled={!superAdmin ? true : false}
                                size="small"
                                label="GA4 Property ID *"
                                value={row.propertyId}
                                onChange={(event) =>
                                  updateGA4Row(
                                    rowIndex,
                                    "propertyId",
                                    event.target.value
                                  )
                                }
                                style={{ minWidth: "160px", flexShrink: 0 }}
                              />
                              <TextField
                                disabled={!superAdmin ? true : false}
                                size="small"
                                label="GA4 Property Name"
                                value={row.propertyName}
                                onChange={(event) =>
                                  updateGA4Row(
                                    rowIndex,
                                    "propertyName",
                                    event.target.value
                                  )
                                }
                                style={{ minWidth: "160px", flexShrink: 0 }}
                              />
                              <TextField
                                disabled={!superAdmin ? true : false}
                                size="small"
                                label="GA4 source"
                                value={row.source}
                                onChange={(event) =>
                                  updateGA4Row(
                                    rowIndex,
                                    "source",
                                    event.target.value
                                  )
                                }
                                style={{ minWidth: "160px", flexShrink: 0 }}
                              />

                              <div
                                className="multiple-medium"
                                style={{ minWidth: "180px", flexShrink: 0 }}
                              >
                                {row.medValues?.map(
                                  (item: any, mediumIndex: number) => {
                                    return (
                                      <button
                                        key={mediumIndex}
                                        onClick={(e) => e.preventDefault()}
                                        style={{
                                          margin: "2px",
                                          padding: "4px 8px",
                                          backgroundColor: "#16306D",
                                          borderRadius: "4px",
                                          fontSize: "11px",
                                        }}
                                      >
                                        {item}{" "}
                                        <span
                                          onClick={(e: any) => {
                                            handleRemoveMediumForRow(
                                              e,
                                              rowIndex,
                                              mediumIndex
                                            );
                                          }}
                                          style={{
                                            cursor: "pointer",
                                            marginLeft: "4px",
                                          }}
                                        >
                                          ×
                                        </span>
                                      </button>
                                    );
                                  }
                                )}

                                <input
                                  onKeyDown={(e: any) =>
                                    handleAddMultipleMediumForRow(e, rowIndex)
                                  }
                                  style={{
                                    margin: "0",
                                    backgroundColor: "transparent",
                                    width: "120px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    padding: "4px 6px",
                                    fontSize: "11px",
                                  }}
                                  placeholder="Medium + Enter"
                                  value={row.singleMedValue}
                                  onChange={(event) =>
                                    updateGA4Row(
                                      rowIndex,
                                      "singleMedValue",
                                      event.target.value
                                    )
                                  }
                                />
                              </div>

                              {ga4Rows.length > 1 && (
                                <div style={{ paddingRight: "100px" }}>
                                  <button
                                    onClick={() => removeGA4Row(rowIndex)}
                                    style={{
                                      backgroundColor: "#f44336",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "4px",
                                      padding: "8px 12px",
                                      cursor: "pointer",
                                      fontSize: "11px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}

                          <button
                            onClick={addNewGA4Row}
                            style={{
                              backgroundColor: "#4caf50",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              padding: "10px 16px",
                              cursor: "pointer",
                              fontSize: "14px",
                              marginTop: "10px",
                            }}
                          >
                            + Add New GA4 Row
                          </button>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "20px",
                        margin: "50px",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        // alignItems: "center",
                      }}
                    >
                      <div>
                        <TableContainer
                          component={Paper}
                          sx={{
                            maxHeight: "500px",
                            overflow: "auto",
                            cursor: "pointer",
                          }}
                          onClick={() => setBrandGoalDialogOpen(true)}
                        >
                          <MuiTable
                            sx={{ minWidth: "300px", maxWidth: "350px" }}
                            aria-label="simple table"
                            stickyHeader
                          >
                            <MuiTableHead>
                              <MuiTableRow>
                                <MuiTableCell
                                  sx={{
                                    textAlign: "left",
                                    fontWeight: "900",
                                  }}
                                >
                                  Brand Goals
                                </MuiTableCell>
                                <MuiTableCell
                                  sx={{
                                    textAlign: "center",
                                    fontWeight: "900",
                                  }}
                                >
                                  <EditOutlinedIcon />
                                </MuiTableCell>
                              </MuiTableRow>
                              <MuiTableRow>
                                <MuiTableCell sx={{ fontWeight: "900" }}>
                                  Metrics ( Name ){" "}
                                </MuiTableCell>
                                <MuiTableCell
                                  sx={{ fontWeight: "900" }}
                                  align="left"
                                >
                                  Value
                                </MuiTableCell>
                              </MuiTableRow>
                            </MuiTableHead>
                            <MuiTableBody>
                              {Object.keys(brandGoals[0]).map(
                                (key: any, index: any) => {
                                  return (
                                    <MuiTableRow
                                      key={index}
                                      sx={{
                                        "&:last-child td, &:last-child th": {
                                          border: 0,
                                        },
                                      }}
                                    >
                                      <MuiTableCell component="th" scope="row">
                                        {key}
                                      </MuiTableCell>
                                      <MuiTableCell align="left">
                                        {brandGoals[0][key]}
                                      </MuiTableCell>
                                    </MuiTableRow>
                                  );
                                }
                              )}
                            </MuiTableBody>
                          </MuiTable>
                        </TableContainer>
                      </div>
                      <div>
                        <TableContainer
                          onClick={() => setBrandWeightDialogOpen(true)}
                          component={Paper}
                          sx={{
                            maxHeight: "500px",
                            overflow: "auto",
                            cursor: "pointer",
                          }}
                        >
                          <MuiTable
                            sx={{ minWidth: "300px", maxWidth: "350px" }}
                            aria-label="simple table"
                            stickyHeader
                          >
                            <MuiTableHead>
                              <MuiTableRow>
                                <MuiTableCell
                                  sx={{
                                    textAlign: "left",
                                    fontWeight: "900",
                                  }}
                                >
                                  Brand Weights
                                </MuiTableCell>
                                <MuiTableCell
                                  sx={{
                                    textAlign: "center",
                                    fontWeight: "900",
                                  }}
                                >
                                  <EditOutlinedIcon />
                                </MuiTableCell>
                              </MuiTableRow>
                            </MuiTableHead>
                            <MuiTableHead>
                              <MuiTableRow>
                                <MuiTableCell sx={{ fontWeight: "900" }}>
                                  Metrics ( Name ){" "}
                                </MuiTableCell>
                                <MuiTableCell
                                  sx={{ fontWeight: "900" }}
                                  align="left"
                                >
                                  Value
                                </MuiTableCell>
                              </MuiTableRow>
                            </MuiTableHead>
                            <MuiTableBody>
                              {Object.keys(brandWeights[0]).map(
                                (key: any, index: any) => {
                                  return (
                                    <MuiTableRow
                                      key={index}
                                      sx={{
                                        "&:last-child td, &:last-child th": {
                                          border: 0,
                                        },
                                      }}
                                    >
                                      <MuiTableCell component="th" scope="row">
                                        {key}
                                      </MuiTableCell>
                                      <MuiTableCell align="left">
                                        {brandWeights[0][key]}
                                      </MuiTableCell>
                                    </MuiTableRow>
                                  );
                                }
                              )}
                            </MuiTableBody>
                          </MuiTable>
                        </TableContainer>
                      </div>
                      <div>
                        <TableContainer
                          onClick={() => setScrapDialogOpen(true)}
                          component={Paper}
                          sx={{
                            maxHeight: "500px",
                            overflow: "auto",
                            cursor: "pointer",
                          }}
                        >
                          <MuiTable
                            sx={{ minWidth: "300px", maxWidth: "350px" }}
                            aria-label="simple table"
                            stickyHeader
                          >
                            <MuiTableHead>
                              <MuiTableRow>
                                <MuiTableCell
                                  sx={{
                                    textAlign: "left",
                                    fontWeight: "900",
                                  }}
                                >
                                  Scraping Urls
                                </MuiTableCell>
                                <MuiTableCell
                                  sx={{
                                    textAlign: "center",
                                    fontWeight: "900",
                                  }}
                                >
                                  <EditOutlinedIcon />
                                </MuiTableCell>
                              </MuiTableRow>
                            </MuiTableHead>
                            <MuiTableHead>
                              <MuiTableRow>
                                <MuiTableCell sx={{ fontWeight: "900" }}>
                                  Platform ( Name ){" "}
                                </MuiTableCell>
                                <MuiTableCell
                                  sx={{ fontWeight: "900" }}
                                  align="right"
                                >
                                  Url
                                </MuiTableCell>
                              </MuiTableRow>
                            </MuiTableHead>
                            <MuiTableBody>
                              {scrapData?.length === 0 && (
                                <MuiTableRow
                                  sx={{
                                    fontFamily: '"Figtree", sans-serif',
                                    fontSize: "0.8rem",
                                    color: "#16306D",
                                  }}
                                >
                                  <MuiTableCell
                                    colSpan={2}
                                    sx={{ textAlign: "center" }}
                                  >
                                    No Data is present
                                    <br /> <b>Click to Add</b>
                                  </MuiTableCell>
                                </MuiTableRow>
                              )}
                              {scrapData?.length > 0 &&
                                scrapData.map((key: any, index: any) => {
                                  return (
                                    <MuiTableRow
                                      key={index}
                                      sx={{
                                        "&:last-child td, &:last-child th": {
                                          border: 0,
                                        },
                                      }}
                                    >
                                      <MuiTableCell component="th" scope="row">
                                        {key.platform}
                                      </MuiTableCell>
                                      <MuiTableCell
                                        align="right"
                                        sx={{ width: "300px !important" }}
                                      >
                                        {key.url}
                                      </MuiTableCell>
                                    </MuiTableRow>
                                  );
                                })}
                            </MuiTableBody>
                          </MuiTable>
                        </TableContainer>
                      </div>
                    </div>
                  </>
                )}

                {false && selectedItem == 2 && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "30px",
                        width: "100%",
                      }}
                    >
                      {perfData?.length > 0 && (
                        <TableContainer component={Paper}>
                          <MuiTable
                            aria-label="collapsible table"
                            stickyHeader
                            sx={{
                              width: "100%",
                            }}
                          >
                            <MuiTableHead>
                              <MuiTableRow>
                                <MuiTableCell />
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Account Type
                                </MuiTableCell>
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Account Name
                                </MuiTableCell>
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Account ID
                                </MuiTableCell>
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Property Name
                                </MuiTableCell>
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Property ID
                                </MuiTableCell>
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Attribution Window
                                </MuiTableCell>
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Attribution Basis
                                </MuiTableCell>
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Selected Conversion Event
                                </MuiTableCell>
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Selected Conversion Tag Names
                                </MuiTableCell>
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Metaplier Access
                                </MuiTableCell>
                                <MuiTableCell
                                  align="center"
                                  style={{ fontWeight: "800" }}
                                >
                                  Actions
                                </MuiTableCell>
                              </MuiTableRow>
                            </MuiTableHead>
                            <MuiTableBody>
                              {perfData?.map((item: any, index: any) => {
                                return <Row key={index} row={item} />;
                              })}
                              {/* {rows.map((row) => (
                        ))} */}
                            </MuiTableBody>
                          </MuiTable>
                        </TableContainer>
                      )}

                      {perfData?.length == 0 && (
                        <>
                          <div className="no-perf-wrapper">
                            <div className="no-perf-content">
                              <span>
                                There are no Platforms added, <br />
                                click on <b>Add button</b> below to add one
                              </span>
                            </div>
                            <Button
                              style={{ margin: "0" }}
                              variant="contained"
                              sx={{
                                border: "1px solid rgba(0,0,0,0.25)",
                                width: "22ch",
                                height: "40px",
                                textTransform: "none",
                                color: "white",
                                backgroundColor:
                                  "#16306D" /* Original background color */,
                                fontSize: "16px",
                                "&:hover": {
                                  /* Hover styles */
                                  backgroundColor:
                                    "rgb(22, 48, 109,0.9)" /* New background color on hover */,
                                },
                              }}
                              onClick={handleClickAddPerfDialogOpen}
                            >
                              Add
                            </Button>
                          </div>
                        </>
                      )}
                      {perfData?.length != 0 && (
                        <div
                          style={{
                            width: "inherit",
                            display: "flex",
                            justifyContent: "right",
                          }}
                        >
                          <Button
                            style={{ margin: "0" }}
                            variant="contained"
                            sx={{
                              border: "1px solid rgba(0,0,0,0.25)",
                              width: "22ch",
                              height: "40px",
                              textTransform: "none",
                              color: "white",
                              backgroundColor:
                                "#16306D" /* Original background color */,
                              fontSize: "16px",
                              "&:hover": {
                                /* Hover styles */
                                backgroundColor:
                                  "rgb(22, 48, 109,0.9)" /* New background color on hover */,
                              },
                            }}
                            onClick={handleClickAddPerfDialogOpen}
                          >
                            Add
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    flexDirection: "column",

                    gap: "20px",
                    width: "100%",
                  }}
                >
                  {addFiscalCalendar?.length > 0 && (
                    <>
                      <TableContainer
                        component={Paper}
                        sx={{
                          maxHeight: 400,
                          maxWidth: 1110,
                          marginTop: "60px",

                          cursor: "pointer",
                        }}
                      >
                        <MuiTable
                          sx={{ minWidth: 100 }}
                          aria-label="fiscal calendar table"
                          stickyHeader
                        >
                          <MuiTableHead>
                            <StyledTableCell
                              colSpan={Object.keys(addFiscalCalendar[0]).length}
                              sx={{
                                textAlign: "center",
                                fontSize: "27px",
                                fontFamily: "Gibbson sans-serif",
                                fontWeight: "700",
                              }}
                            >
                              Fiscal Calendar
                            </StyledTableCell>
                          </MuiTableHead>
                          <MuiTableHead>
                            <StyledTableRow>
                              {addFiscalCalendar?.length > 0 &&
                                Object.keys(addFiscalCalendar[0])?.map(
                                  (header: any) => (
                                    <StyledTableCell key={header}>
                                      {header}
                                    </StyledTableCell>
                                  )
                                )}
                            </StyledTableRow>
                          </MuiTableHead>
                          <MuiTableBody>
                            {addFiscalCalendar?.map((data: any, index: any) => (
                              <StyledTableRow key={index}>
                                {addFiscalCalendar?.length > 0 &&
                                  Object.keys(addFiscalCalendar[0])?.map(
                                    (header: any) => (
                                      <StyledTableCell key={header}>
                                        {data[header]}
                                      </StyledTableCell>
                                    )
                                  )}
                              </StyledTableRow>
                            ))}
                          </MuiTableBody>
                        </MuiTable>
                      </TableContainer>

                      <div
                        style={{
                          display: "flex",
                          gap: "15px",
                        }}
                      >
                        {addFiscalCalendar && (
                          <span
                            style={{
                              display: "flex",
                              cursor: "pointer",
                              justifyContent: "flex-end",
                              alignItems: "center",
                              gap: "2px",
                            }}
                            onClick={handleResetFiscalClicked}
                          >
                            <RotateLeftIcon fontSize="inherit" /> Reset
                          </span>
                        )}

                        <span
                          style={{
                            display: "flex",
                            cursor: "pointer",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "2px",
                          }}
                          onClick={handleDownloadFiscalTemplate}
                        >
                          <FileDownloadOutlinedIcon fontSize="inherit" />{" "}
                          Template
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </ThemeProvider>
            </Box>
            </div>
            <SheetFooter className="border-t pt-4 mt-4">
            <Button
                variant="outline" 
                onClick={() => setShowAddBrandDialog(false)}
              >
                Cancel
              </Button>
              <Button
              onClick={() => {
                if (hideAddBrandProgressDialog) {
                  addBrand();
                }
              }}
                disabled={!hideAddBrandProgressDialog}
            >
                {hideAddBrandProgressDialog ? (
                  "Save"
                ) : (
                  <SpinnerLoader size={16} />
                )}
            </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Edit Brand Sheet */}
        <Sheet
          open={showEditBrandDialog}
          onOpenChange={(open) => {
            if (!open) {
              setShowEditBrandDialog(false);
              setShowAdditionalFields(false);
              setHideEditBrandProgressDialog(true);
              setAddFiscalCalendar(null);
              resetEditFormFields();
            }
            }}
          >
          <SheetContent
            side="right"
            className="w-[600px] sm:max-w-[600px] flex flex-col h-full min-w-0 max-w-[100vw]"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            >
            <SheetHeader>
              <SheetTitle>Edit Brand</SheetTitle>
            </SheetHeader>
                <div
              className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 pr-2"
              ref={dialogContentRef}
            >
              <div className="space-y-6 p-4 min-w-0">
                {/* Image Upload Section */}
                <div className="flex justify-center">
                    <div
                    className="h-[200px] w-full max-w-[400px] rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                      onClick={() => editInputFile.current.click()}
                    >
                    {editBrandImage ? (
                      <img
                        className="w-full h-full object-contain rounded-lg"
                          src={editBrandImage}
                          alt="Brand"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-500">
                        <ImageIcon className="h-10 w-10" />
                        <p className="text-sm">Click to upload brand image</p>
                      </div>
                    )}
                        <input
                          type="file"
                          ref={editInputFile}
                      className="hidden"
                          onChange={(event) => {
                            if (event.target.files && event.target.files[0]) {
                              if (!superAdmin) {
                                setEditBrandImageChange(1);
                              }
                              setEditBrandImageRaw(event.target.files[0]);
                              setEditBrandImage(
                                URL.createObjectURL(event.target.files[0])
                              );
                            }
                          }}
                        />
                      </div>
                    </div>

                {/* Row 1: Name, Alias, Launch Date */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="editBrandName">Name *</Label>
                    <Input
                      id="editBrandName"
                      placeholder="Eg. - LT Partners"
                      value={editBrandName || ""}
                      onChange={(e) => setEditBrandName(e.target.value)}
                      disabled={!superAdmin}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="editAlias">Alias *</Label>
                    <Input
                      id="editAlias"
                      placeholder="Eg. - lift@newengen.com"
                      value={editAlias || ""}
                      onChange={(e) => setEditAlias(e.target.value)}
                      disabled={!superAdmin}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Launch Date</Label>
                    <LiftDatePicker
                      value={editBrandDate ? dayjs(editBrandDate).toDate() : undefined}
                      onChange={(date) => {
                        setEditBrandDate(date ? dayjs(date) : null);
                        setEditDateJoined(date ? dayjs(date) : null);
                          }}
                      className="w-full"
                    />
                    </div>
                </div>

                {/* Row 2: Verticals, Type, Domain */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Verticals *</Label>
                    <MultiSelectCheckboxTag
                      placeholder="Select verticals"
                      value={editBrandVertical || []}
                      onChange={(selectedValues: string[]) => {
                        setEditBrandVertical(selectedValues);
                          setEditBrandVerticalChange(1);
                      }}
                      options={verticalsOptions.map((v: string) => ({
                        label: v,
                        value: v,
                      }))}
                      containerClassName="w-full"
                      showSelectAll
                    />
                  </div>
                  {superAdmin && (
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <SingleSelect
                        options={types.map((option) => ({
                          label: option.label,
                          value: option.value,
                        }))}
                        defaultValue={editBrandType}
                        onChange={(value) => setEditBrandType(value)}
                        placeholder="Select Type"
                        containerClassName="w-full"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="editDomain">Domain *</Label>
                    <Input
                      id="editDomain"
                      placeholder="Eg. - newengen.com"
                      value={editBrandDomain || ""}
                      onChange={(e) => {
                          if (!superAdmin) {
                            setEditBrandDomainChange(1);
                          }
                        setEditBrandDomain(e.target.value);
                        }}
                      />
                    </div>
                </div>

                {/* Row 3: Timezone, Date Joined, Week Start Day */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <SingleSelect
                      options={timezoneList.map((tz: any) => ({
                        label: tz,
                        value: tz,
                      }))}
                      defaultValue={editTimeZone}
                      onChange={(value) => setEditTimeZone(value)}
                      placeholder="Select Timezone"
                      containerClassName="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date Joined</Label>
                    <LiftDatePicker
                      value={editDateJoined ? dayjs(editDateJoined).toDate() : undefined}
                      onChange={(date) => setEditDateJoined(date ? dayjs(date) : null)}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Week Start Day</Label>
                    <SingleSelect
                      options={weekDays.map((day: any) => ({
                        label: day,
                        value: day,
                      }))}
                      defaultValue={editWeekStartDay}
                      onChange={(value) => setEditWeekStartDay(value)}
                      placeholder="Select Day"
                      containerClassName="w-full"
                          />
                  </div>
                      </div>

                {/* Row 4: Organization, Date Preset */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Organization *</Label>
                    <SingleSelect
                      options={allOrg.filter((org: any) => org.is_active).map((org: any) => ({
                        label: org.label,
                        value: org.value.toString(),
                      }))}
                      defaultValue={editOrg?.value?.toString()}
                      onChange={(value) => {
                        const selectedOrg = allOrg.find((org: any) => org.value.toString() === value);
                        if (selectedOrg) {
                          handleEditChange(null, selectedOrg);
                        }
                      }}
                      placeholder="Select Organization"
                      containerClassName="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date Preset</Label>
                    <SingleSelect
                      options={date_presets_list.flatMap((item: any) =>
                        item.data.map((d: any) => ({
                          label: `${item.label} - ${d}`,
                          value: d,
                        }))
                      )}
                      defaultValue={selectedEditDatePreset}
                      onChange={(value) => setSelectedEditDatePreset(value)}
                      placeholder="Select Date Preset"
                      containerClassName="w-full"
                    />
                  </div>
                </div>

                {/* Row 5: Revenue Primary, Conversion Primary */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Revenue Primary</Label>
                    <SingleSelect
                      options={[
                        { label: "Ads Platform", value: "Ads Platform" },
                        { label: "Web Analytics", value: "Web Analytics" },
                        { label: "Mobile Measurement", value: "Mobile Measurement" },
                        { label: "Ad Server", value: "Ad Server" },
                        { label: "Multi-Touch Attribution", value: "Multi-Touch Attribution" },
                        { label: "E-Commerce", value: "E-Commerce" },
                        { label: "Internal Client Source", value: "Internal Client Source" },
                        { label: "Other Platform", value: "Other Platform" },
                      ]}
                      defaultValue={editRevenueSourceOfTruth}
                      onChange={(value) => setEditRevenueSourceOfTruth(value)}
                          disabled={!superAdmin}
                      placeholder="Select Revenue Primary"
                      containerClassName="w-full"
                    />
                    </div>
                  <div className="space-y-2">
                    <Label>Conversion Primary</Label>
                    <SingleSelect
                      options={[
                        { label: "Ads Platform", value: "Ads Platform" },
                        { label: "Web Analytics", value: "Web Analytics" },
                        { label: "Mobile Measurement", value: "Mobile Measurement" },
                        { label: "Ad Server", value: "Ad Server" },
                        { label: "Multi-Touch Attribution", value: "Multi-Touch Attribution" },
                        { label: "E-Commerce", value: "E-Commerce" },
                        { label: "Internal Client Source", value: "Internal Client Source" },
                        { label: "Other Platform", value: "Other Platform" },
                      ]}
                      defaultValue={editConversionSourceOfTruth}
                      onChange={(value) => setEditConversionSourceOfTruth(value)}
                      disabled={!superAdmin}
                      placeholder="Select Conversion Primary"
                      containerClassName="w-full"
                          />
                  </div>
                </div>

                {/* Upload Fiscal Calendar - opens in drawer */}
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={handleClickFiscalEditDialogOpen}
                    className="w-full"
                  >
                    Upload Fiscal Calendar
                  </Button>
                </div>

                {/* Brand Features Checkboxes */}
                <div className="space-y-2">
                  <Label>Brand Features</Label>
                  <div className="flex flex-wrap items-center gap-6">
                    <div className="flex items-center gap-2">
                      <LiftCheckbox
                        id="edit-affiliate-checkbox"
                            checked={selectedEditOptions.includes("Affiliate")}
                        onCheckedChange={(checked) => {
                          handleSetEditAdditionalOptns({ target: { name: "Affiliate", checked } } as any);
                        }}
                      />
                      <Label htmlFor="edit-affiliate-checkbox" className="font-normal cursor-pointer">Affiliate</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <LiftCheckbox
                        id="edit-performance-checkbox"
                        checked={selectedEditOptions.includes("Performance")}
                        onCheckedChange={(checked) => {
                          handleSetEditAdditionalOptns({ target: { name: "Performance", checked } } as any);
                        }}
                      />
                      <Label htmlFor="edit-performance-checkbox" className="font-normal cursor-pointer">Performance</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <LiftCheckbox
                        id="edit-prospect-checkbox"
                        checked={selectedEditOptions.includes("Prospect")}
                        onCheckedChange={(checked) => {
                          handleSetEditAdditionalOptns({ target: { name: "Prospect", checked } } as any);
                        }}
                      />
                      <Label htmlFor="edit-prospect-checkbox" className="font-normal cursor-pointer">Prospect</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <LiftCheckbox
                        id="edit-creative-checkbox"
                        checked={selectedEditOptions.includes("Creative")}
                        onCheckedChange={(checked) => {
                          handleSetEditAdditionalOptns({ target: { name: "Creative", checked } } as any);
                        }}
                      />
                      <Label htmlFor="edit-creative-checkbox" className="font-normal cursor-pointer">Creative</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <LiftCheckbox
                        id="edit-acorn-checkbox"
                        checked={selectedEditOptions.includes("Acorn")}
                        onCheckedChange={(checked) => {
                          handleSetEditAdditionalOptns({ target: { name: "Acorn", checked } } as any);
                        }}
                      />
                      <Label htmlFor="edit-acorn-checkbox" className="font-normal cursor-pointer">
                        {ACORN_BRAND_FEATURE_LABEL}
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <LiftCheckbox
                        id="edit-performance-creator-checkbox"
                        checked={editIsPerformanceCreator}
                        onCheckedChange={(checked) => {
                          setEditIsPerformanceCreator(checked as boolean);
                        }}
                      />
                      <Label htmlFor="edit-performance-creator-checkbox" className="font-normal cursor-pointer">Performance Creator</Label>
                    </div>
                  </div>
                </div>

                {/* HubSpot Company ID Input */}
                <div className="space-y-2">
                  <Label>
                    HubSpot Company ID{editIsPerformanceCreator ? " *" : ""}
                  </Label>
                  <Input
                    placeholder="Enter HubSpot Company ID"
                    value={editHubspotCompanyId}
                    onChange={(e) => setEditHubspotCompanyId(e.target.value)}
                    maxLength={100}
                    aria-required={editIsPerformanceCreator}
                  />
                  <p
                    className={`text-xs ${
                      editIsPerformanceCreator && !editHubspotCompanyId.trim()
                        ? "text-red-500"
                        : "text-muted-foreground"
                    }`}
                  >
                    {editIsPerformanceCreator
                      ? "Required for Performance Creator brands."
                      : "Optional. Max 100 characters."}
                  </p>
                </div>

                {shouldShowGrapevineFields(editIsPerformanceCreator) && (
                  <div className="space-y-4 rounded-md border p-3">
                    {editGvLoading && (
                      <p className="text-sm text-muted-foreground">
                        Loading live Grapevine details...
                      </p>
                    )}
                    {editGvError && (
                      <p className="text-sm text-red-500" role="alert">
                        {editGvError}
                      </p>
                    )}
                    <div className="space-y-2">
                      <Label>Grapevine Slug</Label>
                      <Input
                        placeholder="brand-slug"
                        value={editGvSlug}
                        onChange={(e) => setEditGvSlug(e.target.value)}
                        maxLength={150}
                      />
                      <GrapevineSlugAvailabilityMessage
                        status={editGvSlugStatus}
                        slug={editGvSlug}
                        resolvedSlug={editGvResolvedSlug}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Grapevine Vertical *</Label>
                      <Input
                        placeholder="e.g. Beverages"
                        value={editGvVertical}
                        onChange={(e) => setEditGvVertical(e.target.value)}
                        maxLength={100}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Name</Label>
                      <Input
                        placeholder="Grapevine contact name"
                        value={editGvContactName}
                        onChange={(e) => setEditGvContactName(e.target.value)}
                        maxLength={150}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Email *</Label>
                      <Input
                        placeholder="contact@brand.com"
                        value={editGvContactEmail}
                        onChange={(e) => setEditGvContactEmail(e.target.value)}
                        maxLength={150}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Homepage URL</Label>
                      <Input
                        placeholder="https://brand.com"
                        value={editGvHomepageUrl}
                        onChange={(e) => setEditGvHomepageUrl(e.target.value)}
                        maxLength={255}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Brand Tier</Label>
                      <Input
                        placeholder="e.g. Normal"
                        value={editGvTier}
                        onChange={(e) => setEditGvTier(e.target.value)}
                        maxLength={100}
                      />
                    </div>
                  </div>
                )}

                {/* Info Message - only when Affiliate (Affluent) is checked */}
                {selectedEditOptions.includes("Affiliate") && (
                  <div className="text-sm text-gray-600 text-center">
                    Make sure the brand name matches with Affluent!
                  </div>
                )}
                </div>
              {/* End of lift-ui form section */}

              {/* Remaining MUI section for tabs - kept as-is due to complexity */}
              <Box sx={{ px: 2, pb: 2 }}>
            <ThemeProvider theme={theme}>
              {false && selectedEditOptions.length > 0 && (
                <hr style={{ width: "100%", margin: "17px 0" }} />
              )}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  flexDirection: "column",
                  gap: "30px",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  {/* NEW CONTENT GOES HERE */}
                  {/* <button > */}
                  {isOverflowingAdd && (
                    <ChevronLeft
                      onClick={() => moveScroll(0)}
                      className="scroll-btn"
                    />
                  )}
                  {/* </button> */}
                  <div
                    ref={scrollHeader}
                    className="add-header"
                    style={{
                      position: "relative",
                      width: "1050px",
                      display: "flex",
                      justifyContent: isOverflowingAdd
                        ? "flex-start"
                        : "center",
                      alignItems: "center",
                      overflow: "auto",
                      gap: "30px",
                      margin: "10px 0 20px 0",
                    }}
                  >
                    {false && selectedEditOptions.includes("Affiliate") && (
                      <div
                        className={
                          selectedItem == 1
                            ? "add-header-content active"
                            : "add-header-content"
                        }
                        id="1"
                        onClick={(e: any) => setSelectedItem(e.target.id)}
                      >
                        Affiliate
                      </div>
                    )}

                    {isOverflowingAdd && (
                      <ChevronRight
                        onClick={() => moveScroll(1)}
                        className="scroll-btn"
                      />
                    )}
                  </div>
                </div>
              </div>
              {false && selectedItem == 1 && selectedEditOptions.length > 0 && (
                <>
                  <div
                    className="affiliate-wrapper"
                    style={{
                      display: "flex",
                      marginTop: "20px",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Label className="font-normal">Want to link GA4?</Label>
                      <LiftSwitch
                        checked={isGA4Active}
                        onCheckedChange={() => setIsGA4Active(!isGA4Active)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Affiliate Email Alias</Label>
                      <Input
                        disabled={!superAdmin}
                        placeholder="Eg. - lift@newengen.com"
                        value={editAffiliateAlias || ""}
                        onChange={(e) => setEditAffiliateAlias(e.target.value)}
                        className="w-full max-w-[25ch]"
                      />
                    </div>

                    {isGA4Active && (
                      <div style={{ marginTop: "20px" }}>
                        {editGa4Rows.map((row: any, rowIndex: number) => (
                          <div
                            key={rowIndex}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "15px",
                              paddingTop: "15px",
                              paddingBottom: "15px",
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px",
                              backgroundColor: "#fafafa",
                              flexWrap: "nowrap",
                              overflowX: "auto",
                              width: "100%",
                            }}
                          >
                            <div className="space-y-1 min-w-[160px] shrink-0">
                              <Label className="text-xs">GA4 Account Name</Label>
                              <Input
                                disabled={!superAdmin}
                                value={row.accountName}
                                onChange={(e) =>
                                  updateEditGA4Row(
                                    rowIndex,
                                    "accountName",
                                    e.target.value
                                  )
                                }
                                className="w-full"
                              />
                            </div>
                            <div className="space-y-1 min-w-[160px] shrink-0">
                              <Label className="text-xs">GA4 Account ID</Label>
                              <Input
                                type="number"
                                disabled={!superAdmin}
                                value={row.accountId}
                                onChange={(e) =>
                                  updateEditGA4Row(
                                    rowIndex,
                                    "accountId",
                                    e.target.value
                                  )
                                }
                                className="w-full"
                              />
                            </div>
                            <div className="space-y-1 min-w-[160px] shrink-0">
                              <Label className="text-xs">GA4 Property ID *</Label>
                              <Input
                                type="number"
                                disabled={!superAdmin}
                                value={row.propertyId}
                                onChange={(e) =>
                                  updateEditGA4Row(
                                    rowIndex,
                                    "propertyId",
                                    e.target.value
                                  )
                                }
                                className="w-full"
                              />
                            </div>
                            <div className="space-y-1 min-w-[160px] shrink-0">
                              <Label className="text-xs">GA4 Property Name</Label>
                              <Input
                                disabled={!superAdmin}
                                value={row.propertyName}
                                onChange={(e) =>
                                  updateEditGA4Row(
                                    rowIndex,
                                    "propertyName",
                                    e.target.value
                                  )
                                }
                                className="w-full"
                              />
                            </div>
                            <div className="space-y-1 min-w-[160px] shrink-0">
                              <Label className="text-xs">GA4 source</Label>
                              <Input
                                disabled={!superAdmin}
                                value={row.source}
                                onChange={(e) =>
                                  updateEditGA4Row(
                                    rowIndex,
                                    "source",
                                    e.target.value
                                  )
                                }
                                className="w-full"
                              />
                            </div>

                            <div
                              className="multiple-medium"
                              style={{ minWidth: "180px", flexShrink: 0 }}
                            >
                              {row.medValues?.map(
                                (item: any, mediumIndex: number) => {
                                  return (
                                    <button
                                      key={mediumIndex}
                                      onClick={(e) => e.preventDefault()}
                                      style={{
                                        margin: "2px",
                                        padding: "4px 8px",
                                        backgroundColor: "#16306D",
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                      }}
                                    >
                                      {item}{" "}
                                      <span
                                        onClick={(e: any) => {
                                          handleRemoveMediumForEditRow(
                                            e,
                                            rowIndex,
                                            mediumIndex
                                          );
                                        }}
                                        style={{
                                          cursor: "pointer",
                                          marginLeft: "4px",
                                        }}
                                      >
                                        ×
                                      </span>
                                    </button>
                                  );
                                }
                              )}

                              <input
                                onKeyDown={(e: any) =>
                                  handleAddMultipleMediumForEditRow(e, rowIndex)
                                }
                                style={{
                                  margin: "0",
                                  backgroundColor: "transparent",
                                  width: "120px",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  padding: "4px 6px",
                                  fontSize: "11px",
                                }}
                                placeholder="Medium + Enter"
                                value={row.singleMedValue}
                                onChange={(event) =>
                                  updateEditGA4Row(
                                    rowIndex,
                                    "singleMedValue",
                                    event.target.value
                                  )
                                }
                              />
                            </div>

                            {editGa4Rows.length > 1 && (
                              <div style={{ paddingRight: "100px" }}>
                                <button
                                  onClick={() => removeEditGA4Row(rowIndex)}
                                  style={{
                                    backgroundColor: "#f44336",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    fontSize: "11px",
                                    flexShrink: 0,
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>
                        ))}

                        <button
                          onClick={addNewEditGA4Row}
                          style={{
                            backgroundColor: "#4caf50",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding: "10px 16px",
                            cursor: "pointer",
                            fontSize: "14px",
                            marginTop: "10px",
                          }}
                        >
                          + Add New GA4 Row
                        </button>
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "20px",
                      margin: "50px",
                    }}
                  >
                    <div>
                      {/* <TableContainer
                        onClick={() => setScrapDialogOpen(true)}
                        component={Paper}
                        sx={{
                          maxHeight: "500px",
                          overflow: "auto",
                          cursor: "pointer",
                        }}
                      > */}
                      {/* <Table
                          sx={{ minWidth: "100px", maxWidth: "300px" }}
                          aria-label="simple table"
                          stickyHeader
                        > */}
                      {/* <MuiTableHead>
                            <MuiTableRow>
                              <TableCell
                                colSpan={2}
                                sx={{
                                  textAlign: "center",
                                  fontWeight: "900",
                                }}
                              >
                                Scrapping Urls
                              </MuiTableCell>
                            </MuiTableRow>
                          </MuiTableHead> */}
                      {/* <MuiTableHead>
                            <MuiTableRow>
                              <MuiTableCell sx={{ fontWeight: "900" }}>
                                Platform ( Name ){" "}
                              </MuiTableCell>
                              <TableCell
                                sx={{ fontWeight: "900" }}
                                align="right"
                              >
                                Url
                              </MuiTableCell>
                            </MuiTableRow>
                          </MuiTableHead> */}
                      {/* <MuiTableBody>
                            {scrapData?.length === 0 && (
                              <TableRow
                                sx={{
                                  fontFamily: '"Figtree", sans-serif',
                                  fontSize: "0.8rem",
                                  color: "#16306D",
                                }}
                              >
                                <TableCell
                                  colSpan={2}
                                  sx={{ textAlign: "center" }}
                                >
                                  No Data is present
                                  <br /> <b>Click to Add</b>
                                </MuiTableCell>
                              </MuiTableRow>
                            )}
                            {scrapData?.length > 0 &&
                              scrapData.map((key: any, index: any) => {
                                return (
                                  <TableRow
                                    key={index}
                                    sx={{
                                      "&:last-child td, &:last-child th": {
                                        border: 0,
                                      },
                                    }}
                                  >
                                    <MuiTableCell component="th" scope="row">
                                      {key.platform}
                                    </MuiTableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ width: "300px !important" }}
                                    >
                                      {key.url}
                                    </MuiTableCell>
                                  </MuiTableRow>
                                );
                              })}
                          </MuiTableBody> */}
                      {/* </MuiTable> */}
                      {/* </TableContainer> */}
                    </div>
                  </div>
                </>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                  flexDirection: "column",

                  gap: "20px",
                  width: "100%",
                }}
              >
                {editFiscalCalendar?.length > 0 && (
                  <>
                    <TableContainer
                      component={Paper}
                      sx={{
                        maxHeight: 400,
                        maxWidth: 1110,
                        marginTop: "60px",

                        cursor: "pointer",
                      }}
                    >
                      <MuiTable
                        sx={{ minWidth: 100 }}
                        aria-label="fiscal calendar table"
                        stickyHeader
                      >
                        <MuiTableHead>
                          <StyledTableCell
                            colSpan={6}
                            sx={{
                              textAlign: "center",
                              fontSize: "27px",
                              fontFamily: "Gibbson sans-serif",
                              fontWeight: "700",
                            }}
                          >
                            Fiscal Calendar
                          </StyledTableCell>
                        </MuiTableHead>
                        <MuiTableHead>
                          <StyledTableRow>
                            {/* {addFiscalCalendar?.length > 0 &&
                            Object.keys(addFiscalCalendar[0])?.map(
                              (header: any) => (
                                <StyledTableCell key={header}>
                                  {header}
                                </StyledTableCell>
                              )
                            )} */}
                            <StyledTableCell>Id</StyledTableCell>
                            <StyledTableCell>Year</StyledTableCell>
                            <StyledTableCell>Month</StyledTableCell>
                            <StyledTableCell>Quarter</StyledTableCell>
                            <StyledTableCell>Month End Date</StyledTableCell>
                            <StyledTableCell>Month Start Date</StyledTableCell>
                          </StyledTableRow>
                        </MuiTableHead>
                        <MuiTableBody>
                          {editFiscalCalendar?.map((data: any, index: any) => (
                            <StyledTableRow key={index}>
                              <StyledTableCell>{index + 1}</StyledTableCell>
                              <StyledTableCell>{data.year}</StyledTableCell>
                              <StyledTableCell>{data.month}</StyledTableCell>
                              <StyledTableCell>{data.quarter}</StyledTableCell>
                              <StyledTableCell>
                                {data.month_end_date}
                              </StyledTableCell>
                              <StyledTableCell>
                                {data.month_start_date}
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                        </MuiTableBody>
                      </MuiTable>
                    </TableContainer>

                    <div
                      style={{
                        display: "flex",
                        gap: "15px",
                      }}
                    >
                      {/* {editFiscalCalendar && (
                      <span
                        style={{
                          display: "flex",
                          cursor: "pointer",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: "2px",
                        }}
                        onClick={handleResetFiscalClicked}
                      >
                        <RotateLeftIcon fontSize="inherit" /> Reset
                      </span>
                    )} */}

                      <span
                        style={{
                          display: "flex",
                          cursor: "pointer",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: "2px",
                        }}
                        onClick={handleDownloadFiscalTemplate}
                      >
                        <FileDownloadOutlinedIcon fontSize="inherit" /> Template
                      </span>
                    </div>
                  </>
                )}
              </div>
            </ThemeProvider>

            {/* Additional Fields */}
            <div className="flex items-center gap-2 px-4 pb-2">
              <Label className="font-normal">Additional Brand Details</Label>
              <LiftSwitch
                checked={showAdditionalFields}
                onCheckedChange={(checked) => setShowAdditionalFields(!!checked)}
              />
            </div>
            {showAdditionalFields && (
              <div
                ref={additionalFieldsRef}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 w-full max-w-full"
              >
                <p className="text-lg font-semibold sm:col-span-2">Additional Brand Details</p>
                <div className="space-y-2">
                  <Label>SOW Link</Label>
                  <Input
                    value={editSOWLink || ""}
                    onChange={(e) => setEditSOWLink(e.target.value)}
                    placeholder="SOW Link"
                    className="w-full min-w-0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>MSA Link</Label>
                  <Input
                    value={editMSALink || ""}
                    onChange={(e) => setEditMSALink(e.target.value)}
                    placeholder="MSA Link"
                    className="w-full min-w-0"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2 min-w-0">
                  <LiftTextarea
                    label="Contract Terms"
                    value={editContractTerms || ""}
                    onChange={(e) => setEditContractTerms(e.target.value)}
                    placeholder="Contract Terms"
                    rows={3}
                    containerClassName="!left-0 !top-0 w-full max-w-full min-w-0"
                    fieldClassName="w-full max-w-full min-w-0"
                  />
                </div>
                <div className="space-y-2 min-w-0">
                  <Label>Director</Label>
                  <SingleSelect
                    options={allUsers.map((u: any) => ({
                      label: u.name,
                      value: String(u.id),
                    }))}
                    defaultValue={editDirectorID ? String(editDirectorID) : undefined}
                    onChange={(value) => setEditDirectorID(value || "")}
                    placeholder="Select Director"
                    containerClassName="w-full min-w-0"
                  />
                </div>
                <div className="space-y-2 min-w-0">
                  <Label>Manager</Label>
                  <SingleSelect
                    options={allUsers.map((u: any) => ({
                      label: u.name,
                      value: String(u.id),
                    }))}
                    defaultValue={editManagerID ? String(editManagerID) : undefined}
                    onChange={(value) => setEditManagerID(value || "")}
                    placeholder="Select Manager"
                    containerClassName="w-full min-w-0"
                  />
                </div>
                <div className="space-y-2 min-w-0">
                  <Label>Associate 1</Label>
                  <SingleSelect
                    options={allUsers.map((u: any) => ({
                      label: u.name,
                      value: String(u.id),
                    }))}
                    defaultValue={editAssociateID1 ? String(editAssociateID1) : undefined}
                    onChange={(value) => setEditAssociateID1(value || "")}
                    placeholder="Select Associate 1"
                    containerClassName="w-full min-w-0"
                  />
                </div>
                <div className="space-y-2 min-w-0">
                  <Label>Associate 2</Label>
                  <SingleSelect
                    options={allUsers.map((u: any) => ({
                      label: u.name,
                      value: String(u.id),
                    }))}
                    defaultValue={editAssociateID2 ? String(editAssociateID2) : undefined}
                    onChange={(value) => setEditAssociateID2(value || "")}
                    placeholder="Select Associate 2"
                    containerClassName="w-full min-w-0"
                  />
                </div>
                <div className="space-y-2 min-w-0">
                  <Label>Operations Manager</Label>
                  <SingleSelect
                    options={allUsers.map((u: any) => ({
                      label: u.name,
                      value: String(u.id),
                    }))}
                    defaultValue={editOpsID ? String(editOpsID) : undefined}
                    onChange={(value) => setEditOpsID(value || "")}
                    placeholder="Select Operations Manager"
                    containerClassName="w-full min-w-0"
                  />
                </div>
                <div className="space-y-2 min-w-0">
                  <Label>Date Joined</Label>
                  <LiftDatePicker
                    value={editDateJoined ? dayjs(editDateJoined).toDate() : undefined}
                    onChange={(date) => setEditDateJoined(date ? dayjs(date) : null)}
                    className="w-full min-w-0"
                  />
                </div>
              </div>
            )}
            </Box>
              </div>
            <SheetFooter className="border-t pt-4 mt-4">
            <Button
                variant="outline"
                onClick={() => {
                  setShowEditBrandDialog(false);
                  setShowAdditionalFields(false);
                  setHideEditBrandProgressDialog(true);
                  setAddFiscalCalendar(null);
                  resetEditFormFields();
                }}
              >
                Cancel
              </Button>
              <Button
              onClick={() => {
                if (hideEditBrandProgressDialog) {
                  updateBrand();
                }
              }}
                disabled={!hideEditBrandProgressDialog}
            >
                {hideEditBrandProgressDialog ? (
                  "Update"
                ) : (
                  <SpinnerLoader size={16} />
                )}
            </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Dialog for Brand Goals */}
        <Dialog
          maxWidth="md"
          open={brandGoalDialogOpen}
          onClose={handleCloseBrandGoalDialog}
        >
          <DialogTitle>Brand Goals Metrics</DialogTitle>
          <DialogContent>
            <Box
              noValidate
              component="form"
              sx={{
                display: "flex",
                flexDirection: "column",
                m: "auto",
                width: "fit-content",
              }}
            >
              <div className="bg-list">
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  <div className="bg-revenue">
                    <TextField
                      required
                      type="number"
                      size="small"
                      label="revenue"
                      id="revenue"
                      value={brandGoals[0].revenue}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                  <div className="bg-spend">
                    <TextField
                      required
                      size="small"
                      label="spend"
                      id="spend"
                      type="number"
                      value={brandGoals[0].spend}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                  <div className="bg-comm_rate">
                    <TextField
                      required
                      size="small"
                      label="comm_rate"
                      id="comm_rate"
                      type="number"
                      value={brandGoals[0].comm_rate}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                  <div className="bg-conv_rate">
                    <TextField
                      required
                      size="small"
                      label="conv_rate"
                      id="conv_rate"
                      type="number"
                      value={brandGoals[0].conv_rate}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                  <div className="bg-clicks">
                    <TextField
                      required
                      size="small"
                      label="clicks"
                      id="clicks"
                      type="number"
                      value={brandGoals[0].clicks}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                  <div className="bg-actions">
                    <TextField
                      required
                      size="small"
                      label="actions"
                      id="actions"
                      type="number"
                      value={brandGoals[0].actions}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>

                  <div className="bg-cac">
                    <TextField
                      required
                      size="small"
                      label="cac"
                      id="cac"
                      type="number"
                      value={brandGoals[0].cac}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>

                  <div className="bg-discount">
                    <TextField
                      required
                      size="small"
                      id="discount"
                      label="discount"
                      type="number"
                      value={brandGoals[0].discount}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                  <div className="bg-aov">
                    <TextField
                      required
                      size="small"
                      label="aov"
                      id="aov"
                      type="number"
                      value={brandGoals[0].aov}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                  <div className="bg-roas">
                    <TextField
                      required
                      size="small"
                      label="roas"
                      id="roas"
                      type="number"
                      value={brandGoals[0].roas}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                  <div className="bg-new_user">
                    <TextField
                      required
                      type="number"
                      size="small"
                      label="new_user"
                      id="new_user"
                      value={brandGoals[0].new_user}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                  <div className="bg-canc">
                    <TextField
                      required
                      size="small"
                      label="canc"
                      id="canc"
                      type="number"
                      value={brandGoals[0].canc}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                  <div className="bg-new_rev">
                    <TextField
                      required
                      size="small"
                      label="new_rev"
                      id="new_rev"
                      type="number"
                      value={brandGoals[0].new_rev}
                      // helperText="Eg. - LT Partners"
                      // value={addBrandName}
                      onChange={(event) => handleChangeBrandGoals(event)}
                    />
                  </div>
                </div>
              </div>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseBrandGoalDialog}
              sx={{ color: "#16306D" }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Brand Weights */}
        <Dialog
          maxWidth="sm"
          open={brandWeightDialogOpen}
          onClose={handleCloseBrandWeightDialog}
        >
          <DialogTitle>Brand Weights Metrics</DialogTitle>
          <DialogContent>
            <Box
              noValidate
              component="form"
              sx={{
                display: "flex",
                flexDirection: "column",
                m: "auto",
                width: "fit-content",
              }}
            >
              <div className="bw-list">
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div className="bw-revenue">
                    <TextField
                      required
                      type="number"
                      size="small"
                      label="revenue"
                      id="revenue"
                      // helperText="Eg. - LT Partners"
                      value={brandWeights[0].revenue}
                      onChange={(event) => handleChangeBrandWeights(event)}
                    />
                  </div>
                  <div className="bw-new_user">
                    <TextField
                      required
                      type="number"
                      size="small"
                      label="new_user"
                      id="new_user"
                      // helperText="Eg. - LT Partners"
                      value={brandWeights[0].new_user}
                      onChange={(event) => handleChangeBrandWeights(event)}
                    />
                  </div>
                  <div className="bw-new_v_existing">
                    <TextField
                      required
                      size="small"
                      label="new_v_existing"
                      type="number"
                      id="new_v_existing"
                      // helperText="Eg. - LT Partners"
                      value={brandWeights[0].new_v_existing}
                      onChange={(event) => handleChangeBrandWeights(event)}
                    />
                  </div>
                  <div className="bw-ecpc">
                    <TextField
                      required
                      size="small"
                      label="ecpc"
                      type="number"
                      id="ecpc"
                      // helperText="Eg. - LT Partners"
                      value={brandWeights[0].ecpc}
                      onChange={(event) => handleChangeBrandWeights(event)}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div className="bw-conv_rate">
                    <TextField
                      required
                      size="small"
                      label="conv_rate"
                      type="number"
                      id="conv_rate"
                      // helperText="Eg. - LT Partners"
                      value={brandWeights[0].conv_rate}
                      onChange={(event) => handleChangeBrandWeights(event)}
                    />
                  </div>
                  <div className="bw-click">
                    <TextField
                      required
                      size="small"
                      label="click"
                      type="number"
                      id="click"
                      // helperText="Eg. - LT Partners"
                      value={brandWeights[0].click}
                      onChange={(event) => handleChangeBrandWeights(event)}
                    />
                  </div>
                  <div className="bw-conversions">
                    <TextField
                      required
                      size="small"
                      label="conversions"
                      type="number"
                      id="conversions"
                      // helperText="Eg. - LT Partners"
                      value={brandWeights[0].conversions}
                      onChange={(event) => handleChangeBrandWeights(event)}
                    />
                  </div>

                  <div className="bw-aov">
                    <TextField
                      required
                      size="small"
                      label="aov"
                      type="number"
                      id="aov"
                      // helperText="Eg. - LT Partners"
                      value={brandWeights[0].aov}
                      onChange={(event) => handleChangeBrandWeights(event)}
                    />
                  </div>
                </div>
              </div>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseBrandWeightDialog}
              sx={{ color: "#16306D" }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Scraping urls */}
        <Dialog
          fullWidth
          maxWidth="md"
          open={scrapDialogOpen}
          onClose={handleCloseScrapDialog}
        >
          <DialogTitle
            sx={{
              fontFamily: '"Figtree", sans-serif',
              fontSize: "1.2rem",
              fontWeight: "700",
            }}
          >
            Scraping Urls
          </DialogTitle>
          <DialogContent>
            <Box
              noValidate
              component="form"
              sx={{
                display: "flex",
                flexDirection: "column",
                m: "20px",
                gap: "20px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ThemeProvider theme={theme}>
                {scrapData?.length === 0 && (
                  <>
                    <div className="no-scrap-wrapper">
                      <div className="no-scrap-content">
                        <span>
                          There are no Scraping urls added, <br />
                          click on <b>Add button</b> below to add one
                        </span>
                      </div>
                      <Button
                        style={{ margin: "0" }}
                        variant="contained"
                        sx={{
                          border: "1px solid rgba(0,0,0,0.25)",
                          width: "22ch",
                          height: "40px",
                          textTransform: "none",
                          color: "white",
                          backgroundColor:
                            "#16306D" /* Original background color */,
                          fontSize: "16px",
                          "&:hover": {
                            /* Hover styles */
                            backgroundColor:
                              "rgb(22, 48, 109,0.9)" /* New background color on hover */,
                          },
                        }}
                        onClick={handleAddScrapItem}
                      >
                        Add
                      </Button>
                    </div>
                  </>
                )}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "50px",
                    fontFamily: '"Gibbson" sans-serif',
                  }}
                >
                  {scrapData?.length > 0 &&
                    scrapData.map((item: any, index: any) => {
                      return (
                        <div className="scrap-list">
                          <>
                            <FormControl>
                              <InputLabel id="scrap-dropdown">
                                Select Platform
                              </InputLabel>
                              <Select
                                labelId="scrap-dropdown"
                                disabled={
                                  item.platform !== "" &&
                                  item.id === index + 1 &&
                                  !(editScrapItem?.id === item.id)
                                    ? true
                                    : false
                                }
                                id="platform"
                                name="platform"
                                value={
                                  item.platform === "" && item.id === index + 1
                                    ? changeScrapItem.platform
                                    : item.platform !== "" &&
                                      item.id === index + 1 &&
                                      editScrapItem?.id === item.id
                                    ? editScrapItem.platform
                                    : item.platform
                                }
                                onChange={(e) => {
                                  if (item.platform === "") {
                                    handleChangeScrapData(e);
                                  } else {
                                    handleChangeEditScrapItem(e);
                                  }
                                }}
                                label="Select Platform"
                                style={{
                                  width: "300px",
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      width: "300px",
                                    },
                                  },
                                }}
                              >
                                {scrapPlatforms.map((option: any) => (
                                  <MenuItem
                                    key={option.name}
                                    value={option.name}
                                  >
                                    {option.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                              }}
                            >
                              <TextField
                                disabled={
                                  item.url !== "" &&
                                  item.id === index + 1 &&
                                  !(editScrapItem?.id === item.id)
                                    ? true
                                    : false
                                }
                                required
                                size="medium"
                                label="Url"
                                id="url"
                                value={
                                  item.url === "" && item.id === index + 1
                                    ? changeScrapItem.url
                                    : item.url !== "" &&
                                      item.id === index + 1 &&
                                      editScrapItem?.id === item.id
                                    ? editScrapItem.url
                                    : item.url
                                }
                                onChange={(event) => {
                                  if (item.url === "") {
                                    handleChangeScrapData(event);
                                  } else {
                                    handleChangeEditScrapItem(event);
                                  }
                                }}
                              />
                              {isAddIconClicked &&
                                !isCorrect &&
                                (changeScrapItem.id === item.id ||
                                  editScrapItem?.id === item.id) && (
                                  <span
                                    style={{
                                      fontFamily: '"Figtree", sans-serif',
                                      fontSize: "0.8rem",
                                      color: "red",
                                    }}
                                  >
                                    Url must be https://www.{scrapDomain}
                                    /dummy_slug
                                  </span>
                                )}
                            </div>

                            <div
                              style={{
                                display: "flex",
                                gap: "20px",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "50px",
                              }}
                            >
                              {item.id === index + 1 &&
                              (item.platform === "" || item.url === "") ? (
                                <AddIcon
                                  sx={{ cursor: "pointer" }}
                                  onClick={() =>
                                    handleAddItemToScrapData(index)
                                  }
                                />
                              ) : !(editScrapItem?.id === item.id) &&
                                item.id === index + 1 ? (
                                <ModeEditOutlinedIcon
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => handleEditScrapItem(item)}
                                />
                              ) : (
                                <CheckIcon
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => handleSaveEditScrapItem(index)}
                                />
                              )}

                              <DeleteOutlineIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() =>
                                  handleDeleteScrapItem(item, index)
                                }
                              />
                            </div>
                          </>
                        </div>
                      );
                    })}

                  {/* {scrapData.length > 0 &&
                  editScrapItem &&
                  scrapData.map((item: any, index: any) => {
                    return (
                      <div className="scrap-list">
                        <>
                          <FormControl>
                            <InputLabel id="scrap-dropdown">
                              Select Platform
                            </InputLabel>
                            <Select
                              labelId="scrap-dropdown"
                              disabled={
                                item.platform !== "" &&
                                item.id === index + 1 &&
                                !(editScrapItem.id === item.id)
                                  ? true
                                  : false
                              }
                              id="platform"
                              name="platform"
                              value={
                                editScrapItem.id === item.id &&
                                item.id === index + 1
                                  ? editScrapItem.platform
                                  : item.platform === "" && item.id === index+1 && changeScrapItem.id === item.id? changeScrapItem.platform : item.platform
                              }
                              onChange={(e) => handleChangeEditScrapItem(e)}
                              label="Select Platform"
                              style={{
                                width: "300px",
                              }}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    width: "300px",
                                  },
                                },
                              }}
                            >
                              {scrapPlatforms.map((option: any) => (
                                <MenuItem key={option.name} value={option.name}>
                                  {option.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "10px",
                            }}
                          >
                            <TextField
                              disabled={
                                item.url !== "" &&
                                item.id === index + 1 &&
                                !(editScrapItem.id === item.id)
                                  ? true
                                  : false
                              }
                              required
                              size="medium"
                              label="Url"
                              id="url"
                              // helperText="Eg. - LT Partners"
                              value={
                                editScrapItem.id === item.id &&
                                item.id === index + 1
                                  ? editScrapItem.url
                                  : item.url === "" && item.id === index+1 && changeScrapItem.id === item.id ? changeScrapItem.url : item.url
                              }
                              onChange={(event) =>
                                handleChangeEditScrapItem(event)
                              }
                            />
                            {isAddIconClicked &&
                              !isCorrect &&
                              item.id === index + 1 &&
                              (changeScrapItem.id === item.id ||
                                editScrapItem?.id === item.id) && (
                                <span
                                  style={{ color: "red", fontSize: "14px" }}
                                >
                                  Domain should be {scrapDomain}
                                </span>
                              )}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "20px",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "50px",
                            }}
                          >
                            {item.id === index + 1 &&
                            (item.platform === "" || item.url === "") ? (
                              <AddIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleAddItemToScrapData(index)}
                              />
                            ) : !(editScrapItem.id === item.id) &&
                              item.id === index + 1 ? (
                              <ModeEditOutlinedIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleEditScrapItem(item)}
                              />
                            ) : (
                              <CheckIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleSaveEditScrapItem(index)}
                              />
                            )}

                            <DeleteOutlineIcon
                              sx={{ cursor: "pointer" }}
                              onClick={() => handleDeleteScrapItem(index)}
                            />
                          </div>
                        </>
                      </div>
                    );
                  })} */}
                </div>
              </ThemeProvider>
            </Box>
          </DialogContent>
          <div
            style={{
              color: "#16306D",

              cursor: "pointer",
              fontSize: "40px",
              textAlign: "center",
              margin: "20px",
            }}
          >
            {scrapData?.length > 0 && (
              <Button
                style={{ margin: "0" }}
                variant="contained"
                sx={{
                  border: "1px solid rgba(0,0,0,0.25)",
                  width: "22ch",
                  height: "40px",
                  textTransform: "none",

                  fontFamily: '"Figtree", sans-serif',

                  color: "white",
                  backgroundColor: "#16306D" /* Original background color */,
                  fontSize: "16px",
                  "&:hover": {
                    /* Hover styles */
                    backgroundColor:
                      "rgb(22, 48, 109,0.9)" /* New background color on hover */,
                  },
                }}
                onClick={handleAddScrapItem}
              >
                Add
              </Button>
            )}
          </div>

          <DialogActions>
            <Button
              onClick={handleCloseScrapDialog}
              style={{
                fontFamily: '"Figtree", sans-serif',
                fontSize: "0.8rem",
                color: "#16306D",
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sheet for ADD performance */}
        <Sheet
          open={addDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              dispatch(setAddDialogOpen(false));
              setAddPerfCurrentStep(1);
              setAddPerfCompletedSteps([]);
            }
          }}
        >
          <SheetContent className="sm:max-w-xl flex flex-col" onInteractOutside={(e) => e.preventDefault()}>
            <SheetHeader>
              <AddPerformanceStepHeader
                currentStep={addPerfCurrentStep}
                completedSteps={addPerfCompletedSteps}
                onStepClick={(step) => addPerfFormRef.current?.handleStepClick(step)}
              />
            </SheetHeader>
            <AddPerformance
              ref={addPerfFormRef}
              calledFromWhichComponent="Brands"
              brands={brandsBackup}
              onStepChange={(step, completedSteps) => {
                setAddPerfCurrentStep(step);
                setAddPerfCompletedSteps(completedSteps);
              }}
              onClose={() => {
                dispatch(setAddDialogOpen(false));
                setAddPerfCurrentStep(1);
                setAddPerfCompletedSteps([]);
              }}
              isOpen={addDialogOpen}
            />
          </SheetContent>
        </Sheet>

        {/* Dialog for edit Performance */}
        <EditPerformance calledFromWhichComponent={"Brands"} />

        {/* Drawer for Add Fiscal Calendar (nested, like naming taxonomy) - no overlay to avoid double tint */}
        <FiscalSheet
          open={uploadFiscalDialogOpen}
          onOpenChange={(open) => { if (!open) handleClickFiscalDialogClose(); }}
        >
          <FiscalSheetContent
            side="right"
            hideOverlay
            className="w-[90vw] sm:max-w-[700px] flex flex-col max-h-[100vh] overflow-hidden"
          >
            <FiscalSheetHeader>
              <FiscalSheetTitle>Fiscal Calendar</FiscalSheetTitle>
            </FiscalSheetHeader>
            <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 py-4">
            <Box
              noValidate
              component="form"
              sx={{
                display: "flex",
                flexDirection: "column",
                m: "auto",
                width: "fit-content",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "right",
                  alignItems: "flex-end",
                  gap: "5px",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    height: addFiscalCalendar?.length > 0 ? "auto" : "260px",
                    minHeight: "260px",
                    maxHeight: addFiscalCalendar?.length > 0 ? "480px" : "260px",
                    width: "25rem",
                    minWidth: "25rem",
                    maxWidth: "25rem",
                    borderRadius: "8px",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "2px dashed #d1d5db",
                    cursor: "pointer",
                    overflow: "auto",
                    alignSelf: "center",
                  }}
                  onClick={() => csvInput.current.click()}
                  {...getRootPropsForAdd()}
                >
                  {/* {fileName && <p>Selected file: {fileName}</p>} */}
                  {/* {errorMessage && (
                        <p style={{ color: "red" }}>{errorMessage}</p>
                      )} */}
                  <input
                    type="file"
                    accept=".xls, .xlsx"
                    ref={csvInput}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    {...getInputPropsForAdd()}
                  />
                  {/* <ComingSoon /> */}
                  {!addFiscalCalendar && !isLoading && (
                    <>
                      <span
                        style={{
                          textAlign: "center",
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "inherit",
                          height: "inherit",
                        }}
                      >
                        <CloudUploadRoundedIcon fontSize="medium" />
                        Drag and Drop or Select to browse
                      </span>
                    </>
                  )}

                  {!addFiscalCalendar && isLoading && (
                    <div
                      style={{
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "inherit",
                        height: "inherit",
                      }}
                    >
                      <Spinner
                        className="spin"
                        radius={45}
                        color={"black"}
                        stroke={3}
                        visible={true}
                      />
                    </div>
                  )}
                  {addFiscalCalendar?.length > 0 && (
                    <TableContainer
                      component={Paper}
                      sx={{
                        maxHeight: 400,

                        cursor: "pointer",
                      }}
                    >
                      <MuiTable
                        sx={{ minWidth: 100 }}
                        aria-label="fiscal calendar table"
                        stickyHeader
                      >
                        <MuiTableHead>
                          <StyledTableRow>
                            {addFiscalCalendar?.length > 0 &&
                              Object.keys(addFiscalCalendar[0])?.map(
                                (header: any) => (
                                  <StyledTableCell key={header}>
                                    {header}
                                  </StyledTableCell>
                                )
                              )}
                          </StyledTableRow>
                        </MuiTableHead>
                        <MuiTableBody>
                          {addFiscalCalendar?.map((data: any, index: any) => (
                            <StyledTableRow key={index}>
                              {addFiscalCalendar?.length > 0 &&
                                Object.keys(addFiscalCalendar[0])?.map(
                                  (header: any) => (
                                    <StyledTableCell key={header}>
                                      {data[header]}
                                    </StyledTableCell>
                                  )
                                )}
                            </StyledTableRow>
                          ))}
                        </MuiTableBody>
                      </MuiTable>
                    </TableContainer>
                  )}

                  {addFiscalCalendar?.length == 0 && !isLoading && (
                    <div
                      style={{
                        textAlign: "center",
                        width: "inherit",
                        height: "inherit",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "15px",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "5px",
                          flexDirection: "column",
                        }}
                      >
                        <CloudUploadRoundedIcon />
                        Excel Data is not Valid,
                        <br /> <b>Please Upload Again</b>
                      </span>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "15px",
                  }}
                >
                  {addFiscalCalendar && (
                    <span
                      style={{
                        display: "flex",
                        cursor: "pointer",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "2px",
                      }}
                      onClick={handleResetFiscalClicked}
                    >
                      <RotateLeftIcon fontSize="inherit" /> Reset
                    </span>
                  )}

                  <span
                    style={{
                      display: "flex",
                      cursor: "pointer",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "2px",
                    }}
                    onClick={handleDownloadFiscalTemplate}
                  >
                    <FileDownloadOutlinedIcon fontSize="inherit" /> Template
                  </span>
                </div>
              </div>
            </Box>
            </div>
            <FiscalSheetFooter className="border-t pt-4 mt-4">
              <Button variant="outline" onClick={handleClickFiscalDialogClose}>
                Close
              </Button>
            </FiscalSheetFooter>
          </FiscalSheetContent>
        </FiscalSheet>

        {/* Drawer for Update Fiscal Calendar (nested) - no overlay to avoid double tint */}
        <FiscalSheet
          open={uploadFiscalEditDialogOpen}
          onOpenChange={(open) => { if (!open) handleClickEditFiscalDialogClose(); }}
        >
          <FiscalSheetContent
            side="right"
            hideOverlay
            className="w-[90vw] sm:max-w-[700px] flex flex-col max-h-[100vh] overflow-hidden"
          >
            <FiscalSheetHeader>
              <FiscalSheetTitle>Fiscal Calendar</FiscalSheetTitle>
            </FiscalSheetHeader>
            <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 py-4">
            <Box
              noValidate
              component="form"
              sx={{
                display: "flex",
                flexDirection: "column",
                m: "auto",
                width: "fit-content",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "right",
                  alignItems: "flex-end",
                  gap: "5px",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    height: editFiscalCalendar?.length > 0 ? "auto" : "260px",
                    minHeight: "260px",
                    maxHeight: editFiscalCalendar?.length > 0 ? "480px" : "260px",
                    width: "25rem",
                    minWidth: "25rem",
                    maxWidth: "25rem",
                    borderRadius: "8px",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "2px dashed #d1d5db",
                    cursor: "pointer",
                    overflow: "auto",
                    alignSelf: "center",
                  }}
                  onClick={() => csvInput.current.click()}
                  {...getRootPropsForEdit()}
                >
                  {/* {fileName && <p>Selected file: {fileName}</p>} */}
                  {/* {errorMessage && (
                        <p style={{ color: "red" }}>{errorMessage}</p>
                      )} */}
                  <input
                    type="file"
                    accept=".xls, .xlsx"
                    ref={csvInput}
                    style={{ display: "none" }}
                    onChange={handleEditFileChange}
                    {...getInputPropsForEdit()}
                  />

                  {/* <ComingSoon /> */}
                  {!editFiscalCalendar && !isEditLoading && (
                    <>
                      <span
                        style={{
                          textAlign: "center",
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "inherit",
                          height: "inherit",
                        }}
                      >
                        <CloudUploadRoundedIcon fontSize="medium" />
                        Drag and Drop or Select to browse
                      </span>
                    </>
                  )}

                  {!editFiscalCalendar && isEditLoading && (
                    <div
                      style={{
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "inherit",
                        height: "inherit",
                      }}
                    >
                      <Spinner
                        className="spin"
                        radius={45}
                        color={"black"}
                        stroke={3}
                        visible={true}
                      />
                    </div>
                  )}

                  {editFiscalCalendar?.length == 0 && !isEditLoading && (
                    <div
                      style={{
                        textAlign: "center",
                        width: "inherit",
                        height: "inherit",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "15px",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "5px",
                          flexDirection: "column",
                        }}
                      >
                        <CloudUploadRoundedIcon />
                        Excel Data is not Valid,
                        <br /> <b>Please Upload Again</b>
                      </span>
                    </div>
                  )}

                  {editFiscalCalendar?.length > 0 && (
                    <>
                      <TableContainer
                        component={Paper}
                        sx={{
                          maxHeight: 400,

                          cursor: "pointer",
                        }}
                      >
                        <MuiTable
                          sx={{ minWidth: 100 }}
                          aria-label="fiscal calendar table"
                          stickyHeader
                        >
                          <MuiTableHead>
                            <StyledTableRow>
                              {editFiscalCalendar?.length > 0 &&
                                Object.keys(editFiscalCalendar[0])?.map(
                                  (header: any) => (
                                    <StyledTableCell key={header}>
                                      {header}
                                    </StyledTableCell>
                                  )
                                )}
                            </StyledTableRow>
                          </MuiTableHead>
                          <MuiTableBody>
                            {editFiscalCalendar?.map(
                              (data: any, index: any) => (
                                <StyledTableRow key={index}>
                                  {editFiscalCalendar?.length > 0 &&
                                    Object.keys(editFiscalCalendar[0])?.map(
                                      (header: any) => (
                                        <StyledTableCell key={header}>
                                          {data[header]}
                                        </StyledTableCell>
                                      )
                                    )}
                                </StyledTableRow>
                              )
                            )}
                          </MuiTableBody>
                        </MuiTable>
                      </TableContainer>
                    </>
                  )}
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  {editFiscalCalendar?.length > 0 && !isEditLoading && (
                    <div
                      style={{
                        display: "flex",
                        gap: "15px",
                      }}
                    >
                      {editFiscalCalendar && (
                        <span
                          style={{
                            display: "flex",
                            cursor: "pointer",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "2px",
                          }}
                          onClick={handleResetEditFiscalClicked}
                        >
                          <RotateLeftIcon fontSize="inherit" /> Reset
                        </span>
                      )}
                    </div>
                  )}
                  <span
                    style={{
                      display: "flex",
                      cursor: "pointer",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "2px",
                    }}
                    onClick={handleDownloadFiscalTemplate}
                  >
                    <FileDownloadOutlinedIcon fontSize="inherit" /> Template
                  </span>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "right",
                  alignItems: "flex-end",
                  gap: "5px",
                  padding: "20px",
                }}
              >
                {addFiscalCalendar?.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      minHeight: "260px",
                      maxHeight: "480px",
                      width: "25rem",
                      minWidth: "25rem",
                      maxWidth: "25rem",
                      borderRadius: "8px",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      border: "2px dashed #d1d5db",
                      cursor: "pointer",
                      overflow: "auto",
                    }}
                    // onClick={() => csvInput.current.click()}
                    // {...getRootProps()}
                  >
                    {/* {fileName && <p>Selected file: {fileName}</p>} */}
                    {/* {errorMessage && (
                        <p style={{ color: "red" }}>{errorMessage}</p>
                      )} */}

                    {addFiscalCalendar?.length > 0 && (
                      <TableContainer
                        component={Paper}
                        sx={{
                          maxHeight: 440,

                          cursor: "pointer",
                        }}
                      >
                        <MuiTable
                          sx={{ minWidth: 100 }}
                          aria-label="fiscal calendar table"
                          stickyHeader
                        >
                          <MuiTableHead>
                            <StyledTableRow>
                              {addFiscalCalendar?.length > 0 &&
                                Object.keys(addFiscalCalendar[0])?.map(
                                  (header: any) => (
                                    <StyledTableCell key={header}>
                                      {header}
                                    </StyledTableCell>
                                  )
                                )}
                            </StyledTableRow>
                          </MuiTableHead>
                          <MuiTableBody>
                            {addFiscalCalendar?.map((data: any, index: any) => (
                              <StyledTableRow key={index}>
                                {addFiscalCalendar?.length > 0 &&
                                  Object.keys(addFiscalCalendar[0])?.map(
                                    (header: any) => (
                                      <StyledTableCell key={header}>
                                        {data[header]}
                                      </StyledTableCell>
                                    )
                                  )}
                              </StyledTableRow>
                            ))}
                          </MuiTableBody>
                        </MuiTable>
                      </TableContainer>
                    )}
                  </div>
                )}
                {addFiscalCalendar && (
                  <div
                    style={{
                      display: "flex",
                      gap: "15px",
                    }}
                  >
                    {addFiscalCalendar && (
                      <span
                        style={{
                          display: "flex",
                          cursor: "pointer",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "2px",
                        }}
                        onClick={handleDeleteFiscalClicked}
                      >
                        <DeleteOutline fontSize="inherit" /> Delete
                      </span>
                    )}

                    <span
                      style={{
                        display: "flex",
                        cursor: "pointer",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "2px",
                      }}
                      onClick={handleDownloadFiscalTemplate}
                    >
                      <FileDownloadOutlinedIcon fontSize="inherit" /> Template
                    </span>
                  </div>
                )}
              </div>
            </Box>
            </div>
            <FiscalSheetFooter className="border-t pt-4 mt-4">
              <Button variant="outline" onClick={handleClickEditFiscalDialogClose}>
                Close
              </Button>
            </FiscalSheetFooter>
          </FiscalSheetContent>
        </FiscalSheet>

        {/* Alert Dialog for Metaplier Access */}
        <Dialog
          open={dialogMBM}
          onClose={handleCloseAlertDialogForMBM}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Use Google's location service?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              You can give Metaplier Access to only one Facebook Ad Account. If
              you want to give access to this account then other accounts with
              Metaplier Access will be removed.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAlertDialogForMBM}>Disagree</Button>
            <Button onClick={handleCloseAlertDialogForMBM} autoFocus>
              Agree
            </Button>
          </DialogActions>
        </Dialog>

      <PageHeader
        title={`Brands ${!searchQuery.trim() ? `(${totalBrandsCount})` : `(${brands.length} of ${totalBrandsCount})`}`}
        renderCustomTop={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={throttleGetBrands}
              className="flex items-center gap-2 h-8 px-3"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            {superAdmin && hasEditAccess && (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  if (USE_ADD_BRAND_WIZARD_MOCK) {
                    setShowAddBrandWizard(true);
                  } else {
                    setShowAddBrandDialog(true);
                  }
                }}
                className="bg-primary hover:bg-primary/90 flex items-center gap-2 h-8 px-3"
              >
                <Plus className="h-4 w-4" />
                Add Brand
              </Button>
            )}
            {admin_data && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  window.open(
                    `https://app.lift.lt.partners/testload?_id=${admin_data.user}&token=${admin_data?.token}&onboard=${1}`,
                    "_blank"
                  )
                }
                className="flex items-center gap-2 h-8 px-3"
              >
                <ExternalLink className="h-4 w-4" />
                Go to LIFT
              </Button>
            )}
            <div className="relative ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search brands..."
                value={searchText}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchText(event.target.value);
                  searchOptimized(event.target.value);
                }}
                className="pl-10 w-80"
              />
            </div>
          </div>
        }
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 px-8 pt-8 pb-4">
        <div className="flex-1 min-h-0">
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 170px)"
            columns={(() => {
              const cols: TableColumn<any>[] = [
                {
                  key: "brand_image",
                  header: "Brand",
                  accessor: (row: any) => row.image_url || row.image,
                  render: (_v: any, row: any) => (
                    <img
                      alt="brand-img"
                      src={superAdmin ? row.image_url : row.image}
                      className="h-[60px] w-[120px] object-contain rounded cursor-pointer"
                      onClick={() => window.open(`https://${row.domain}`)}
                    />
                  ),
                },
                {
                  key: "name",
                  header: "Name",
                  accessor: "name",
                  sortable: true,
                  render: (_v: any, row: any) => <span className="font-medium">{row.name}</span>,
                },
                {
                  key: "organization",
                  header: "Organization",
                  accessor: "organization_id",
                  render: (_v: any, row: any) => (
                    <span className="font-medium">
                      {row.organization_id
                        ? allOrg.find((org: any) => org.value == row.organization_id)?.label
                        : "Not Assigned"}
                    </span>
                  ),
                },
                {
                  key: "alias",
                  header: "Alias",
                  accessor: "affiliate_alias",
                  render: (_v: any, row: any) => {
                    const aliasVal = superAdmin
                      ? row.affiliate_alias && row.affiliate_alias.length > 0
                        ? row.affiliate_alias
                        : "Not Set"
                      : row.name;
                    return (
                      <TooltipProvider>
                        <LiftTooltip>
                          <TooltipTrigger asChild>
                            <span className="truncate cursor-default max-w-[120px] block">{aliasVal}</span>
                          </TooltipTrigger>
                          <TooltipContent>{aliasVal}</TooltipContent>
                        </LiftTooltip>
                      </TooltipProvider>
                    );
                  },
                },
                {
                  key: "launch_date",
                  header: "Launch Date",
                  accessor: (row: any) => (superAdmin ? row.launch : row.created_at),
                  sortable: true,
                  render: (_v: any, row: any) => (
                    <span className="font-medium">{formatDate(superAdmin ? row.launch : row.created_at)}</span>
                  ),
                },
                {
                  key: "vertical",
                  header: "Vertical",
                  accessor: "vertical",
                  render: (_v: any, row: any) => {
                    if (!row.vertical) return "-";
                    const parts = row.vertical.split(",");
                    const display =
                      parts.length > 3
                        ? `${parts[0]}, ${parts[1]} & more`
                        : parts.length > 2
                        ? `${parts[0]}, ${parts[1]} & ${parts[2]}`
                        : parts.length > 1
                        ? `${parts[0]} & ${parts[1]}`
                        : row.vertical;
                    return (
                      <TooltipProvider>
                        <LiftTooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default font-medium">{display}</span>
                          </TooltipTrigger>
                          <TooltipContent>{row.vertical}</TooltipContent>
                        </LiftTooltip>
                      </TooltipProvider>
                    );
                  },
                },
              ];

              if (superAdmin) {
                cols.push({
                  key: "type",
                  header: "Type",
                  accessor: "type",
                  render: (_v: any, row: any) => <span className="font-medium">{row.type}</span>,
                });
              }

              cols.push(
                {
                  key: "domain",
                  header: "Domain",
                  accessor: "domain",
                  width: "200px",
                  render: (_v: any, row: any) => (
                    <TooltipProvider>
                      <LiftTooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="truncate cursor-pointer text-blue-600 hover:underline font-medium"
                            onClick={() => window.open(`https://${row.domain}`)}
                          >
                            {row.domain}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{row.domain}</TooltipContent>
                      </LiftTooltip>
                    </TooltipProvider>
                  ),
                },
                {
                  key: "revenue_primary",
                  header: "Revenue Primary",
                  accessor: (row: any) => row?.brandData?.revenue_source_of_truth || "NA",
                  render: (v: any) => <span className="font-medium">{v}</span>,
                },
                {
                  key: "conversion_primary",
                  header: "Conversion Primary",
                  accessor: (row: any) => row?.brandData?.conversion_source_of_truth || "NA",
                  render: (v: any) => <span className="font-medium">{v}</span>,
                }
              );

              if (hasEditAccess) {
                cols.push(
                  {
                    key: "active",
                    header: "Active",
                    accessor: "is_active",
                    align: "center",
                    render: (_v: any, row: any) => {
                      const org = allOrg.find((org: any) => org.value === row.organization_id);
                      return (
                        <TooltipProvider>
                          <LiftTooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <LiftSwitch
                                  checked={row.is_active}
                                  onCheckedChange={() => {
                                    if (org?.is_active) {
                                      setAboutToUpdateActiveBrandShow(!aboutToUpdateActiveBrandShow);
                                      setAboutToUpdateActiveBrand(row);
                                    }
                                  }}
                                  disabled={!org?.is_active}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {row.is_active
                                ? "Click to deactivate"
                                : org?.is_active
                                ? "Click to activate"
                                : "Organization is inactive"}
                            </TooltipContent>
                          </LiftTooltip>
                        </TooltipProvider>
                      );
                    },
                  }
                );
              }

              if (hasEditAccess || hasManageAccess) {
                cols.push({
                  key: "actions",
                  header: "Actions",
                  accessor: (row: any) => row.id,
                  render: (_v: any, row: any) => (
                    <div className="flex items-center gap-1 justify-center">
                      <TooltipProvider>
                        <LiftTooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => window.open(`https://${row.domain}`)}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Visit</TooltipContent>
                        </LiftTooltip>
                      </TooltipProvider>
                      {hasEditAccess && (
                        <TooltipProvider>
                          <LiftTooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={row.is_active ? "h-8 w-8 p-0" : "h-8 w-8 p-0 text-gray-400 cursor-not-allowed disabled:opacity-100"}
                                onClick={() => { if (row.is_active) editBrand(row); }}
                                disabled={!row.is_active}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {!row.is_active ? "You have to activate the brand to edit" : "Edit"}
                            </TooltipContent>
                          </LiftTooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  ),
                });
              }

              return cols;
            })()}
            data={brands}
            loading={loadList}
            emptyMessage="No brands found"
            stickyHeader
            stickyFooter
            showActions={false}
            pagination={{
              currentPage: page + 1,
              totalPages: Math.max(1, Math.ceil(totalBrandsCount / rowsPerPage)),
              pageSize: rowsPerPage,
              totalItems: totalBrandsCount,
              onPageChange: (p) => setPage(p - 1),
              onPageSizeChange: (size) => { setRowsPerPage(size); setPage(0); },
              pageSizeOptions: [25, 50, 100, 200],
            }}
          />
        </div>
      </div>
    </div>
    </>
  );
};

export default Brands;
